import * as Api from '@ViewModels';
import produce from 'immer';
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import moment from 'moment';
import { stringify as toQueryString } from 'query-string';
import { createContext } from 'react';
import { Design } from 'react-email-editor';
import { ISubmittedValues } from '../admin/components/CreateCustomDashboardCard';
import { EmailChangeType } from '../admin/components/tools/EmailChangeModal';
import { EnhancementType, Privacy, UserRole } from '../admin/containers/account/accountTabs/Settings/options';
import { TCustomSettings } from '../admin/containers/account/accountTabs/Settings/presentation';
import { GhostCalendarIndustryOptions } from '../admin/containers/templates/GhostCalendar/models';
import { UserRoleName } from '../admin/utils';
import { SlotMachineAchievementsViewModel, SlotMachineAchievementViewModel } from '../aida/viewModels/slotMachines';
import { DefaultSupportedIndustries, IEditEmailTemplateContent } from '../models';
import * as AdminModels from '../models/AdminModels';
import { FirstNamePlaceholder } from '../models/Token';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	getCategoryPathByCampaignType,
	getProviderTypesFromPostTargest,
	getSubverticalOptions,
	getValidAllowLevitateImpersonationUntil,
	mapSocialMediaTypesToPostTargets,
} from '../models/UiUtils';
import { ISelectOption } from '../web/components/DeprecatedSelect';
import { success } from '../web/styles/colors';
import {
	AppImportContactsViewModel,
	ComposeEmailViewModel,
	ContentCalendarSuggestionAll,
	DefaultBulkSendExcludedFilterCriteria,
	EmailWorkloadViewModel,
	ETextingFeatureProvider,
	IContentCalendarSuggestion,
	ICredential,
	UserSessionContext,
} from './AppViewModels';
import { HtmlNewsletterViewModel, IHtmlNewsletter } from './HtmlNewletterViewModels';

export interface IAccountDetailsContext {
	account: Api.IAccount;
	accountOperations: AccountOperationsViewModel;
}

export interface IAccountDetailsSettingsContext {
	onAddCustomCard?(values: ISubmittedValues): void;
	onAddCustomField?(): void;
	onAdminSendOnBehalfContactOwnerCriteriaChanged?(option: ISelectOption<Api.ContactOwnerCriteria>): void;
	onBlogEnabledChanged?(checked: boolean): void;
	onClassificationEmailsChanged?(checked: boolean): void;
	onComplianceEnabledChanged?(checked: boolean): void;
	onComplianceRequiredChanged?(checked: boolean): void;
	onSendComplianceReviewResultEmailsChanged?(checked: boolean): void;
	onComplianceWaitTimeChanged?(selectedOptions: ISelectOption<Api.IMeetingDuration>): void;
	onConnectionTypesEnabledChanged?(checked: boolean): void;
	onCreateContactsFromCalendarChanged?(checked: boolean): void;
	onCrossAccountTemplateSharingChanged?(checked: boolean): void;
	onCsmViewMyCampaignsEnabledChanged?(checked: boolean): void;
	onDefaultContactsVisibilityChanged?(option: ISelectOption<Privacy>): void;
	onDefaultUserRoleChanged?(options: ISelectOption<UserRole>): void;
	onDefaultEmailStyleEnabledChanged?(checked: boolean): void;
	onDefaultEmailStyleFontFaceChanged?(fontFace: string): void;
	onDefaultEmailStyleFontSizeChanged?(fontSize: string): void;
	onDefaultEmailStyleFontColorChanged?(fontColor: string): void;
	onDeleteRenewalKeyFactsChanged?(checked: boolean): void;
	onDisableMobileAppsChanged?(checked: boolean): void;
	onHideCompaniesChanged?(checked: boolean): void;
	onHideContactCompanyEmployeeReportChanged?(checked: boolean): void;
	onHideEmailCampaignSignatureChanged?(checked: boolean): void;
	onHIPAAEnabledChanged?(checked: boolean): void;
	onShowAllContactsByDefaultChanged?(checked: boolean): void;
	onEditHtmlNewsletterFooter?(): void;
	onEnableCcInCampaignsChanged?(checked: boolean): void;
	onEnableContentGenerationChanged?(checked: boolean): void;
	onEnableCustomContentCalendarChanged?(checked: boolean): void;
	onEnablePublicAPIChanged?(checked: boolean): void;
	onEnableStripePaymentsChanged?(checked: boolean): void;
	onEnableSocialMediaChanged?(checked: boolean): void;
	onEnableSocialMediaLogoOverlayChanged?(checked: boolean): void;
	onEnableSocialMediaLogoBackgroundChanged?(checked: boolean): void;
	onEnableSocialMediaReadOnly?(checked: boolean): void;
	onEnableSurveysChanged?(checked: boolean): void;
	onEnableTranslationChanged?(checked: boolean): void;
	onHandwrittenCardsEnabledChanged?(checked: boolean): void;
	onHandwrittenCardsProviderChanged?(option: ISelectOption<Api.HandwrittenCardProvider>): void;
	onHouseholdsEnabledChanged?(checked: boolean): void;
	onHouseholdsSendToHouseholdByDefaultChanged?(checked: boolean): void;
	onHtmlNewsletterChanged?(checked: boolean): void;
	onInternalMeetingsChanged?(checked: boolean): void;
	onLookForLeadsChanged?(checked: boolean): void;
	onMeetingCompanyNameRequiredChanged?(checked: boolean): void;
	onMeetingSchedulerEnabled?(checked: boolean): void;
	onDataBoardOpportunitiesEnabledChanged?(checked: boolean): void;
	onDataBoardDonationsEnabledChanged?(checked: boolean): void;
	onDonationTagSettingsChanged?(settings: Omit<Api.IDonationDataboardTagSettings, '_type'>): void;
	onDataBoardPoliciesEnabledChanged?(checked: boolean): void;
	onDataBoardPoliciesCarrierTagsEnabledChanged?(checked: boolean): void;
	onDataBoardPoliciesLineOfBusinessTagsEnabledChanged?(checked: boolean): void;
	onDataBoardMattersEnabledChanged?(checked: boolean): void;
	onDataBoardMattersMatterStageTagsEnabledChanged?(checked: boolean): void;
	onDataBoardMeetingsEnabledChanged?(checked: boolean): void;
	onRequiredRoleForFullLevitateUI?(options: ISelectOption<UserRole>): void;
	onRequiredRoleToViewAllOpportunities?(options: ISelectOption<UserRole>): void;
	onResetAccountClicked?(): void;
	onSaveForAnalysisChanged?(): void;
	onSendEmailForNewLeadChanged?(checked: boolean): void;
	onSendOnBehalfEnabled?(checked: boolean): void;
	onSendOnBehalfWaitTimeChanged?(selectedOptions: ISelectOption<Api.IMeetingDuration>): void;
	onSentMailClickTrackingChanged?(checked: boolean): void;
	onSentMailOpenTrackingChanged?(checked: boolean): void;
	onSetShowInternalSendOption?(checked: boolean): void;
	onSetAllowStartAutomationsOnHold?(checked: boolean): void;
	onSetEnhancementOption?(selectedOptions: ISelectOption<EnhancementType>): void;
	onSetFinancialReviewOption?(option: ISelectOption<number>): void;
	onSetFinancialReviewOptionChange?(checked: boolean): void;
	onSetMaximumGroupAutomationContacts?(option: ISelectOption<number>): void;
	onSetMaximumGroupAutomationContactsChanged?(checked: boolean): void;
	onSetMaximumGroupSendContacts?(option: ISelectOption<number>): void;
	onSetMaximumGroupSendContactsChanged?(checked: boolean): void;
	onSetMaximumHTMLNewsletterContacts?(option: ISelectOption<number>): void;
	onSetNoteViewerCanEditEnabledChanged?(checked: boolean): void;
	onSetRenewalReminderDaysOption?(option: ISelectOption<number>): void;
	onSetRenewalReminderDaysOptionChange?(checked: boolean): void;
	onSetRenewalSuppressMonthsOption?(option: ISelectOption<number>): void;
	onSetRenewalSuppressMonthsOptionChange?(checked: boolean): void;
	onSetShowDuplicateContacts?(checked: boolean): void;
	onSetShowUpcomingBirthdaysToOption?(options: ISelectOption<Api.UpcomingKeyDateDisplayScope>): void;
	onToggleAccountPreferenceBoolean?(path: BooleanKeys<Api.IAccountPreferences>, checked: boolean): void;
	onSetSuppressContactsRemovedFromResourceSends?(checked: boolean): void;
	onSetSurveyDefaultReviewLink?(url?: string): void;
	onSetSurveyNewReviewsEmailNotification?(enabled: boolean): void;
	onSetTurning65MonthsOption?(option: ISelectOption<number>): void;
	onSetTurning65MonthsOptionChange?(checked: boolean): void;
	onSetTurning72MonthsOption?(option: ISelectOption<number>): void;
	onSetTurning72MonthsOptionChange?(checked: boolean): void;
	onSetTurning73MonthsOption?(option: ISelectOption<number>): void;
	onSetTurning73MonthsOptionChange?(checked: boolean): void;
	onSetNewDonationsOptionChange?(checked: boolean): void;
	onSetNewPolicyOptionChange?(checked: boolean): void;
	onSetNewMatterOptionChange?(checked: boolean): void;
	onSetMatterStageChangedOptionChange?(checked: boolean): void;
	onSetMatterStatusChangedOptionChange?(checked: boolean): void;
	onSetTurningXXMonthsOption?(option: ISelectOption<number>): void;
	onSetTurningXXMonthsOptionChange?(checked: boolean): void;
	onSetTurningXX2MonthsOption?(option: ISelectOption<number>): void;
	onSetTurningXX2MonthsOptionChange?(checked: boolean): void;
	onSetTurningXX3MonthsOption?(option: ISelectOption<number>): void;
	onSetTurningXX3MonthsOptionChange?(checked: boolean): void;
	onSetTurningXX4MonthsOption?(option: ISelectOption<number>): void;
	onSetTurningXX4MonthsOptionChange?(checked: boolean): void;
	onSetTurningXX5MonthsOption?(option: ISelectOption<number>): void;
	onSetTurningXX5MonthsOptionChange?(checked: boolean): void;
	onSuppressCustomCardOptionChange?(card: TCustomSettings): void;
	onKeepInTouchAgentChanged?(checked: boolean): void;
	onKeepInTouchAgentDailyScanQuotaChanged?(value: number): void;
	onKeepInTouchAgentMinDaysBetweenScansChanged?(value: number): void;
	onTextingChanged?(checked: boolean): void;
	onTextingDailySendLimitChanged?(interval: number): void;
	onTextingHourlySendLimitChanged?(interval: number): void;
	onTextingProviderChanged?(option: ISelectOption<ETextingFeatureProvider>): void;
	onTextingSecondsBetweenSendsChanged?(seconds: number): void;
	onTextingRequireArchivingChanged?(checked: boolean): void;
	onTextingUseParentCampaignRegistrationChanged?(enabled: boolean): void;
	onTurningXXInputChanged?(value: number): void;
	onTurningXX2InputChanged?(value: number): void;
	onTurningXX3InputChanged?(value: number): void;
	onTurningXX4InputChanged?(value: number): void;
	onTurningXX5InputChanged?(value: number): void;
	onTimelineChanged?(checked: boolean): void;
	onTwoFactorAuthChanged?(checked: boolean): void;
	onAllowOAuthLoginChanged?(checked: boolean): void;
	onOnlyAllowOAuthLoginChanged?(checked: boolean): void;
	onEnabledOAuthProvidersChanged?(providers: string[]): void;
	onWebsitesChanged?(checked: boolean): void;
	onWebsitesDudaSiteIdChanged?(value: string): void;
	onWebsitesWebsiteUrlChanged?(value: string): void;
	onUpdateCustomCard?(values: ISubmittedValues, card: TCustomSettings): void;
	onUpdateCustomCardDays?(value: ISelectOption<number>, card: TCustomSettings): void;
	setStartOnBehalfTriggerEnabled?(enabled: boolean): void;
	setTagAutomationTriggerEnabled?(enabled: boolean): void;
	setManualAutomationTriggerEnabled?(enabled: boolean): void;
	setTextingCampaignAutomationTriggerEnabled?(enabled: boolean): void;
	toggleAutomations?(checked: boolean): void;
	onUpdatePreApprovedFeature?: (option: boolean) => void;
	onUpdatePreApprovedDisplayText?: (value: string) => void;
	onUpdatePreApprovedEmailPrefix?: (value: string) => void;
	onUpdatePreReviewedFeature?: (option: boolean) => void;
	onUpdatePreReviewedDefaultFeature?: (option: boolean) => void;
	onUpdatePreReviewedDisplayText?: (value: string) => void;
	onUpdatePreReviewedEmailPrefix?: (value: string) => void;
	onUpdateRejectedFeature?: (option: boolean) => void;
	onUpdateRejectedDisplayText?: (value: string) => void;
	onUpdateRejectedEmailPrefix?: (value: string) => void;
}

export const AccountDetailsContext = createContext<IAccountDetailsContext>(null);
export const AccountDetailsSettingsContext = createContext<IAccountDetailsSettingsContext>(null);

export enum AdminPermission {
	/**
	 * Do not use All
	 *
	 * @deprecated
	 */
	All = 'All',
	AnnotateAccount = 'AnnotateAccount',
	AssignSuccessManager = 'AssignSuccessManager',
	CancelAutomations = 'CancelAutomations',
	CreateAndDeleteSnapshots = 'CreateAndDeleteSnapshots',
	CreateAutomationTemplateOnBehalf = 'CreateAutomationTemplateOnBehalf',
	CreateCampaigns = 'CreateCampaigns',
	Deny = 'Deny',
	ExportActiveUserData = 'ExportActiveUserData',
	InviteUsers = 'InviteUsers',
	ManageSurveyTemplates = 'ManageSurveyTemplates',
	ManageTemplates = 'ManageTemplates',
	ModifyAccount = 'ModifyAccount',
	ModifyUser = 'ModifyUser',
	OnlyForDevelopers = 'OnlyForDevelopers',
	OnlyForDeveloperOps = 'OnlyForDeveloperOps',
	ReadAccount = 'ReadAccount',
	ReadCampaigns = 'ReadCampaigns',
	ReadUser = 'ReadUser',
	ResetAccount = 'ResetAccount',
	ResetPassword = 'ResetPassword',
	RevertImport = 'RevertImport',
	SupportLevel3 = 'SupportLevel3',
	ViewErrorDetail = 'ViewErrorDetail',
	ViewOpenInvoices = 'ViewOpenInvoices',
	ViewUserErrors = 'ViewUserErrors',
	GrantLoginOnBehalf = 'GrantLoginOnBehalf',
	ManageAIAgentTemplates = 'ManageAIAgentTemplates',
	ModifySystemPrompts = 'ModifySystemPrompts',
	ModifySalesPrompts = 'ModifySalesPrompts',
	ModifyContentGenerationPrompts = 'ModifyContentGenerationPrompts',
}

export class AdminUserSessionContext extends Api.UserSessionContext {
	protected mPermissions: AdminPermission[];
	protected mPermissionsLoadingPromise: Promise<Api.IOperationResult<AdminPermission[]>>;

	constructor(config?: Api.IUserSessionContextConfig) {
		super(config);

		makeObservable<AdminUserSessionContext, 'mPermissions' | 'mPermissionsLoadingPromise'>(this, {
			mPermissions: observable.ref,
			mPermissionsLoadingPromise: observable.ref,
			isLoadingPermissions: computed,
			permissions: computed,
		});
	}

	public get isLoadingPermissions() {
		return !!this.mPermissionsLoadingPromise;
	}

	public get permissions() {
		return this.mPermissions
			? this.mPermissions.reduce<Record<string, boolean>>((result, x) => {
					result[x] = true;
					return result;
				}, {})
			: {};
	}

	public loadPermissions = () => {
		if (!this.mPermissionsLoadingPromise) {
			const promise = new Promise<Api.IOperationResult<AdminPermission[]>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<string[]>) => {
					if (opResult.success) {
						this.mPermissions = opResult.value as AdminPermission[];
						this.mPermissionsLoadingPromise = null;
						resolve(opResult as Api.IOperationResult<AdminPermission[]>);
					} else {
						reject(opResult);
					}
				});
				this.webServiceHelper.callWebServiceWithOperationResults<string[]>(
					'admin/userOperations/permissions',
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
			promise.catch(() => {
				// eat this error
				this.mPermissionsLoadingPromise = null;
				this.mPermissions = [];
			});
			this.mPermissionsLoadingPromise = promise;
		}

		return this.mPermissionsLoadingPromise;
	};

	public hasPermission = (permission: AdminPermission) => {
		return !!this.permissions[permission];
	};

	protected setUserSession(userSession?: Api.IUserSession) {
		super.setUserSession(userSession);
		if (userSession) {
			this.loadPermissions();
		} else {
			this.mPermissionsLoadingPromise = null;
			this.mPermissions = [];
		}
	}
}

export enum CancellationCategory {
	NeverGotStarted,
	DidntSeeValue,
	UseCase,
	Issues,
	Adoption,
	Price,
	Competitor,
	Concerns,
	Product,
	BusinessChanges,
}

type CancellationReasonRequest = {
	cancellationMessage: string;
	cancellationCategory: CancellationCategory;
};

export class AccountOperationsViewModel extends Api.ViewModel {
	protected mAccount: Api.IAccount;
	protected mDeleteKeyFactsPromise: Promise<Api.IOperationResult<Api.IOperationResultNoValue>>;
	protected mDeleteAllDonationsPromise: Promise<Api.IOperationResult<Api.IOperationResultNoValue>>;
	protected mEmailProviderDefaultsPromise: Promise<Api.IEmailProviderConfiguration>;
	protected mInternalId: string;
	protected mInternalIdPromise: Promise<Api.IOperationResult<string>>;
	protected mLoadAccountPromise: Promise<Api.IAccount>;
	protected mSaveAccountPromise: Promise<Api.IAccount>;
	protected mSavingAccount: boolean;
	protected mBillingUpdatePromise: Promise<Api.IOperationResult<Api.IAccount>>;

	constructor(userSession: AdminUserSessionContext, account?: Api.IAccount) {
		super(userSession);

		makeObservable<
			AccountOperationsViewModel,
			| 'mAccount'
			| 'mDeleteKeyFactsPromise'
			| 'mDeleteAllDonationsPromise'
			| 'mEmailProviderDefaultsPromise'
			| 'mInternalId'
			| 'mInternalIdPromise'
			| 'mLoadAccountPromise'
			| 'mSaveAccountPromise'
			| 'mSavingAccount'
			| 'mBillingUpdatePromise'
			| 'callBillingUpdate'
			| 'updateAccountFeatures'
		>(this, {
			mAccount: observable.ref,
			mDeleteKeyFactsPromise: observable.ref,
			mDeleteAllDonationsPromise: observable.ref,
			mEmailProviderDefaultsPromise: observable.ref,
			mInternalId: observable.ref,
			mInternalIdPromise: observable.ref,
			mLoadAccountPromise: observable.ref,
			mSaveAccountPromise: observable.ref,
			mSavingAccount: observable.ref,
			mBillingUpdatePromise: observable.ref,
			isLoadingBilling: computed,
			loadPdfStatement: action,
			updateBilling: action,
			updateTrialExtension: action,
			discount: action,
			cancelImmediately: action,
			cancelLater: action,
			removePendingCancellation: action,
			updateCancellationStatus: action,
			unsubscribeContactsByEmail: action,
			grantTemporaryAccess: action,
			updateAccountData: action,
			callBillingUpdate: action,
			isBusy: override,
			account: computed,
			isGettingEmailSendLimits: computed,
			isDeletingKeyFacts: computed,
			isDeletingAllDonations: computed,
			isLoaded: override,
			isSaving: computed,
			isLoading: override,
			hasPlanDetails: computed,
			hasAccountAdditionalInfo: computed,
			hasAccountPreferences: computed,
			hasAccountPreferencesDefaults: computed,
			accountName: computed,
			accountStatus: computed,
			accountStatusDescription: computed,
			billingType: computed,
			billingTypeDescription: computed,
			isCancelable: computed,
			isPaid: computed,
			isTrial: computed,
			creationDateDescription: computed,
			renewalOrExpireDateDescription: computed,
			lastCsmMeetingDate: computed,
			nextMeeting: computed,
			billingCycleDescription: computed,
			industryDescription: computed,
			soldByDescription: computed,
			successManagerDescription: computed,
			webSpecialistDescription: computed,
			adsSpecialistDescription: computed,
			stageDescription: computed,
			brokerDealer: computed,
			systemOfRecord: computed,
			defaultUserRole: computed,
			accountFlagged: computed,
			allowLevitateImpersonationUntil: computed,
			setAccountLogo: action,
			loadInternalCompanyId: action,
			updateAutomationFeatures: action,
			updateConnectionTypesFeature: action,
			updateCrossAccountTemplateSharingFeature: action,
			updateHtmlNewsletterFeatures: action,
			updateKeepInTouchAgentFeatures: action,
			updateMeetingSchedulerFeatures: action,
			updateTextingFeatures: action,
			updateHandwrittenFeatures: action,
			updateHouseholdsFeature: action,
			updateTimelineFeatures: action,
			updateSurveyFeatures: action,
			updateSocialMediaFeatures: action,
			updateTranslationFeatures: action,
			updateContentGenerationFeatures: action,
			updatePublicAPIFeatures: action,
			updateCustomContentCalendarFeatures: action,
			updateAccountFeatures: action,
			updateClickTracking: action,
			updateCsmViewMyCampaignsEnabled: action,
			updateComplianceEnabled: action,
			updateComplianceWaitTime: action,
			toggleTwoFactorAuth: action,
			updateOAuthLoginSettings: action,
			updateBlogEnabled: action,
			updateBlogFeatures: action,
			updateWebsitesFeature: action,
			updateDisableMobileApps: action,
			updateHideCompanies: action,
			updateHideEmailCampaignSignature: action,
			updatShowAllContactsByDefault: action,
			updateAnyUserEditingNotes: action,
			toggleAccountPreference: action,
			updateAccountPreferenceString: action,
			updateCustomCalendarStatus: action,
			updateCustomCalendarIntakeFormDue: action,
			updateCustomCalendarModifications: action,
			toggleSaveForAnalysis: action,
			getProformaInvoiceUrl: action,
			getSelfServicePaymentUrl: action,
			updatePreApprovedFeature: action,
			updatePreReviewedFeature: action,
			updateRejectedFeature: action,
			nextWebsitesMeeting: computed,
			updateStripePaymentsFeatures: action,
		});

		this.mAccount = account;
	}

	public get isLoadingBilling() {
		return !!this.mBillingUpdatePromise;
	}

	// load pdf statement
	public loadPdfStatement = () => {
		this.impersonate({ account: { id: this.userSession.account.id } });
		return new Promise((resolve, reject) => {
			this.userSession.webServiceHelper.callWebServiceRawResponse(
				this.composeApiUrl({ urlPath: `account/${this.account.id}/statement/pdf` }),
				'GET',
				null,
				response => {
					response.blob().then(blob => {
						const url = window.URL.createObjectURL(blob);
						const a = document.createElement('a');
						a.href = url;
						a.download = `Latest_Statement_${this.mAccount.companyName}_${new Date().toISOString()}.pdf`;
						// support older browsers
						document.body.appendChild(a);
						a.click();
						a.remove();
					});
					resolve(undefined);
				},
				error => reject(error)
			);
		});
	};

	// update billing status (dev only)
	public updateBilling = () => this.callBillingUpdate(`admin/account/${this.account.id}/updateBilling`, 'PUT');

	// update trial extension
	public updateTrialExtension = (trialExtension: Api.ITrialExtensionSettings) =>
		this.callBillingUpdate(`admin/account/${this.account.id}/trialExtension`, 'PUT', trialExtension.durationInDays);

	// discount
	public discount = (percent: Api.AccountDiscountPercent) =>
		this.callBillingUpdate(`admin/account/${this.account.id}/discount/${percent}`, 'PUT');

	// cancel now
	public cancelImmediately = (cancellationReasonRequest: CancellationReasonRequest) =>
		this.callBillingUpdate(`admin/account/${this.account.id}/cancel/now/v2`, 'POST', cancellationReasonRequest);

	// cancel later
	public cancelLater = (cancellationReasonRequest: CancellationReasonRequest) =>
		this.callBillingUpdate(`admin/account/${this.account.id}/cancel/pending/v2`, 'PATCH', cancellationReasonRequest);

	// remove cancel later
	public removePendingCancellation = (reason: string) =>
		this.callBillingUpdate(`admin/account/${this.account.id}/cancel/remove`, 'DELETE', reason);

	public updateCancellationStatus = (cancellationReasonRequest: CancellationReasonRequest) =>
		this.callBillingUpdate(`admin/account/${this.account.id}/cancel/reason/v2`, 'PUT', cancellationReasonRequest);

	public unsubscribeContactsByEmail = (emails: string[]) => {
		return new Promise<Api.IBulkOperationResult<string>>((resolve, reject) => {
			return this.mUserSession.webServiceHelper.callWebServiceWithBulkOperationResult<string>(
				`impersonate/${this.account.id}/contact/unsubscribe`,
				'POST',
				{ emails },
				ok => resolve(ok),
				err => reject(Api.asApiError(err))
			);
		});
	};

	public grantTemporaryAccess = async () => {
		const value: Api.IAccount = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
			`impersonate/${this.account.id}/account/grantLevitateTemporaryAccess`,
			'PUT',
			null
		);
		this.mAccount = value;
		return value;
	};

	private callBillingUpdate = async (route: string, method: Api.HTTPMethod, body?: any) => {
		if (!this.mBillingUpdatePromise) {
			try {
				const value = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(route, method, body);
				this.mAccount = value;
				return value;
			} finally {
				runInAction(() => {
					this.mBillingUpdatePromise = null;
				});
			}
		}
		return null;
	};

	public get isBusy() {
		return this.loading || this.busy || this.isSaving;
	}

	public get account() {
		return this.mAccount;
	}

	/** Update the account data with new values. Triggers MobX reactivity. */
	public updateAccountData = (account: Api.IAccount) => {
		this.mAccount = account;
	};

	public get isGettingEmailSendLimits() {
		return !!this.mEmailProviderDefaultsPromise;
	}

	public get isDeletingKeyFacts() {
		return !!this.mDeleteKeyFactsPromise;
	}

	public get isDeletingAllDonations() {
		return !!this.mDeleteAllDonationsPromise;
	}

	public get isLoaded() {
		return !this.account || !this.account.id || !!this.account.creationDate;
	}

	public get isSaving() {
		return !!this.mSaveAccountPromise || !!this.mSavingAccount;
	}

	public get isLoading() {
		return !!this.mLoadAccountPromise;
	}

	public get hasPlanDetails() {
		return this.mAccount && this.mAccount.planDetails;
	}

	public get hasAccountAdditionalInfo() {
		return this.mAccount && this.mAccount.additionalInfo;
	}

	public get hasAccountPreferences() {
		return this.mAccount && this.mAccount.preferences;
	}

	public get hasAccountPreferencesDefaults() {
		return this.hasAccountPreferences && this.mAccount.preferences.defaults;
	}

	public get accountName() {
		return this.account?.companyName;
	}

	public get accountStatus() {
		return !this.account?.activationStatus ? 'UNKNOWN' : this.account.activationStatus;
	}

	public get accountStatusDescription() {
		if (!this.account?.activationStatus) {
			return 'Unknown';
		}

		return `${this.account.activationStatus.charAt(0).toLocaleUpperCase()}${this.account.activationStatus
			.slice(1)
			.toLocaleLowerCase()}`;
	}

	get billingType() {
		return this.account?.planDetails?.billingType;
	}

	public get billingTypeDescription() {
		if (!this.account?.planDetails?.billingType) {
			return 'Unknown';
		}
		return `${this.account.planDetails.billingType.charAt(0).toLocaleUpperCase()}${this.account.planDetails.billingType
			.slice(1)
			.toLocaleLowerCase()}`;
	}

	public get isCancelable() {
		return this.billingType === Api.BillingType.Trial || this.billingType === Api.BillingType.Comp;
	}

	public get isPaid() {
		return this.billingType === Api.BillingType.Paid || this.billingType === Api.BillingType.Comp;
	}

	public get isTrial() {
		return this.billingType === Api.BillingType.Trial;
	}

	public get creationDateDescription() {
		const creationDate: Date = this.account?.creationDate;
		return creationDate ? moment(creationDate).format('MM/DD/YYYY') : 'Unknown';
	}

	public get renewalOrExpireDateDescription() {
		const renewalDate: Date = this?.account?.planDetails?.renewalDate;
		const trialExpirationDate: Date = this?.account?.planDetails?.trialExpirationDate;
		const billingType: string = this?.account?.planDetails?.billingType;

		if (billingType === Api.BillingType.Paid) {
			return moment(renewalDate).format('MM/DD/YYYY');
		} else if (billingType === Api.BillingType.Trial) {
			return moment(trialExpirationDate).format('MM/DD/YYYY');
		} else {
			return 'N/A'; // Comps
		}
	}

	public get lastCsmMeetingDate() {
		const lastCsmMeeting: Date = this.account?.additionalInfo?.lastCsmMeeting;

		if (!lastCsmMeeting) {
			return 'Unknown';
		}

		return moment(lastCsmMeeting).format('MM/DD/YYYY');
	}

	public get nextMeeting() {
		const nextMeeting = this.account?.additionalInfo?.nextMeeting;

		if (!nextMeeting) {
			return 'Not Set';
		}

		const momentNextMeeting = moment(nextMeeting).utc();
		if (momentNextMeeting.isSame(moment().startOf('day'), 'd')) {
			return 'Today';
		} else if (momentNextMeeting.isSame(moment().add(1, 'days').startOf('day'), 'd')) {
			return 'Tomorrow';
		}

		return momentNextMeeting.format('MM/DD/YYYY');
	}

	public get nextWebsitesMeeting() {
		const nextMeeting = this.account?.additionalInfo?.nextWebsitesMeeting;

		if (!nextMeeting) {
			return 'Not Set';
		}

		const momentNextMeeting = moment(nextMeeting).utc();
		if (momentNextMeeting.isSame(moment().startOf('day'), 'd')) {
			return 'Today';
		} else if (momentNextMeeting.isSame(moment().add(1, 'days').startOf('day'), 'd')) {
			return 'Tomorrow';
		}

		return momentNextMeeting.format('MM/DD/YYYY');
	}

	public get billingCycleDescription() {
		const billingType: string = this?.account?.planDetails?.billingType;
		const billingCycle = this?.account?.planDetails?.billingCycle;
		return billingType === Api.BillingType.Paid && billingCycle
			? this?.account?.planDetails?.billingCycle.toString() + ' months' || 'N/A'
			: 'N/A';
	}

	public get industryDescription() {
		const industry: string = this.account?.additionalInfo?.industry;
		return industry || 'Unknown';
	}

	public get soldByDescription() {
		const salesRepName: string = this.account?.additionalInfo?.salesRepName;
		return salesRepName || 'Unknown';
	}

	public get successManagerDescription() {
		const customerSuccessName: string = this.account?.additionalInfo?.customerSuccessName;
		return customerSuccessName || 'Unknown';
	}

	public get webSpecialistDescription() {
		const customerSuccessName: string = this.account?.additionalInfo?.webSpecialistName;
		return customerSuccessName || 'Unassigned';
	}

	public get adsSpecialistDescription() {
		const adsSpecialistName: string = this.account?.additionalInfo?.adsSpecialistName;
		return adsSpecialistName || 'Unassigned';
	}

	public get stageDescription() {
		const stage: string = this.account?.additionalInfo?.stage;
		return stage || 'None';
	}

	public get brokerDealer() {
		return this.account?.additionalInfo?.brokerDealer || 'Unknown';
	}

	get systemOfRecord() {
		return this.account?.additionalInfo.systemOfRecord || 'Unknown';
	}

	public get defaultUserRole(): Api.UserRole {
		return this.hasAccountPreferencesDefaults && this.mAccount.preferences.defaults.userRole
			? this.mAccount.preferences.defaults.userRole
			: 'user';
	}

	public get accountFlagged() {
		return this.account?.additionalInfo?.accountFlagged;
	}

	public get allowLevitateImpersonationUntil() {
		return getValidAllowLevitateImpersonationUntil(this.account);
	}

	public resetAccount = async (account: Api.IAccount) => {
		if (!this.isBusy && account?.id) {
			return this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(`admin/account/${account.id}/reset`, 'POST');
		}
	};

	public resetAccountAndIntegration = async (account: Api.IAccount) => {
		if (!this.isBusy && account?.id) {
			return this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${account.id}/reset?includeIntegrations=true`,
				'POST'
			);
		}
	};

	public deactivateAccount = async (account: Api.IAccount) => {
		if (!this.isBusy) {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${account.id}/DeactivateNow`,
				'POST'
			);
			this.mAccount = value;
			return value;
		}
	};

	public getInitialMeetingTeam = async (account: Api.IAccount) => {
		if (!this.isBusy && account?.id) {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IAccountTeam>(
				`admin/Scheduler/${account.id}/InitialMeetingTeam`,
				'GET'
			);
			return value;
		}
	};

	public getScheduleMeetingUrl = async (account: Api.IAccount, team: string) => {
		if (!this.isBusy && account?.id) {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IInitiateSchedulerResponse>(
				`admin/Scheduler/${account.id}?team=${encodeURIComponent(team)}`,
				'POST'
			);
			return value;
		}
	};

	public getScheduleMeetingShortcut = async (account: Api.IAccount, team: string) => {
		if (!this.isBusy && account?.id) {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IInitiateSchedulerResponse>(
				`admin/Scheduler/${account.id}/shortcut?team=${encodeURIComponent(team)}`,
				'POST'
			);
			return value;
		}
	};

	public getTeamsByAccountId = (account?: Api.IAccount) => {
		const mAccount = account || this.mAccount;
		if (!this.isBusy && mAccount.id) {
			const promise = new Promise<Api.IAccountTeam[]>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callWebServiceAsync<Api.IAccountTeam[]>(`admin/Scheduler/${mAccount.id}/Teams`, 'GET')
					.then(opResult => {
						resolve(opResult as Api.IAccountTeam[]);
					})
					.catch(e => {
						reject(e);
					});
			});
			return promise;
		}
		return null;
	};

	public getDefaultEmailSendLimits = (
		provider: Api.EmailProviderType,
		domain: string,
		accountId: string
	): Promise<Api.IEmailProviderConfiguration> => {
		if (!this.isGettingEmailSendLimits) {
			this.mEmailProviderDefaultsPromise = new Promise<Api.IEmailProviderConfiguration>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callWebServiceAsync<Api.IEmailProviderConfiguration>(
						`admin/email/defaultConfiguration?provider=${provider}&domain=${domain}&accountId=${accountId}`,
						'GET'
					)
					.then((opResult: Api.IOperationResult<Api.IEmailProviderConfiguration>) => {
						this.mEmailProviderDefaultsPromise = null;

						if (opResult.success) {
							resolve(opResult.value);
						} else {
							reject(opResult);
						}
					});
			});
		}

		return this.mEmailProviderDefaultsPromise;
	};

	public deleteKeyFacts = (keyFactKind: Api.KeyDateKind) => {
		if (!this.isDeletingKeyFacts) {
			this.mDeleteKeyFactsPromise = new Promise<Api.IOperationResult<Api.IOperationResultNoValue>>(
				(resolve, reject) => {
					this.mUserSession.webServiceHelper
						.callAsync<Api.IOperationResult<Api.IOperationResultNoValue>>(
							`impersonate/${this.mAccount.id}/Contact/KeyFact?keyFactKind=${keyFactKind}`,
							'DELETE'
						)
						.then(() => {
							resolve(undefined);
						})
						.catch(error => {
							reject(error);
						})
						.finally(() => {
							this.mDeleteKeyFactsPromise = null;
						});
				}
			);
			return this.mDeleteKeyFactsPromise;
		}

		return null;
	};

	public deleteAllDonations = (sourceToExclude?: string) => {
		if (!this.isDeletingAllDonations) {
			const queryParam = sourceToExclude ? `?sourceToExclude=${encodeURIComponent(sourceToExclude)}` : '';
			this.mDeleteAllDonationsPromise = new Promise<Api.IOperationResult<Api.IOperationResultNoValue>>(
				(resolve, reject) => {
					this.mUserSession.webServiceHelper
						.callAsync<Api.IOperationResult<Api.IOperationResultNoValue>>(
							`impersonate/${this.mAccount.id}/donation/all${queryParam}`,
							'DELETE'
						)
						.then(() => {
							resolve(undefined);
						})
						.catch(error => {
							reject(error);
						})
						.finally(() => {
							this.mDeleteAllDonationsPromise = null;
						});
				}
			);
			return this.mDeleteAllDonationsPromise;
		}

		return null;
	};

	public setAccountLogo = async (file: Api.IFileAttachmentWithURL) => {
		runInAction(() => {
			this.mAccount = {
				...this.mAccount,
				preferences: {
					...this.mAccount.preferences,
					logo: file,
				},
			};
		});
	};

	public saveAccount = async (account: Api.IAccount) => {
		if (this.isBusy) {
			return null;
		}

		this.mSavingAccount = true;
		try {
			// Strip server-computed health data to avoid overwriting with stale values
			const { health: _health, ...additionalInfoWithoutHealth } = account.additionalInfo || {};
			const payload = { ...account, additionalInfo: additionalInfoWithoutHealth };
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${account.id}`,
				'PUT',
				payload
			);
			this.mAccount = value;
			return value;
		} catch (error) {
			throw Api.asApiError(error);
		} finally {
			this.mSavingAccount = false;
		}
	};

	public saveMainAccountId = async (account: Api.IAccount) => {
		if (this.isBusy) {
			return null;
		}

		this.mSavingAccount = true;
		try {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${account.id}/mainAccountId/?mainAccountId=${account.mainAccountId ?? ''}`,
				'PATCH'
			);
			this.mAccount = value;
			return value;
		} catch (error) {
			throw Api.asApiError(error);
		} finally {
			this.mSavingAccount = false;
		}
	};

	public takeSnapshot = async () => {
		if (!this.isBusy) {
			const value = await this.mUserSession.webServiceHelper.callAsync<AdminModels.IAccountSnapshot>(
				`admin/accountSnapshot/${this.account.id}`,
				'POST',
				null
			);
			return value;
		}
	};

	public load = (account?: Api.IAccount) => {
		const mAccount = account || this.mAccount;
		if (!this.isBusy && mAccount.id) {
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callAsync<Api.IAccount>(`admin/account/${mAccount.id}`, 'GET')
					.then((value: Api.IAccount) => {
						this.mLoadAccountPromise = null;
						this.mAccount = value;
						resolve(value);
					})
					.catch(e => {
						reject(e);
					});
			});
			this.mLoadAccountPromise = promise;
			return promise;
		}

		return null;
	};

	public getAccountById = async (accountId: string) => {
		if (this.isBusy) {
			return null;
		}

		try {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${accountId}`,
				'GET'
			);
			return value;
		} catch (error) {
			throw Api.asApiError(error);
		}
	};

	/**
	 * This method allows a user to self-register. The only requirement is that the email address is not already being
	 * used and the user's email address matches the EmailDomain field on the Account. Requires: @param user.accountId ||
	 *
	 * @param this.account.id
	 *
	 *   || @param account
	 */
	public createUser = async (user: Partial<Api.IUser>, sendWelcomeEmail = false, account?: Api.IAccount) => {
		const accountId = user?.accountId || account?.id || this.mAccount?.id;
		if (accountId) {
			const value = await this.mUserSession.webServiceHelper.callAsync<Api.IUser>(
				`user/register?sendWelcomeEmail=${sendWelcomeEmail}`,
				'POST',
				{
					...user,
					accountId,
					email: user.primaryEmail.value,
				}
			);
			return value;
		}
	};

	public loadInternalCompanyId = () => {
		if (this.mInternalId) {
			return Promise.resolve<Api.IOperationResult<string>>({
				success: true,
				systemCode: 200,
				value: this.mInternalId,
			});
		}
		if (!this.mInternalIdPromise) {
			this.mInternalIdPromise = new Promise<Api.IOperationResult<string>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<string>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mInternalId = opResult.value;
							this.mInternalIdPromise = null;
							resolve(opResult);
						} else {
							this.mInternalIdPromise = null;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<string>(
					`admin/account/${this.mAccount.id}/internalCompanyId`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
			return this.mInternalIdPromise;
		}
	};

	public updateAccountNote = async (
		account: Api.IAccount,
		type: AdminModels.InternalMeetingNoteType,
		note: Api.INote,
		formData?: FormData
	) => {
		if (!this.isBusy) {
			try {
				// Only update the Sales Notes in place.
				const value = await (note.id && type === AdminModels.InternalMeetingNoteType.Sales
					? this.mUserSession.webServiceHelper.callAsync<Api.INote>(
							`note/${note.id}`,
							'PUT',
							formData ? formData : note
						)
					: this.mUserSession.webServiceHelper.callAsync<Api.INote>(
							`admin/account/${account.id}/additionalInfo/note/${type}`,
							'PUT',
							formData ? formData : note
						));
				// For ads notes (catch-all and per-channel), refresh the account to pick up server-side changes (adsKickoffDate, lastAdsMeeting, per-channel note ids, etc.)
				if (
					type === AdminModels.InternalMeetingNoteType.Ads ||
					type === AdminModels.InternalMeetingNoteType.AdsFacebook ||
					type === AdminModels.InternalMeetingNoteType.AdsGoogleLsa ||
					type === AdminModels.InternalMeetingNoteType.AdsGooglePpc
				) {
					const refreshedAccount = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${account.id}`,
						'GET'
					);
					runInAction(() => {
						this.mAccount = refreshedAccount;
					});
				} else {
					runInAction(() => {
						this.mAccount = {
							...this.mAccount,
							additionalInfo: {
								...(this.mAccount.additionalInfo || {}),
								summaryNoteId: value.id,
							},
						};
					});
				}
				return value;
			} finally {
				runInAction(() => {
					this.mSavingAccount = false;
				});
			}
		}
	};

	public addAccountMeetingNote = async (
		account: Api.IAccount,
		customerMeeting: AdminModels.ICustomerMeeting,
		formData?: FormData
	) => {
		if (!this.isBusy) {
			this.mSavingAccount = true;
			delete customerMeeting.note.id;

			try {
				const value = await this.mUserSession.webServiceHelper.callAsync<Api.INote>(
					`admin/account/${account.id}/meeting`,
					'POST',
					formData ? formData : customerMeeting
				);

				// For ads meetings (catch-all and per-channel), refresh the account to get updated fields (live dates, per-channel last meeting + status, overall mirrors, etc.)
				if (
					customerMeeting.metWith === AdminModels.InternalMeetingNoteType.Ads ||
					customerMeeting.metWith === AdminModels.InternalMeetingNoteType.AdsFacebook ||
					customerMeeting.metWith === AdminModels.InternalMeetingNoteType.AdsGoogleLsa ||
					customerMeeting.metWith === AdminModels.InternalMeetingNoteType.AdsGooglePpc
				) {
					const refreshedAccount = await this.mUserSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${account.id}`,
						'GET'
					);
					runInAction(() => {
						this.mAccount = refreshedAccount;
					});
				} else {
					runInAction(() => {
						this.mAccount = {
							...this.mAccount,
							additionalInfo: {
								...(this.mAccount.additionalInfo || {}),
								csmNoteId: value.id,
							},
						};
					});
				}
				return value;
			} finally {
				runInAction(() => {
					this.mSaveAccountPromise = null;
					this.mSavingAccount = false;
				});
			}
		}
	};

	public updateShareFileFolderLink = (account: Api.IAccount, folderLink: string) => {
		if (!this.isBusy) {
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callAsync<Api.IAccount>(
						`admin/account/${account.id}/additionalInfo/folderLink?folderLink=${encodeURIComponent(folderLink) || ''}`,
						'PUT'
					)
					.then((value: Api.IAccount) => {
						runInAction(() => {
							this.mAccount = value;
						});
						resolve(value);
					})
					.catch(e => {
						reject(e);
					})
					.finally(() => {
						this.mSaveAccountPromise = null;
					});
			});
			this.mSaveAccountPromise = promise;
			return promise;
		}
	};

	public updateNextMeeting = (nextMeeting: Date) => {
		if (!this.isBusy) {
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callAsync<Api.IAccount>(
						`admin/account/${this.account.id}/additionalInfo/nextMeeting?nextMeeting=${
							encodeURIComponent(moment(nextMeeting).format('MM/DD/YYYY')) || ''
						}`,
						'PUT'
					)
					.then((value: Api.IAccount) => {
						runInAction(() => {
							this.mAccount = value;
						});
						resolve(value);
					})
					.catch(e => {
						reject(e);
					})
					.finally(() => {
						this.mSaveAccountPromise = null;
					});
			});
			this.mSaveAccountPromise = promise;
			return promise;
		}

		return null;
	};

	public updateNextWebsitesMeeting = (nextMeeting: Date) => {
		if (!this.isBusy) {
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callAsync<Api.IAccount>(
						`admin/account/${this.account.id}/additionalInfo/nextWebsitesMeeting?nextMeeting=${
							encodeURIComponent(moment(nextMeeting).format('MM/DD/YYYY')) || ''
						}`,
						'PUT'
					)
					.then((value: Api.IAccount) => {
						runInAction(() => {
							this.mAccount = value;
						});
						resolve(value);
					})
					.catch(e => {
						reject(e);
					})
					.finally(() => {
						this.mSaveAccountPromise = null;
					});
			});
			this.mSaveAccountPromise = promise;
			return promise;
		}

		return null;
	};

	public updateNextAdsMeeting = (
		nextMeeting: Date,
		channel?:
			| AdminModels.InternalMeetingNoteType.AdsFacebook
			| AdminModels.InternalMeetingNoteType.AdsGoogleLsa
			| AdminModels.InternalMeetingNoteType.AdsGooglePpc
	) => {
		if (!this.isBusy) {
			const params = [`nextMeeting=${encodeURIComponent(moment(nextMeeting).format('MM/DD/YYYY'))}`];
			if (channel) {
				params.push(`channel=${encodeURIComponent(channel)}`);
			}
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callAsync<Api.IAccount>(
						`admin/account/${this.account.id}/additionalInfo/nextAdsMeeting?${params.join('&')}`,
						'PUT'
					)
					.then((value: Api.IAccount) => {
						runInAction(() => {
							this.mAccount = value;
						});
						resolve(value);
					})
					.catch(e => {
						reject(e);
					})
					.finally(() => {
						runInAction(() => {
							this.mSaveAccountPromise = null;
						});
					});
			});
			this.mSaveAccountPromise = promise;
			return promise;
		}

		return null;
	};

	public updateAdsLiveDate = (
		channel:
			| AdminModels.InternalMeetingNoteType.AdsFacebook
			| AdminModels.InternalMeetingNoteType.AdsGoogleLsa
			| AdminModels.InternalMeetingNoteType.AdsGooglePpc,
		liveDate?: Date | null
	) => {
		if (!this.isBusy) {
			const params = [`channel=${encodeURIComponent(channel)}`];
			if (liveDate) {
				params.push(`liveDate=${encodeURIComponent(moment(liveDate).format('YYYY-MM-DD'))}`);
			}
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callAsync<Api.IAccount>(
						`admin/account/${this.account.id}/additionalInfo/adsLiveDate?${params.join('&')}`,
						'PUT'
					)
					.then((value: Api.IAccount) => {
						runInAction(() => {
							this.mAccount = value;
						});
						resolve(value);
					})
					.catch(e => {
						reject(e);
					})
					.finally(() => {
						runInAction(() => {
							this.mSaveAccountPromise = null;
						});
					});
			});
			this.mSaveAccountPromise = promise;
			return promise;
		}

		return null;
	};

	public updateAccountFlagged = () => {
		if (!this.isBusy) {
			const updatedFlagged = !this.account?.additionalInfo?.accountFlagged;
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				this.mUserSession.webServiceHelper
					.callAsync<Api.IAccount>(
						`admin/account/${this.account.id}/additionalInfo/accountFlagged?accountFlagged=${encodeURIComponent(
							updatedFlagged
						)}`,
						'PUT'
					)
					.then((value: Api.IAccount) => {
						runInAction(() => {
							this.mAccount = value;
						});
						resolve(value);
					})
					.catch(error => {
						reject(error);
					})
					.finally(() => {
						this.mSaveAccountPromise = null;
					});
			});
			this.mSaveAccountPromise = promise;
			return promise;
		}

		return null;
	};

	public updateAutomationFeatures(features: Api.IAutomationFeatures) {
		return this.updateAccountFeatures('automation', features);
	}

	public updateConnectionTypesFeature(features: Api.IConnectionTypesFeature) {
		return this.updateAccountFeatures('connectionTypes', features);
	}

	public updateCrossAccountTemplateSharingFeature(features: Api.ICrossAccountTemplateSharingFeature) {
		return this.updateAccountFeatures('crossAccountTemplateSharing', features);
	}

	public async updateHtmlNewsletterFeatures(features: Api.IHtmlNewsletterFeatures) {
		return this.updateAccountFeatures('htmlNewsletter', features);
	}

	public async updateMeetingSchedulerFeatures(features: Api.IMeetingSchedulerFeature) {
		return this.updateAccountFeatures('meetingScheduler', features);
	}

	public async updateTextingFeatures(features: Api.ITextingFeature) {
		return this.updateAccountFeatures('texting', features);
	}

	public async updateHandwrittenFeatures(features: Api.IHandwrittenCardsFeature) {
		return this.updateAccountFeatures('handwrittenCards', features);
	}

	public async updateHouseholdsFeature(features: Api.IHouseholdsFeature) {
		return this.updateAccountFeatures('households', features);
	}

	public async updateTimelineFeatures(features: Api.IFeature) {
		return this.updateAccountFeatures('timeline', features);
	}

	public async updateSurveyFeatures(features: Api.ISurveysFeature) {
		return this.updateAccountFeatures('surveys', features);
	}

	public async updateSocialMediaFeatures(features: Api.ISocialMediaFeature) {
		return this.updateAccountFeatures('socialMedia', features);
	}

	public async updateTranslationFeatures(features: Api.ITranslationFeature) {
		return this.updateAccountFeatures('translation', features);
	}

	public async updateContentGenerationFeatures(features: Api.IContentGenerationFeature) {
		return this.updateAccountFeatures('contentGeneration', features);
	}

	public async updateCustomContentCalendarFeatures(features: Api.ICustomContentCalendarFeature) {
		return this.updateAccountFeatures('customContentCalendar', features);
	}

	public async updatePublicAPIFeatures(features: Api.IPublicAPIFeature) {
		return this.updateAccountFeatures('publicApi', features);
	}

	public async updateStripePaymentsFeatures(features: Api.IFeature) {
		return this.updateAccountFeatures('stripe' as keyof Api.IAccountFeatures, features);
	}

	public async updateDefaultEmailStyleFeatures(features: Api.IDefaultEmailStyleFeature) {
		return this.updateAccountFeatures('defaultEmailStyle', features);
	}

	public async updateKeepInTouchAgentFeatures(features: Api.IKeepInTouchAgentFeature) {
		return this.updateAccountFeatures('keepInTouch', features);
	}

	private async updateAccountFeatures(path: keyof Api.IAccountFeatures, feature: Api.IFeature) {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IFeature>(
				this.composeApiUrl({ urlPath: `account/features/${path}` }),
				'PUT',
				feature
			);
			if (opResult.success) {
				produce(this.mAccount, (draft = {}) => {
					draft.features = draft.features || {};
					// FOLLOWUP: Resolve

					// @ts-ignore
					draft.features[path] = opResult.value;
				});
			}
			this.busy = false;
			return opResult;
		}
	}

	public async updateClickTracking(shouldTrack: boolean) {
		return this.toggleAccountPreference('clickTrackingEnabled', shouldTrack, 'shouldTrack');
	}

	public async updateCsmViewMyCampaignsEnabled(canView: boolean) {
		return this.toggleAccountPreference('csmViewMyCampaignsEnabled', canView, 'canView');
	}

	public async updateComplianceEnabled(enabled: boolean) {
		return this.toggleAccountPreference('compliance', enabled, 'enabled');
	}

	public async updateComplianceWaitTime(duration: string) {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/preferences/compliance/waitTime?value=${duration}` }),
				'PUT'
			);
			if (opResult.success) {
				this.mAccount = opResult.value;
			}
			this.busy = false;
			return opResult;
		}
	}

	public async updateSendComplianceReviewResultEmails(enabled: boolean) {
		return this.toggleAccountPreference('compliance/sendComplianceReviewResultEmails', enabled, 'enabled');
	}

	public async updateComplianceRequired(enabled: boolean) {
		return this.toggleAccountPreference('compliance/required', enabled, 'enabled');
	}

	public toggleTwoFactorAuth = async (enabled: boolean) => {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ queryParams: { enabled }, urlPath: `account/requireSecondAuthFactor` }),
				'PUT'
			);
			this.busy = false;
			if (opResult.success) {
				this.mAccount = opResult.value;
			} else {
				throw opResult;
			}
			return opResult;
		}
	};

	public updateOAuthLoginSettings = async (oauthLoginSettings: Api.IOAuthLoginSettings) => {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IOAuthLoginSettings>(
				this.composeApiUrl({ urlPath: `account/oauth` }),
				'PUT',
				oauthLoginSettings
			);
			this.busy = false;
			if (opResult.success) {
				this.mAccount = { ...this.mAccount, oAuthLoginSettings: opResult.value };
			} else {
				throw opResult;
			}
			return opResult;
		}
	};

	public updateBlogEnabled = async (enabled: boolean) => {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ queryParams: { enabled }, urlPath: `account/features/blog` }),
				'PUT'
			);
			this.busy = false;
			if (opResult.success) {
				this.mAccount = opResult.value;
			} else {
				throw opResult;
			}
			return opResult;
		}
	};

	public updateHIPAAEnabled = async (enabled: boolean) => {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IHIPAAFeature>(
				this.composeApiUrl({ urlPath: `account/features/hipaa` }),
				'PUT',
				{ enabled }
			);
			this.busy = false;
			if (opResult.success) {
				this.mAccount = produce(this.mAccount, (draft = {}) => {
					draft.features = draft.features || {};
					draft.features.hipaa = opResult.value;
				});
			} else {
				throw opResult;
			}
			return opResult;
		}
	};

	public updateBlogFeatures = async ({
		domain,
		enabled,
		allowedOrigins = [],
		dudaSiteId,
	}: Partial<Api.IBlogFeature>) => {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/features/blogfeature` }),
				'PUT',
				{ ...(this.mAccount?.features?.blogFeature || {}), domain, enabled, allowedOrigins, dudaSiteId }
			);
			this.busy = false;
			if (opResult.success) {
				this.mAccount = opResult.value;
			} else {
				throw opResult;
			}
			return opResult;
		}
	};

	public updateWebsitesFeature = async ({ enabled, websiteUrl, dudaSiteId }: Partial<Api.IWebsitesFeature>) => {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/features/websites` }),
				'PUT',
				{ ...(this.mAccount?.features?.websites || {}), dudaSiteId, enabled, websiteUrl }
			);
			this.busy = false;
			if (opResult.success) {
				this.mAccount = opResult.value;
			} else {
				throw opResult;
			}
			return opResult;
		}
	};

	public async updateDisableMobileApps(value: boolean) {
		return this.toggleAccountPreference('disableMobileApps', value, 'value');
	}

	public async updateHideCompanies(value: boolean) {
		return this.toggleAccountPreference('hideCompanies', value, 'value');
	}

	public async updateHideContactCompanyEmployeeReport(value: boolean) {
		return this.toggleAccountPreference('hideContactCompanyEmployeeReport', value, 'value');
	}

	public async updateHideEmailCampaignSignature(value: boolean) {
		return this.toggleAccountPreference('hideEmailCampaignSignature', value, 'value');
	}

	public async updatShowAllContactsByDefault(value: boolean) {
		return this.toggleAccountPreference('showAllContactsByDefault', value, 'value');
	}

	public async updateAnyUserEditingNotes(canEdit: boolean) {
		return this.toggleAccountPreference('noteViewerCanEditEnabled', canEdit, 'canEdit');
	}

	public async toggleAccountPreference(path: string, enabled: boolean, paramName = 'enabled') {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/preferences/${path}?${paramName}=${enabled}` }),
				'PUT'
			);
			if (opResult.success) {
				this.mAccount = opResult.value;
			}
			this.busy = false;
			return opResult;
		}
	}

	public getUserCount = (): Promise<number> => {
		this.impersonate({ account: { id: this.mAccount.id } });
		return new Promise<number>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<number>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<number>(
				`impersonate/${this.mAccount.id}/account/${this.mAccount.id}/userCount`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public getSelfServicePaymentUrl = (customerId: number): Promise<Api.IOperationResult<string>> => {
		return new Promise<Api.IOperationResult<string>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<string>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<string>(
				`admin/BillingUtilities/GetSelfServicePaymentUrl?chargifyCustomerId=${encodeURIComponent(customerId)}`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public getProformaInvoiceUrl = (customerId: number): Promise<Api.IOperationResult<string>> => {
		return new Promise<Api.IOperationResult<string>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<string>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<string>(
				`admin/BillingUtilities/GetProformaInvoiceUrl?chargifyCustomerId=${encodeURIComponent(customerId)}`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public async updateAccountPreferenceString(path: string, value: string, paramName = 'value') {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/preferences/${path}?${paramName}=${value}` }),
				'PUT'
			);
			if (opResult.success) {
				this.mAccount = opResult.value;
			}
			this.busy = false;
			return opResult;
		}
	}

	// Custom Calendar API Methods
	public async updateCustomCalendarStatus(status: string): Promise<Api.IOperationResult<Api.IAccount> | undefined> {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				`admin/Account/${encodeURIComponent(this.mAccount.id)}/features/customContentCalendar/status?status=${encodeURIComponent(status)}`,
				'PUT'
			);
			if (opResult.success) {
				// Force update the account state using MobX action
				runInAction(() => {
					this.mAccount = opResult.value;
				});
			}
			this.busy = false;
			return opResult;
		}
	}

	public async updateCustomCalendarIntakeFormDue(
		dueDate: string | null
	): Promise<Api.IOperationResult<Api.IAccount> | undefined> {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const url = dueDate
				? `admin/Account/${encodeURIComponent(this.mAccount.id)}/features/customContentCalendar/nextIntakeFormDue?dueDate=${encodeURIComponent(dueDate)}`
				: `admin/Account/${encodeURIComponent(this.mAccount.id)}/features/customContentCalendar/nextIntakeFormDue`;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(url, 'PUT');
			if (opResult.success) {
				// Force update the account state using MobX action
				runInAction(() => {
					this.mAccount = opResult.value;
				});
			}
			this.busy = false;
			return opResult;
		}
	}

	public async updateCustomCalendarEstimatedDeliveryDate(
		estimatedDeliveryDate: string | null
	): Promise<Api.IOperationResult<Api.IAccount> | undefined> {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const url = estimatedDeliveryDate
				? `admin/account/${encodeURIComponent(this.mAccount.id)}/features/customContentCalendar/estimatedDeliveryDate?estimatedDeliveryDate=${encodeURIComponent(estimatedDeliveryDate)}`
				: `admin/account/${encodeURIComponent(this.mAccount.id)}/features/customContentCalendar/estimatedDeliveryDate`;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(url, 'PUT');
			if (opResult.success) {
				runInAction(() => {
					this.mAccount = opResult.value;
				});
			}
			this.busy = false;
			return opResult;
		}
	}

	public async updateCustomCalendarModifications(
		modifications: string
	): Promise<Api.IOperationResult<Api.IAccount> | undefined> {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				`admin/Account/${encodeURIComponent(this.mAccount.id)}/features/customContentCalendar/modifications?modifications=${encodeURIComponent(modifications)}`,
				'PUT'
			);
			if (opResult.success) {
				// Force update the account state using MobX action
				runInAction(() => {
					this.mAccount = opResult.value;
				});
			}
			this.busy = false;
			return opResult;
		}
	}

	public async toggleSaveForAnalysis() {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IEmailScanFeatures>(
				this.composeApiUrl({ urlPath: `account/features/emailScan/toggleSaveForAnalysis` }),
				'PATCH'
			);
			if (opResult.success) {
				produce(this.mAccount, (draft = {}) => {
					draft.features = draft.features || {};
					draft.features.emailScan = opResult.value;
				});
			}
			this.busy = false;
			return opResult;
		}
	}

	public async updatePreApprovedFeature(feature: Partial<Api.ICampaignTemplateReviewSettings>) {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/features/templateReviewSettings/preApproved` }),
				'PUT',
				feature
			);
			if (opResult.success) {
				this.busy = false;
				return opResult;
			}
		}
	}

	public async updatePreReviewedFeature(feature: Partial<Api.ICampaignTemplateReviewSettings>) {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/features/templateReviewSettings/preReviewed` }),
				'PUT',
				feature
			);
			if (opResult.success) {
				this.busy = false;
				return opResult;
			}
		}
	}

	public async updateRejectedFeature(feature: Partial<Api.ICampaignTemplateReviewSettings>) {
		this.impersonate({ account: { id: this.mAccount.id } });
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccount>(
				this.composeApiUrl({ urlPath: `account/features/templateReviewSettings/rejected` }),
				'PUT',
				feature
			);
			if (opResult.success) {
				this.busy = false;
				return opResult;
			}
		}
	}
}

export class AdminUserViewModel extends Api.UserViewModel {
	constructor(userSession: AdminUserSessionContext, user: Api.IUser, impersonationContext?: Api.IImpersonationContext) {
		super(userSession, user);

		makeObservable(this, {
			load: override,
		});

		this.mImpersonationContext = impersonationContext;
	}

	public load(forceReload = false) {
		if (this.isLoaded && !forceReload) {
			return Promise.resolve(this.mUser);
		}

		if (!this.loadingPromise) {
			this.loading = true;
			this.loadingPromise = new Promise<Api.IUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IUser>) => {
					runInAction(() => {
						this.loading = false;
						this.loadingPromise = null;
						if (result.success) {
							this.mSetUser(result.value);
							this.loaded = true;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUser>(
					`${this.mImpersonationContext ? `impersonate/${this.mImpersonationContext.account.id}/` : ''}user/${
						this.mUser.id
					}`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return this.loadingPromise;
	}
}

export class AccountUserViewModel extends Api.UserViewModel {
	protected mPhoneNumberOrders: Api.ITelephonyConfiguration[];
	constructor(userSession: Api.UserSessionContext, user: Api.IUserWithStats | Api.IUser) {
		super(userSession, user);

		makeObservable<AccountUserViewModel, 'mPhoneNumberOrders'>(this, {
			mPhoneNumberOrders: observable,
			userWithStats: computed,
			userRoleDescription: computed,
			primaryEmailDomain: computed,
			emailCalendarTokenExists: computed,
			reconnectRequired: computed,
			textingPhoneNumber: computed,
			updateUserStatus: action,
			loadUserStats: action,
			updateTrackingPixelPollingValue: action,
			updateEmailConnectionOptOutValue: action,
			updateUserPreferencesAsAdmin: action,
			updateRole: action,
			getErrors: action,
			sendPasswordReset: action,
			generatePasswordResetLink: action,
			deleteAdmin: action,
			changeEmail: action,
			getWelcomeEmailLink: action,
			getUserPhone: action,
			getLoginAsCredentials: action,
			getProfileInternalContactId: action,
			changeSendFromEmailAddress: action,
			changeUseSharedMailbox: action,
			deleteSendFromEmailAddress: action,
			getAliases: action,
		});
	}

	public get userWithStats() {
		return this.mUser as Api.IUserWithStats;
	}

	public get userRoleDescription() {
		return this.role ? UserRoleName[this.role] : this.groups.findIndex(x => x === 'admin') >= 0 ? 'Admin' : 'Employee';
	}

	public get primaryEmailDomain() {
		return this.mUser?.primaryEmail?.value?.split('@')[1];
	}

	public get emailCalendarTokenExists() {
		return this.mUser?.emailCalendarTokenExists;
	}

	public get reconnectRequired() {
		return this.mUser?.reconnectRequired;
	}

	public get textingPhoneNumber() {
		return this.mPhoneNumberOrders?.[0];
	}
	public set textingPhoneNumber(value: Api.ITelephonyConfiguration) {
		this.mPhoneNumberOrders[0] = value;
	}

	public updateUserStatus = (path: AdminModels.UserStatusAction) => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IUserWithStats>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IUserWithStats>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mUser = opResult.value;
							this.busy = false;
							resolve(opResult.value);
						} else {
							this.busy = false;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUserWithStats>(
					`admin/userOperations/${this.id}/${path}`,
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	public loadUserStats = () => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IUserStats>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IUserStats>) => {
					runInAction(() => {
						if (opResult.success) {
							const userStats: Api.IUserWithStats = {
								...(this.mUser || {}),
								stats: opResult.value,
							};
							this.mSetUser(userStats);
							this.busy = false;
							resolve(opResult.value);
						} else {
							this.busy = false;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUserStats>(
					`admin/userOperations/${this.id}/stats`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	public updateTrackingPixelPollingValue = (prefs: Api.IUserPreferences, enabled: boolean) => {
		if (!this.isBusy) {
			this.busy = true;
			const newPreferences = produce(prefs, (draft = {}) => {
				draft.clients = draft?.clients || {};
				draft.clients.outlookClient = draft.clients.outlookClient || {};
				draft.clients.outlookClient.trackingPixelPollingEnabled = enabled;
			});
			const promise = new Promise<Api.IUser>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IUser>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mUser = {
								...this.userWithStats,
								...opResult.value,
							};
							this.busy = false;
							resolve(opResult.value);
						} else {
							this.busy = false;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUser>(
					`admin/userOperations/${this.id}/userPreferences`,
					'PUT',
					newPreferences,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	public updateEmailConnectionOptOutValue = (optout: boolean) => {
		if (!this.isBusy) {
			this.busy = true;

			const promise = new Promise<Api.IUser>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IUser>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mUser = {
								...this.userWithStats,
								...opResult.value,
							};
							this.busy = false;
							resolve(opResult.value);
						} else {
							this.busy = false;
							reject(opResult);
						}
					});
				};
				const url = this.composeApiUrl({
					queryParams: { value: optout },
					urlPath: 'user/userPreferences/emailConnectionOptOut',
				});
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUser>(
					url,
					'PUT',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	public updateUserPreferencesAsAdmin = (newPreferences: Api.IUserPreferences) => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IUser>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IUser>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mUser = {
								...this.userWithStats,
								...opResult.value,
							};
							this.busy = false;
							resolve(opResult.value);
						} else {
							this.busy = false;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUser>(
					`admin/userOperations/${this.id}/userPreferences`,
					'PUT',
					newPreferences,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	public updateRole = (newRole: Api.UserRole) => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<Api.IUserStats>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IUserStats>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mUser = {
								...this.userWithStats,
								...opResult.value,
							};
							this.busy = false;
							resolve(opResult.value);
						} else {
							this.busy = false;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUserStats>(
					`admin/userOperations/${this.id}/setRole?role=${newRole}`,
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	public getErrors = () => {
		const promise = new Promise<AdminModels.IPlatformError[]>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<AdminModels.IPlatformError[]>) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult.value);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IPlatformError[]>(
				`admin/userOperations/${this.id}/recentErrors?days=30`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public sendPasswordReset = () => {
		const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResultNoValue) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults(
				`admin/userOperations/${this.id}/sendResetPasswordEmail`,
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public generatePasswordResetLink = () => {
		if (!this.mImpersonationContext) {
			throw new Error('Impersonation context is required to generate password reset link');
		}

		const promise = new Promise<Api.IOperationResult<string>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<string>) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults(
				`impersonate/${this.mImpersonationContext.account.id}/user/generatePasswordResetLink?impUserId=${this.id}`,
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public deleteAdmin = () => {
		const promise = new Promise<Api.IOperationResult<void>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<void>) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<void>(
				`admin/UserOperations/${this.id}`,
				'DELETE',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public changeEmail = (email: string, reason: string, type: EmailChangeType) => {
		const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const data = { newEmail: email, reason };
			const onFinish = (opResult: Api.IOperationResultNoValue) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			const urlPath = type === EmailChangeType.Authenticator ? 'user/factors/email/changeEmail' : 'user/changeEmail';
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				this.composeApiUrl({ urlPath }),
				'POST',
				data,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public changeSendFromEmailAddress = (emailAddress: string) => {
		const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResultNoValue) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				this.composeApiUrl({ urlPath: `User/preferences/sendFromEmailAddress`, queryParams: { emailAddress } }),
				'PUT',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public deleteSendFromEmailAddress = () => {
		const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResultNoValue) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				this.composeApiUrl({ urlPath: `User/preferences/sendFromEmailAddress` }),
				'DELETE',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public changeUseSharedMailbox = (useSharedMailbox: boolean) => {
		const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResultNoValue) => {
				runInAction(() => {
					if (opResult.success) {
						this.busy = false;
						resolve(opResult);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
				this.composeApiUrl({ urlPath: `User/preferences/useSharedMailbox`, queryParams: { useSharedMailbox } }),
				'PUT',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public getWelcomeEmailLink = async () => {
		try {
			const result = await this.userSession.webServiceHelper.callWebServiceAsync<Api.IWelcomeEmail>(
				`admin/userOperations/welcomeUrl?userId=${this.id}`,
				'POST'
			);
			if (result.success) {
				return result.value;
			} else {
				throw Api.asApiError(result);
			}
		} catch (err) {
			throw Api.asApiError(err);
		}
	};

	public getUserPhone = async () => {
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<Api.ITelephonyConfiguration[]>(
			this.composeApiUrl({ urlPath: 'PhoneNumber/user' }),
			'GET'
		);
		if (result.success) {
			this.mPhoneNumberOrders = result.value;
			return result.value;
		} else {
			throw Api.asApiError(result);
		}
	};

	public getLoginAsCredentials = () => {
		const promise = new Promise<Api.IOperationResult<ICredential>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<ICredential>) => {
				if (opResult.success) {
					this.busy = false;
					resolve(opResult);
				} else {
					this.busy = false;
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults(
				`admin/account/${this.accountId}/CreateImpersonationToken?userId=${this.id}`,
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public getProfileInternalContactId = async (userId: string) => {
		const promise = new Promise<Api.IOperationResult<string>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<string>) => {
				if (opResult.success) {
					this.busy = false;
					resolve(opResult);
				} else {
					this.busy = false;
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults(
				`admin/userOperations/${userId}/internalContactId`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	public getAliases = async () => {
		const promise = new Promise<Api.IOperationResult<string[]>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<string[]>) => {
				if (opResult.success) {
					this.busy = false;
					resolve(opResult);
				} else {
					this.busy = false;
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults(
				this.composeApiUrl({ urlPath: `user/aliases` }),
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};
}

export class AccountUsersViewModel extends Api.ViewModel {
	private mAccount: Api.IAccount;
	private usersPageCollectionController: Api.BaseObservablePageCollectionController<
		Api.IUserWithStats,
		AccountUserViewModel
	>;
	protected mAdmins: AccountUserViewModel[];

	constructor(userSession: Api.UserSessionContext, account: Api.IAccount) {
		super(userSession);

		makeObservable<AccountUsersViewModel, 'mAccount' | 'usersPageCollectionController' | 'mAdmins'>(this, {
			mAccount: observable.ref,
			usersPageCollectionController: observable.ref,
			mAdmins: observable.ref,
			admins: computed,
			loadAdmins: action,
			loadUsers: action,
			reset: action,
			accountUsers: computed,
			totalNumberOfResults: computed,
			isLoading: override,
			resendWelcome: action,
		});

		this.mAccount = account;
		this.mAdmins = [];
		this.usersPageCollectionController = new Api.BaseObservablePageCollectionController<
			Api.IUserWithStats,
			AccountUserViewModel
		>({
			apiPath: `admin/userOperations/byAccount`,
			client: userSession.webServiceHelper,
			transformer: this.mCreateAccountUserViewModel,
		});
	}

	private mCreateAccountUserViewModel = (user: Api.IUserWithStats) => {
		return new AccountUserViewModel(this.mUserSession, user);
	};

	public get admins() {
		return this.mAdmins;
	}

	public loadAdmins = async () => {
		try {
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IUser[]>(
				`impersonate/${this.mAccount?.id}/user/admins`,
				'GET'
			);
			if (!success) {
				throw opResult;
			}
			this.mAdmins = opResult.value.map(x => new AccountUserViewModel(this.mUserSession, x));
			return this.mAdmins;
		} catch (error) {
			throw Api.asApiError(error);
		}
	};

	public loadUsers = () => {
		return this.usersPageCollectionController.getNext(
			{
				accountId: this.mAccount.id,
				expand: 'Stats',
			},
			500
		);
	};

	public reset = () => {
		this.usersPageCollectionController.reset();
	};

	public get accountUsers(): Api.ObservableCollection<AccountUserViewModel> {
		return this.usersPageCollectionController.fetchResults;
	}

	public get totalNumberOfResults() {
		return this.usersPageCollectionController.totalCount;
	}

	public get isLoading() {
		return this.usersPageCollectionController.isFetching;
	}

	public resendWelcome = (userId: string) => {
		const promise = new Promise<Api.IUserWithStats>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IUserWithStats>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults(
				`admin/userOperations/resendWelcomeEmail?userId=${userId}`, // URL
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
		return promise;
	};
}

export enum AccountFilterCriteriaProperty {
	All = 'All',
	Name = 'Name',
	SuccessManager = 'SuccessManager',
	SalesRep = 'SalesRep',
	BillingType = 'BillingType',
	AccountType = 'AccountType',
	ActiveUseStatus = 'ActiveUseStatus',
	ActivationStatus = 'ActivationStatus',
	Industry = 'Industry',
	Email = 'Email',
	RenewalMonth = 'RenewalMonth',
	CancellationDate = 'CancellationDate',
	BillingCycle = 'BillingCycle',
	NoSale = 'NoSale',
	Flagged = 'Flagged',
	SubscriptionLength = 'SubscriptionLength',
	CreationMonth = 'CreationMonth',
	NoStart = 'NoStart',
	Websites = 'Websites',
	Id = 'Id',
	HasNoSalesNotes = 'HasNoSalesNotes',
	HasAds = 'HasAds',
	HasFacebookAds = 'HasFacebookAds',
	HasGoogleLocalServiceAds = 'HasGoogleLocalServiceAds',
	HasGooglePpc = 'HasGooglePpc',
	CustomCalendarEnabled = 'CustomCalendarEnabled',
	CustomCalendarIntakeFormNeeded = 'CustomCalendarIntakeFormNeeded',
}

export type IAccountFilterCriteria = Api.IFilterCriteria<AccountFilterCriteriaProperty>;

export type IAccountsFilterRequest = IAccountFilterCriteria;

export interface IAccountsExportsApiRequest {
	filter?: IAccountsFilterRequest;
	useSnapshots?: boolean;
}

export class AccountsAdminViewModel extends Api.ViewModel {
	private mLastSuccessfulRequest: IAccountsFilterRequest;
	private nextRequest: IAccountsFilterRequest;
	private aggregateActiveUse: AdminModels.IAggregateActiveUse;
	public static SearchCriteriaProperties: AccountFilterCriteriaProperty[] = [
		AccountFilterCriteriaProperty.All,
		AccountFilterCriteriaProperty.Name,
		AccountFilterCriteriaProperty.SuccessManager,
		AccountFilterCriteriaProperty.SalesRep,
		AccountFilterCriteriaProperty.BillingType,
		AccountFilterCriteriaProperty.AccountType,
		AccountFilterCriteriaProperty.ActiveUseStatus,
		AccountFilterCriteriaProperty.ActivationStatus,
		AccountFilterCriteriaProperty.Industry,
		AccountFilterCriteriaProperty.Email,
		AccountFilterCriteriaProperty.RenewalMonth,
		AccountFilterCriteriaProperty.SubscriptionLength,
		AccountFilterCriteriaProperty.CreationMonth,
		AccountFilterCriteriaProperty.Websites,
		AccountFilterCriteriaProperty.Id,
	];

	public static getAllByIds = (userSession: Api.UserSessionContext, accountIds: string[]) => {
		const promise = new Promise<AccountAdminViewModel[]>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IAccount[]>) => {
				if (opResult.success) {
					const accounts = opResult.value.map(x => new AccountAdminViewModel(userSession, x));
					resolve(accounts);
				} else {
					reject(opResult);
				}
			};
			userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount[]>(
				'account/byIds',
				'POST',
				accountIds,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable<AccountsAdminViewModel, 'mLastSuccessfulRequest' | 'nextRequest' | 'aggregateActiveUse'>(this, {
			mLastSuccessfulRequest: observable.ref,
			nextRequest: observable.ref,
			aggregateActiveUse: observable.ref,
			exportFullReport: action,
			activeUse: computed,
			filterRequest: computed,
			isSearching: computed,
			isBusy: override,
			reset: action,
		});
	}

	private exportFilteredAccounts(criteriaRequest: IAccountsExportsApiRequest) {
		const promise = new Promise<Api.ISystemJob>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ISystemJob>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
				'admin/account/export',
				'POST',
				criteriaRequest,
				onFinish,
				onFinish
			);
		});
		return promise;
	}

	public exportFullReport() {
		const filter: IAccountFilterCriteria = {
			criteria: [],
			op: Api.FilterOperator.Or,
		};
		const request: IAccountsExportsApiRequest = {
			filter,
			useSnapshots: false,
		};
		const promise = new Promise<Api.ISystemJob>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.ISystemJob>) => {
				if (opResult.success) {
					resolve(opResult.value);
				} else {
					reject(opResult);
				}
			};
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISystemJob>(
				'admin/account/export',
				'POST',
				request,
				onFinish,
				onFinish
			);
		});
		return promise;
	}

	public get activeUse() {
		return this.aggregateActiveUse;
	}

	public get filterRequest() {
		return this.mLastSuccessfulRequest;
	}

	public get isSearching() {
		const accountsFilterRequest = this.nextRequest || this.mLastSuccessfulRequest;
		if (accountsFilterRequest) {
			return (
				accountsFilterRequest.criteria &&
				accountsFilterRequest.criteria.find(
					x => x.property && x.value && AccountsAdminViewModel.SearchCriteriaProperties.indexOf(x.property) >= 0
				)
			);
		}
		return false;
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	public reset = () => {
		this.busy = false;
		this.loading = false;
		this.mLastSuccessfulRequest = null;
		this.nextRequest = null;
	};

	public exportAccounts(criteria?: IAccountFilterCriteria[], useSnapshots = false) {
		const criteriaObject: IAccountsExportsApiRequest = {
			filter: { criteria: [...(criteria || [])], op: Api.FilterOperator.And },
			useSnapshots,
		};
		return this.exportFilteredAccounts(criteriaObject);
	}
}

export class AccountAdminViewModel extends Api.AccountViewModel {
	private mUpdatePdfSentPromise: Promise<Api.IOperationResult<Api.IAccount>>;
	private mUpdateRenewalPromise: Promise<Api.IOperationResult<Api.IAccount>>;
	private mUpdateRenewalDiscountPercentagePromise: Promise<Api.IOperationResult<Api.IAccount>>;
	private mUpdateSendEmailFeaturesPromise: Promise<Api.IOperationResult<Api.ISendEmailFeatures>>;
	private mUpdateStagePromise: Promise<Api.IOperationResult<Api.IAccount>>;
	private mUpdateNextMeetingPromise: Promise<Api.IOperationResult<Api.IAccount>>;

	public static Create = (userSession: Api.UserSessionContext, accountModel: Api.IAccount) => {
		const promise = new Promise<AccountAdminViewModel>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IAccount>) => {
				if (opResult.success) {
					const account = new AccountAdminViewModel(userSession, opResult.value);
					resolve(account);
				} else {
					reject(opResult);
				}
			};
			userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
				'account',
				'POST',
				accountModel,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	constructor(userSession: Api.UserSessionContext, account: Api.IAccount) {
		super(userSession, account);

		makeObservable<
			AccountAdminViewModel,
			| 'mUpdatePdfSentPromise'
			| 'mUpdateRenewalPromise'
			| 'mUpdateRenewalDiscountPercentagePromise'
			| 'mUpdateSendEmailFeaturesPromise'
			| 'mUpdateStagePromise'
			| 'mUpdateNextMeetingPromise'
			| 'account'
		>(this, {
			mUpdatePdfSentPromise: observable.ref,
			mUpdateRenewalPromise: observable.ref,
			mUpdateRenewalDiscountPercentagePromise: observable.ref,
			mUpdateSendEmailFeaturesPromise: observable.ref,
			mUpdateStagePromise: observable.ref,
			mUpdateNextMeetingPromise: observable.ref,
			account: computed,
			updateRenewalProbability: action,
			updateRenewalDiscountPercentage: action,
			updateStage: action,
			updateSendEmailFeatures: action,
			updateRenewalPdfSent: action,
			isUpdatingSendEmailFeatures: computed,
			isUpdatingStage: computed,
			isUpdatingRenewalProbability: computed,
			isUpdatingRenewalDiscountPercentage: computed,
			isUpdatingRenewalPdfSent: computed,
			isUpdatingNextMeeting: computed,
		});

		this.mAccount = account;
	}

	protected get account() {
		return this.mAccount;
	}

	public updateRenewalProbability = (renewalProbability: number) => {
		if (!this.mUpdateRenewalPromise) {
			this.mUpdateRenewalPromise = new Promise<Api.IOperationResult<Api.IAccount>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IAccount>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mAccount = opResult.value;
							this.mUpdateRenewalPromise = null;
							resolve(opResult);
						} else {
							this.mUpdateRenewalPromise = null;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
					`admin/account/${this.id}/additionalInfo/renewalProbability?renewalProbability=${renewalProbability}`,
					'PATCH',
					null,
					onFinish,
					onFinish
				);
			});
			return this.mUpdateRenewalPromise;
		}
	};

	public updateRenewalDiscountPercentage = (renewalDiscountPercentage: number) => {
		if (!this.mUpdateRenewalDiscountPercentagePromise) {
			this.mUpdateRenewalDiscountPercentagePromise = new Promise<Api.IOperationResult<Api.IAccount>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<Api.IAccount>) => {
						runInAction(() => {
							if (opResult.success) {
								this.mAccount = opResult.value;
								this.mUpdateRenewalDiscountPercentagePromise = null;
								resolve(opResult);
							} else {
								this.mUpdateRenewalDiscountPercentagePromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
						`admin/account/${this.id}/additionalInfo/renewalDiscountPercentage?value=${renewalDiscountPercentage}`,
						'PATCH',
						null,
						onFinish,
						onFinish
					);
				}
			);
			return this.mUpdateRenewalDiscountPercentagePromise;
		}
	};

	public updateStage = (stage: string) => {
		if (!this.mUpdateStagePromise) {
			this.mUpdateStagePromise = new Promise<Api.IOperationResult<Api.IAccount>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IAccount>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mAccount = opResult.value;
							this.mUpdateStagePromise = null;
							resolve(opResult);
						} else {
							this.mUpdateStagePromise = null;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
					`admin/account/${this.id}/additionalInfo/stage?stage=${encodeURIComponent(stage)}`,
					'PUT',
					null,
					onFinish,
					onFinish
				);
			});
			return this.mUpdateStagePromise;
		}
	};

	public updateSendEmailFeatures = (sendEmailFeatures: Partial<Api.ISendEmailFeatures>) => {
		if (!this.mUpdateSendEmailFeaturesPromise) {
			this.mUpdateSendEmailFeaturesPromise = new Promise<Api.IOperationResult<Api.ISendEmailFeatures>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<Api.ISendEmailFeatures>) => {
						runInAction(() => {
							if (opResult.success) {
								this.account.features.sendEmail = opResult.value;
								this.mUpdateSendEmailFeaturesPromise = null;
								resolve(opResult);
							} else {
								this.mUpdateSendEmailFeaturesPromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISendEmailFeatures>(
						`impersonate/${this.id}/Account/features/sendEmail`,
						'PUT',
						sendEmailFeatures,
						onFinish,
						onFinish
					);
				}
			);
			return this.mUpdateSendEmailFeaturesPromise;
		}
	};

	public updateRenewalPdfSent = (sendDate: Date) => {
		if (!this.mUpdatePdfSentPromise) {
			this.mUpdatePdfSentPromise = new Promise<Api.IOperationResult<Api.IAccount>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IAccount>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mAccount = opResult.value;
							this.mUpdatePdfSentPromise = null;
							resolve(opResult);
						} else {
							this.mUpdatePdfSentPromise = null;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
					`admin/account/${this.id}/additionalInfo/renewalPdfSent${
						sendDate ? `?renewalPdfSent=${encodeURIComponent(moment(sendDate).format('MM/DD/YYYY'))}` : ''
					}`,
					'PUT',
					null,
					onFinish,
					onFinish
				);
			});
			return this.mUpdatePdfSentPromise;
		}
	};

	public updateNextMeeting = (nextMeeting: Date) => {
		if (!this.mUpdateNextMeetingPromise) {
			this.mUpdateNextMeetingPromise = new Promise<Api.IOperationResult<Api.IAccount>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IAccount>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mAccount = opResult.value;
							this.mUpdateNextMeetingPromise = null;
							resolve(opResult);
						} else {
							this.mUpdateNextMeetingPromise = null;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
					`admin/account/${this.account.id}/additionalInfo/nextMeeting?nextMeeting=${
						encodeURIComponent(moment(nextMeeting).format('MM/DD/YYYY')) || ''
					}`,
					'PUT',
					null,
					onFinish,
					onFinish
				);
			});
			return this.mUpdateNextMeetingPromise;
		}
	};

	public get isUpdatingSendEmailFeatures() {
		return !!this.mUpdateSendEmailFeaturesPromise;
	}

	public get isUpdatingStage() {
		return !!this.mUpdateStagePromise;
	}

	public get isUpdatingRenewalProbability() {
		return !!this.mUpdateRenewalPromise;
	}

	public get isUpdatingRenewalDiscountPercentage() {
		return !!this.mUpdateRenewalDiscountPercentagePromise;
	}

	public get isUpdatingRenewalPdfSent() {
		return !!this.mUpdatePdfSentPromise;
	}

	public get isUpdatingNextMeeting() {
		return !!this.mUpdateNextMeetingPromise;
	}
}

export enum AccountTagUsage {
	Policy = 'Policy',
}

export class DataRefreshViewModel extends AppImportContactsViewModel {
	private mAccount: Api.IAccount;
	public accountUsers: AccountUsersViewModel;
	public selectedUser: AccountUserViewModel;
	public clearRenewalKeyFacts = true;
	public contactVisibility: boolean;
	public tagsToDelete: Api.ObservableCollection<Api.TagViewModel>;
	public policyTags: Api.ObservableCollection<Api.TagViewModel>;
	public users: Api.ObservableCollection<Api.UserViewModel>;
	public userToUploadTo: Api.UserViewModel;
	public shareImports = false;

	constructor(userSession: Api.UserSessionContext, account: Api.IAccount) {
		super(userSession);

		makeObservable<DataRefreshViewModel, 'mAccount'>(this, {
			mAccount: observable.ref,
			accountUsers: observable.ref,
			selectedUser: observable.ref,
			clearRenewalKeyFacts: observable,
			contactVisibility: observable,
			tagsToDelete: observable.ref,
			policyTags: observable.ref,
			users: observable.ref,
			userToUploadTo: observable.ref,
			shareImports: observable,
			isReadyToImport: computed,
			account: computed,
			importFromFile: action,
			loadPolicyTags: action,
		});

		this.mAccount = account;
		this.accountUsers = new AccountUsersViewModel(userSession, account);
		this.tagsToDelete = new Api.ObservableCollection<Api.TagViewModel>([], 'id');
	}

	protected getUrl(jobId?: string) {
		let url = `admin/contactOperations/${this.mAccount.id}/${this.selectedUser.id}/importFromFile`;
		if (!jobId) {
			url = url + `?deleteRenewalKeyFacts=${encodeURIComponent(this.clearRenewalKeyFacts)}`;
		}
		return !jobId ? url : `${url}/${jobId}`;
	}

	public get isReadyToImport() {
		return !!this.selectedUser;
	}

	public get account() {
		return this.mAccount;
	}
	public importFromFile(file: File): Promise<Api.IOperationResult<Api.IImportContactPreview>> {
		if (this.isBusy) {
			return Promise.resolve(undefined);
		}

		this.busy = true;

		return new Promise<Api.IOperationResult<Api.IImportContactPreview>>((resolve, reject) => {
			const data = new FormData();
			data.append('file', file);

			const tags = Array.from(new Set(this.tagsToDelete.map(x => x.name)));
			data.append('refreshRequest', JSON.stringify({ accountTagNamesToDelete: tags }));

			const onFinish = action((opResult: Api.IOperationResult<Api.IImportContactPreview>) => {
				this.busy = false;
				if (opResult.success) {
					this.mFile = file;
					if (this.mSetPreview(opResult.value)) {
						resolve(opResult);
					} else {
						reject({
							success: false,
							systemCode: 500,
							systemMessage:
								'Please make sure the spreadsheet you uploaded has a header row. If it does, please make sure at least one header is something we can recognize, like "Name" or "Email".',
						});
					}
				} else {
					reject(opResult);
				}
			});

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IImportContactPreview>(
				this.getUrl(),
				'POST',
				data,
				onFinish,
				onFinish
			);
		});
	}

	public loadPolicyTags = async () => {
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IAccountTag[]>(
				`impersonate/${encodeURIComponent(this.mAccount.id)}/tag/byUsage?usage=${encodeURIComponent(
					AccountTagUsage.Policy
				)}`,
				'GET'
			);

			this.busy = false;
			if (opResult.success) {
				this.policyTags = new Api.ObservableCollection(
					opResult.value.map(x => new Api.TagViewModel(this.mUserSession, x)),
					'id'
				);
			} else {
				throw Api.asApiError(opResult);
			}
		}
	};
}

export class AccountImportsViewModel extends Api.ViewModel {
	private mAccount: Api.IAccount;
	protected mLoadImportsPromise: Promise<Api.IOperationResult<Api.IImportJob[]>>;
	protected mRevertImportsPromise: Promise<Api.IOperationResult<AdminModels.IRevertImportResult>>;
	private imports: Api.IImportJob[];
	private url: string;
	private mRevertFailedExpectedCount: number;

	constructor(userSession: Api.UserSessionContext, account: Api.IAccount) {
		super(userSession);

		makeObservable<AccountImportsViewModel, 'mAccount' | 'mLoadImportsPromise' | 'mRevertImportsPromise' | 'imports'>(
			this,
			{
				mAccount: observable.ref,
				mLoadImportsPromise: observable.ref,
				mRevertImportsPromise: observable.ref,
				imports: observable.ref,
				account: computed,
				loadImports: action,
				loadImportsByUser: action,
				revertImport: action,
				forceRevertImport: action,
				downloadImportFile: action,
				filterRemovedExport: action,
				accountImports: computed,
				totalNumberOfResults: computed,
				isLoading: override,
				isReverting: computed,
			}
		);

		this.mAccount = account;
		this.mLoadImportsPromise = null;
		this.url = 'admin/importOperations';
		this.imports = [];
	}

	public get account() {
		return this.mAccount;
	}

	public loadImports = (showReverted = false) => {
		if (!this.mLoadImportsPromise) {
			this.mLoadImportsPromise = new Promise<Api.IOperationResult<Api.IImportJob[]>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IImportJob[]>) => {
					if (opResult.success) {
						this.imports = opResult.value;
						this.mLoadImportsPromise = null;
						resolve(opResult);
					} else {
						this.mLoadImportsPromise = null;
						reject(opResult);
					}
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IImportJob[]>(
					`${this.url}/byAccount?accountId=${encodeURIComponent(this.mAccount.id)}&showReverted=${encodeURIComponent(
						showReverted
					)}`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
			return this.mLoadImportsPromise;
		}
	};

	public loadImportsByUser = (userId: string, showReverted = false) => {
		if (!this.mLoadImportsPromise) {
			this.mLoadImportsPromise = new Promise<Api.IOperationResult<Api.IImportJob[]>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IImportJob[]>) => {
					if (opResult.success) {
						this.imports = opResult.value;
						this.mLoadImportsPromise = null;
						resolve(opResult);
					} else {
						this.mLoadImportsPromise = null;
						reject(opResult);
					}
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IImportJob[]>(
					`${this.url}/byUser?accountId=${this.mAccount.id}&userId=${userId}&showReverted=${encodeURIComponent(
						showReverted
					)}`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
			return this.mLoadImportsPromise;
		}
	};

	public revertImport = (systemJobId: string) => {
		this.mRevertFailedExpectedCount = null;
		if (!this.mRevertImportsPromise) {
			this.mRevertImportsPromise = new Promise<Api.IOperationResult<AdminModels.IRevertImportResult>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IRevertImportResult>) => {
						if (opResult.success) {
							this.filterRemovedExport(systemJobId);
							this.mRevertImportsPromise = null;
							resolve(opResult);
						} else {
							try {
								if (opResult.systemMessage.includes('Total documents exceed expected count')) {
									// Get the number to the left of the >
									const regex = new RegExp(/: \d* > \d*/gm);
									const matches = regex.exec(opResult.systemMessage);
									const count = parseInt(matches[0].split(' ')[1], 10);
									this.mRevertFailedExpectedCount = count;
								}
								this.mRevertImportsPromise = null;
								reject(opResult);
							} catch {
								// console.error('Unable to parse the system message');
							}
						}
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IRevertImportResult>(
						`${this.url}/${systemJobId}/revert?accountId=${this.mAccount.id}`,
						'POST',
						null,
						onFinish,
						onFinish
					);
				}
			);
			return this.mRevertImportsPromise;
		}
	};

	public forceRevertImport = (systemJobId: string) => {
		if (!this.mRevertImportsPromise) {
			this.mRevertImportsPromise = new Promise<Api.IOperationResult<AdminModels.IRevertImportResult>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IRevertImportResult>) => {
						if (opResult.success) {
							this.filterRemovedExport(systemJobId);
							this.mRevertImportsPromise = null;
							resolve(opResult);
						} else {
							this.mRevertImportsPromise = null;
							reject(opResult);
						}
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IRevertImportResult>(
						`${this.url}/${systemJobId}/forceRevert?accountId=${this.mAccount.id}&expectedCount=${this.mRevertFailedExpectedCount}`,
						'POST',
						null,
						onFinish,
						onFinish
					);
				}
			);
			return this.mRevertImportsPromise;
		}
	};

	public downloadImportFile = (systemJobId: string) => {
		return this.userSession.webServiceHelper.callAsync<string>(
			`${this.url}/${systemJobId}/downloadUrl?accountId=${this.mAccount.id}`,
			'GET'
		);
	};

	public filterRemovedExport = (systemJobId: string) => {
		this.imports = this.imports.filter(importJob => importJob.id !== systemJobId);
	};

	public get accountImports() {
		return this.imports;
	}

	public get totalNumberOfResults() {
		return this.imports.length;
	}

	public get isLoading() {
		return !!this.mLoadImportsPromise;
	}

	public get isReverting() {
		return !!this.mRevertImportsPromise;
	}
}

/** For creating a review and approve campaign */
export class CreateCampaignApprovalViewModel extends Api.ViewModel {
	protected mContactsFilterRequest: Api.IBulkContactsRequest;
	protected mContactsPageCollectionController: Api.FilteredPageCollectionController<
		Api.IContact,
		Api.ContactViewModel,
		Api.IBulkContactsRequest
	>;
	protected mLoadingSuggestedTagsPromise: Promise<Api.IOperationResult<string[]>>;
	protected mNote: string;
	protected mSendDate: Date;
	protected mSuggestedTags: string[];
	protected mUsers: Api.ObservableCollection<AccountUserViewModel>;
	public account: Api.IAccount;
	public emailContent: IEditEmailTemplateContent;
	protected mSelectedOptionOverride: Api.IOwnershipFilter;
	public files: Api.AttachmentsToBeUploadedViewModel<File> = new Api.AttachmentsToBeUploadedViewModel<File>();

	protected mSelectedUsers: Api.ObservableCollection<AccountUserViewModel>;
	public readonly bulkEmailComposer: ComposeEmailViewModel;

	constructor(userSession: AdminUserSessionContext, account?: Api.IAccount) {
		super(userSession);

		makeObservable<
			CreateCampaignApprovalViewModel,
			| 'mContactsFilterRequest'
			| 'mContactsPageCollectionController'
			| 'mLoadingSuggestedTagsPromise'
			| 'mNote'
			| 'mSendDate'
			| 'mSuggestedTags'
			| 'mUsers'
			| 'mSelectedOptionOverride'
		>(this, {
			mContactsFilterRequest: observable.ref,
			mContactsPageCollectionController: observable.ref,
			mLoadingSuggestedTagsPromise: observable.ref,
			mNote: observable.ref,
			mSendDate: observable.ref,
			mSuggestedTags: observable.ref,
			mUsers: observable.ref,
			account: observable.ref,
			emailContent: observable.ref,
			mSelectedOptionOverride: observable,
			files: observable,
			hasAnyContacts: computed,
			defaultSelectedFilterOption: computed,
			setSelectedFilterOptionOverride: action,
			baseApiPath: computed,
			userSelectionMode: computed,
			allUsersSelected: computed,
			contactsFilterRequest: computed,
			totalNumberOfContacts: computed,
			contacts: computed,
			users: computed,
			selectedUsers: computed,
			isLoadingSuggestedTags: computed,
			isBusy: override,
			refreshContacts: action,
			reset: action,
			setInitialTag: action,
			loadSuggestedTags: action,
			getContacts: action,
			setContactFilterRequest: action,
			suggestedTags: computed,
			note: computed,
			setSendDate: action,
			sendForApproval: action,
		});

		this.account = account;
		this.bulkEmailComposer = new ComposeEmailViewModel(userSession);
		this.mContactsPageCollectionController = new Api.FilteredPageCollectionController({
			apiPath: 'contact/filter/v2',
			client: userSession.webServiceHelper,
			transformer: this.mCreateContactViewModel,
		});
		this.mSelectedUsers = new Api.ObservableCollection<AccountUserViewModel>([], 'id');
		this.mUsers = new Api.ObservableCollection<AccountUserViewModel>([], 'id');
		this.mSuggestedTags = [];
		this.mContactsFilterRequest = {
			filter: {
				criteria: [{ property: Api.ContactFilterCriteriaProperty.All }, ...Api.DefaultBulkSendExcludedFilterCriteria],
			},
		};
		this.bulkEmailComposer.showForBulkContactsFilterRequest({
			bulkFilterRequest: {
				contactFilterRequest: this.mContactsFilterRequest?.filter,
				ownershipFilter: this.mContactsFilterRequest?.ownershipFilter,
			},
		});
		this.mNote = `Hi ${FirstNamePlaceholder.symbol},\n\r\n\rPlease click on the "Review & Approve" button below to get started. You may edit the content once you are there.`;
		this.mSelectedOptionOverride = null;
		if (this.account?.id) {
			this.impersonate({ account: this.account });
		}
	}

	public get hasAnyContacts() {
		return this.bulkEmailComposer?.emailMessage?.contactEmailApproximation?.total > 0;
	}

	public get defaultSelectedFilterOption() {
		const x = this.mSelectedOptionOverride?.criteria
			? this.mSelectedOptionOverride.criteria.property
			: this.selectedUsers.length === 1
				? Api.ContactFilterCriteriaProperty.OwnedBy
				: Api.ContactFilterCriteriaProperty.All;
		return x;
	}

	public setSelectedFilterOptionOverride(option: Api.IOwnershipFilter) {
		this.mSelectedOptionOverride = option;
	}

	public get baseApiPath() {
		return this.composeApiUrl({ urlPath: 'contact/filter' });
	}

	public get userSelectionMode() {
		const type: AdminModels.BulkEmailApprovalUsersSelectMode = this.mSelectedUsers.length <= 1 ? 'one' : 'many';
		return type;
	}

	public get allUsersSelected() {
		return this.mUsers.length === this.mSelectedUsers.length;
	}

	public get contactsFilterRequest() {
		return this.mContactsFilterRequest;
	}

	public get totalNumberOfContacts() {
		return this.mContactsPageCollectionController.totalCount;
	}

	public get contacts() {
		return this.mContactsPageCollectionController;
	}

	public get users() {
		return this.mUsers;
	}

	public get selectedUsers() {
		return this.mSelectedUsers;
	}

	public get isLoadingSuggestedTags() {
		return !!this.mLoadingSuggestedTagsPromise;
	}

	public get isBusy() {
		return (
			this.busy || this.loading || this.isLoadingSuggestedTags || this.mContactsPageCollectionController?.isFetching
		);
	}

	public refreshContacts() {
		return new Promise<Api.IOperationResult<string[]>>((resolve, reject) => {
			const sorted = Api.VmUtils.sortContactFilterCriteria(this.contactsFilterRequest?.filter?.criteria);
			const filterRequest: Api.IBulkContactsRequest = {
				filter: {
					criteria: [...sorted.filters, ...sorted.searches],
				},
				ownershipFilter: this.contactsFilterRequest?.ownershipFilter,
			};

			this.getContacts(filterRequest)
				?.then(() => {
					resolve(undefined);
				})
				?.catch((error: Api.IOperationResultNoValue) => {
					reject(error);
				});
		});
	}

	public reset = () => {
		this.bulkEmailComposer.reset();
		this.mContactsPageCollectionController.reset();
		this.mSelectedUsers.clear();
		this.mSuggestedTags = [];
		this.mUsers.clear();
		this.mContactsFilterRequest = {
			filter: {
				criteria: [{ property: Api.ContactFilterCriteriaProperty.All }, ...Api.DefaultBulkSendExcludedFilterCriteria],
			},
		};
	};

	public setInitialTag = (tag: string) => {
		this.mContactsFilterRequest = {
			filter: {
				criteria: [
					{
						property: Api.ContactFilterCriteriaProperty.All,
					},
					{
						property: Api.ContactFilterCriteriaProperty.Tag,
						value: tag,
					},
					...Api.DefaultBulkSendExcludedFilterCriteria,
				],
			},
		};
		this.mContactsPageCollectionController.reset();
		this.mSelectedUsers.clear();
		this.mUsers.clear();
	};

	public loadSuggestedTags() {
		if (!this.isBusy) {
			this.mLoadingSuggestedTagsPromise = new Promise<Api.IOperationResult<string[]>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<string[]>) => {
					this.mLoadingSuggestedTagsPromise = null;
					if (opResult.success) {
						this.mSuggestedTags = opResult.value;
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<string[]>(
					this.composeApiUrl({ urlPath: 'email/suggestedTags' }),
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}
		return this.mLoadingSuggestedTagsPromise;
	}

	public getOwnersByTags = (filterRequest = this.mContactsFilterRequest) => {
		return new Promise<AccountUserViewModel[]>((resolve, reject) => {
			const onFinish = action((opResult: Api.IOperationResult<Api.IPagedCollection<Api.IUserWithStats>>) => {
				this.mLoadingSuggestedTagsPromise = null;
				if (opResult.success) {
					this.mUsers.setItems(
						opResult.value.values.map(x => new AccountUserViewModel(this.userSession as AdminUserSessionContext, x))
					);
					resolve(this.mUsers.toArray());
				} else {
					reject(opResult);
				}
			});
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IPagedCollection<Api.IUserWithStats>>(
				this.composeApiUrl({ urlPath: 'user/ownersByFilter' }),
				'POST',
				filterRequest.filter,
				onFinish,
				onFinish
			);
		});
	};

	public getContacts = (filterRequest = this.mContactsFilterRequest) => {
		const apiParams: Api.IDictionary<string> = {};
		if (this.selectedUsers?.length === 1) {
			apiParams.impUserId = this.selectedUsers.getByIndex(0).id;
		}
		const promise = this.mContactsPageCollectionController?.getNext(filterRequest, 25, apiParams);
		if (promise) {
			this.mContactsFilterRequest = filterRequest;
		}
		return promise;
	};

	public setContactFilterRequest = (filterRequest: Api.IBulkContactsRequest) => {
		this.mContactsFilterRequest = filterRequest;
		return Promise.all([this.getOwnersByTags(), this.getContacts()]);
	};

	public get suggestedTags() {
		return this.mSuggestedTags;
	}

	public get note() {
		return this.mNote;
	}

	public setNote(note: string) {
		this.mNote = note;
	}

	public get sendDate() {
		return this.mSendDate;
	}

	public setSendDate(sendDate: Date) {
		this.mSendDate = sendDate;
	}

	public impersonate(impersonationContext?: Api.IImpersonationContext) {
		super.impersonate(impersonationContext);
		this.mContactsPageCollectionController?.impersonate(impersonationContext);
		this.bulkEmailComposer?.impersonate(this.mImpersonationContext);
		return this;
	}

	public sendForApproval = () => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.CampaignViewModel[]>((resolve, reject) => {
				const composedFilter: Api.IBulkContactsRequest = {
					...this.mContactsFilterRequest,
					filter: {
						criteria: [
							...this.mContactsFilterRequest.filter.criteria,
							{
								op: Api.FilterOperator.Not,
								property: Api.ContactFilterCriteriaProperty.WithoutEmailAddresses,
							},
							...Array.from(Api.DefaultBulkSendExcludedTags).map(
								x =>
									({
										op: Api.FilterOperator.Not,
										property: Api.ContactFilterCriteriaProperty.Tag,
										value: x,
									}) as Api.IContactFilterCriteria
							),
						],
					},
				};
				const approvalRequest: AdminModels.ICampaignEmailRequest = {
					approvalContent: Api.createRawRichTextContentStateWithText(this.mNote),
					contactsFilter: composedFilter.filter,
					ownershipFilter: composedFilter.ownershipFilter,
					content: this.emailContent.editorState.getRawRichTextContent(),
					startDate: this.sendDate.toISOString(),
					subject: this.emailContent.subject,
					templateReference: this.emailContent?.template
						? {
								isCustomized: false,
								isSystemTemplate: true,
								name: this.emailContent.template?.name,
								templateId: this.emailContent.template?.id,
							}
						: null,
					userIds: this.selectedUsers.map(x => x.id),
				};
				const formData = new FormData();
				formData.append('value', JSON.stringify(approvalRequest));
				if (this.files?.count > 0) {
					this.files.attachments.forEach(x => formData.append('files', x));
				}

				const impersonationContext: Api.IImpersonationContext = {
					account: this.account,
				};
				const onFinish = action((opResult: Api.IOperationResult<Api.ICampaign[]>) => {
					if (opResult.success) {
						const campaigns = opResult.value.map(x => {
							return new Api.CampaignViewModel(this.userSession, x).impersonate(impersonationContext);
						});
						resolve(campaigns);
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ICampaign[]>(
					this.composeApiUrl({ urlPath: 'email/campaign' }),
					'POST',
					formData,
					onFinish,
					onFinish
				);
			});
		}
	};

	private mCreateContactViewModel = (contact: Api.IContact) => {
		return new Api.ContactViewModel(this.mUserSession, contact).impersonate(this.mImpersonationContext);
	};
}

export class ContentCalendarCreateCampaignApprovalViewModel extends CreateCampaignApprovalViewModel {
	private mAdmins: Api.UserViewModel[];

	constructor(userSession: AdminUserSessionContext, account?: Api.IAccount) {
		super(userSession, account);

		makeObservable<ContentCalendarCreateCampaignApprovalViewModel, 'mAdmins'>(this, {
			mAdmins: observable.ref,
			admins: computed,
			defaultAdmin: computed,
			loadAdmins: action,
			createApproval: action,
		});

		this.mAdmins = [];
	}

	public get admins() {
		return this.mAdmins || [];
	}

	public get defaultAdmin() {
		return this.admins[0];
	}

	public loadAdmins = async () => {
		try {
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IUser[]>(
				`impersonate/${this.account?.id}/user/admins`,
				'GET'
			);
			if (!success) {
				throw opResult;
			}
			this.mAdmins = opResult.value.map(x => new AccountUserViewModel(this.mUserSession, x));
			return this.mAdmins;
		} catch (error) {
			throw Api.asApiError(error);
		}
	};

	public createApproval = (sendFromUser?: Api.IUser) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.CampaignViewModel>((resolve, reject) => {
				// FOLLOWUP: Resolve

				// @ts-ignore
				const composedFilter: Api.IContactFilterCriteria = this.bulkEmailComposer.emailMessage
					.hasUserSelectedContactFilterSearchCriteria
					? {
							criteria: [
								...(
									this.bulkEmailComposer.emailMessage.contactsFilterRequest?.contactFilterRequest?.criteria || []
								).filter(x => {
									return (
										!(
											x.op === Api.FilterOperator.Not &&
											x.property === Api.ContactFilterCriteriaProperty.WithoutEmailAddresses
										) &&
										!(
											x.op === Api.FilterOperator.Not &&
											x.property === Api.ContactFilterCriteriaProperty.Tag &&
											Api.DefaultBulkSendExcludedTags.has(x.value)
										)
									);
								}),
								{
									op: Api.FilterOperator.Not,
									property: Api.ContactFilterCriteriaProperty.WithoutEmailAddresses,
								},
								...Array.from(Api.DefaultBulkSendExcludedTags).map(x => ({
									op: Api.FilterOperator.Not,
									property: Api.ContactFilterCriteriaProperty.Tag,
									value: x,
								})),
							],
						}
					: { criteria: null };

				const emailMessageModel = this.bulkEmailComposer.emailMessage.toJs();
				emailMessageModel.options = {
					...(emailMessageModel.options || {}),
					sendWithCompliance: this.account.preferences.complianceSettings?.enabled,
				};
				if (sendFromUser) {
					emailMessageModel.options.sendEmailFrom = Api.SendEmailFrom.SelectedUser;
					emailMessageModel.options.sendEmailFromUser =
						sendFromUser instanceof Api.UserViewModel
							? (sendFromUser as Api.UserViewModel).toJs() // .toJs required to remove circular structure (ie. object refering to itself)...without calling this, will get `Converting circular structure to JSON` error
							: sendFromUser;
					emailMessageModel.options.sendEmailFromUserId = sendFromUser.id;
				} else {
					emailMessageModel.options.sendEmailFrom = Api.SendEmailFrom.ContactOwner;
				}
				emailMessageModel.contactsFilterRequest = {
					contactFilterRequest: composedFilter,
					ownershipFilter: this.bulkEmailComposer.emailMessage.contactsFilterRequest?.ownershipFilter,
				};

				const formData = new FormData();
				formData.append('value', JSON.stringify(emailMessageModel));

				if (this.bulkEmailComposer.emailMessage.attachments?.count) {
					this.bulkEmailComposer.emailMessage.attachments?.attachments?.forEach(x => formData.append('files', x));
				}

				const impUserId = sendFromUser ? sendFromUser.id : undefined;
				const onFinish = action(async (opResult: Api.IOperationResult<Api.ISendEmailResponse>) => {
					if (opResult.success) {
						try {
							const campaign = new Api.CampaignViewModel(this.mUserSession, {
								id: opResult.value.id,
							}).impersonate(
								impUserId && this.mImpersonationContext?.account?.id
									? {
											account: this.mImpersonationContext?.account,
											user: { id: impUserId },
										}
									: this.mImpersonationContext
							);
							await campaign.load();
							this.busy = false;
							resolve(campaign);
						} catch (error) {
							this.busy = false;
							reject(error);
						}
					} else {
						this.busy = false;
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISendEmailResponse>(
					this.composeApiUrl({ queryParams: { impUserId }, urlPath: 'email' }),
					'POST',
					formData,
					onFinish,
					onFinish
				);
			});
		}
	};
}

export class AccountCampaignViewModel extends Api.CampaignViewModel {
	private mResendingApprovalRequest: boolean;

	constructor(userSession: UserSessionContext, model: Api.ICampaign) {
		super(userSession, model);

		makeObservable<AccountCampaignViewModel, 'mResendingApprovalRequest'>(this, {
			mResendingApprovalRequest: observable,
			isBusy: override,
		});
	}

	public get isBusy() {
		return this.busy || this.loading || this.mLoadingDefaultMessageContent || this.mResendingApprovalRequest;
	}
}
export class SocialAccountCampaignViewModel extends Api.SocialMediaPostViewModel {
	private mResendingApprovalRequest: boolean;

	constructor(userSession: UserSessionContext, post: Partial<Api.ISocialMediaPost>) {
		super(userSession, post);

		makeObservable<SocialAccountCampaignViewModel, 'mResendingApprovalRequest'>(this, {
			mResendingApprovalRequest: observable,
			isBusy: override,
			isResendingApprovalRequest: computed,
		});
	}

	public get isBusy() {
		return this.busy || this.loading || this.mResendingApprovalRequest;
	}

	public get isResendingApprovalRequest() {
		return this.mResendingApprovalRequest;
	}

	public sendApprovalRequest = () => {
		this.mResendingApprovalRequest = true;
		return new Promise<boolean>((resolve, reject) => {
			const onFinish = action((opResult: Api.IOperationResult<Api.ISocialMediaPost[]>) => {
				this.mResendingApprovalRequest = false;
				if (opResult.success) {
					resolve(true);
				} else {
					reject(opResult);
				}
			});
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISocialMediaPost[]>(
				this.composeApiUrl({ urlPath: `social/post/${this.id}/approvalrequest` }),
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public sendComplianceApprovalRequest = (sendWithComplianceEmail?: string) => {
		this.mResendingApprovalRequest = true;
		return new Promise<boolean>((resolve, reject) => {
			const onFinish = action((opResult: Api.IOperationResult<Api.ISocialMediaPost[]>) => {
				this.mResendingApprovalRequest = false;
				if (opResult.success) {
					resolve(true);
				} else {
					reject(opResult);
				}
			});
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISocialMediaPost[]>(
				this.composeApiUrl({
					queryParams: { sendWithComplianceEmail },
					urlPath: `social/post/${this.id}/sendCompliance`,
				}),
				'POST',
				null,
				onFinish,
				onFinish
			);
		});
	};
}

export class AdminReportingViewModel extends Api.ViewModel {
	protected mActiveUseSummary: AdminModels.IActiveUseSummary;
	protected mActiveUseSummaryPromise: Promise<Api.IOperationResult<AdminModels.IActiveUseSummary>>;
	protected mAccountChangeSummary: AdminModels.IActiveUseDailySummaryByAccount[];
	protected mAccountChangeSummaryPromise: Promise<Api.IOperationResult<AdminModels.IActiveUseDailySummaryByAccount[]>>;
	protected mRenewalByMonth: AdminModels.IMonthlyRecurringRevenueSummary;
	protected mRenewalByMonthPromise: Promise<Api.IOperationResult<AdminModels.IMonthlyRecurringRevenueSummary>>;

	protected mCurrentMonthPercent: string;
	protected mNextMonthPercent: string;

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable<
			AdminReportingViewModel,
			| 'mActiveUseSummary'
			| 'mActiveUseSummaryPromise'
			| 'mAccountChangeSummary'
			| 'mAccountChangeSummaryPromise'
			| 'mRenewalByMonth'
			| 'mRenewalByMonthPromise'
			| 'mCurrentMonthPercent'
			| 'mNextMonthPercent'
		>(this, {
			mActiveUseSummary: observable.ref,
			mActiveUseSummaryPromise: observable.ref,
			mAccountChangeSummary: observable.ref,
			mAccountChangeSummaryPromise: observable.ref,
			mRenewalByMonth: observable.ref,
			mRenewalByMonthPromise: observable.ref,
			mCurrentMonthPercent: observable.ref,
			mNextMonthPercent: observable.ref,
			loadAll: action,
			loadActiveUseSummary: action,
			currentMonthPercent: computed,
			nextMonthPercent: computed,
			activeUseSummary: computed,
			isLoadingActiveUseSummary: computed,
			loadAccountsChanged: action,
			accountsChanged: computed,
			isLoadingAccountsChanged: computed,
			loadRenewalByMonth: action,
			renewalByMonth: computed,
			isLoadingRenewalByMonth: computed,
			validDailyActiveUse: computed,
			validGreenActiveUse: computed,
			validGrayActiveUse: computed,
			validYellowActiveUse: computed,
			validOrangeActiveUse: computed,
			validNewRedActiveUse: computed,
			validInsuranceActiveUse: computed,
			validRealEstateActiveUse: computed,
			validFinancialActiveUse: computed,
			validMortgageActiveUse: computed,
			validLegalActiveUse: computed,
			validRenewalByMonth: computed,
			validRenewalByMonthInsurance: computed,
			validRenewalDollarsByMonthInsurance: computed,
			validRenewalByMonthRealEstate: computed,
			validRenewalByMonthFinancial: computed,
			validRenewalByMonthMortgage: computed,
			validRenewalByMonthLegal: computed,
			validRenewalByMonthNonProfit: computed,
			validRenewalByMonthAccounting: computed,
			noSaleTotal: computed,
			noSaleInsurance: computed,
			noSaleRealEstate: computed,
			noSaleFinancial: computed,
			noSaleMortgage: computed,
			noSaleLegal: computed,
			noSaleNonProfit: computed,
			churn: computed,
			totalMRR: computed,
		});
	}

	public loadAll = async () => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<
				[
					Api.IPageCollectionControllerFetchResult<Api.IAccount[]>,
					Api.IOperationResult<AdminModels.IAggregateActiveUse>,
				]
			>((resolve, reject) => {
				const criteria: IAccountFilterCriteria = {
					criteria: [
						{
							property: AccountFilterCriteriaProperty.RenewalMonth,
							value: moment().add(1, 'month').format('YYYY-MM'),
						},
					],
					op: Api.FilterOperator.And,
				};
				const nextMonthPromise = this.loadRenewalByMonth(criteria);
				const dailyUsePromise = this.loadActiveUseSummary();

				Promise.all([nextMonthPromise, dailyUsePromise])
					.then(fetchResults => {
						runInAction(() => {
							if (fetchResults[0]) {
								this.mNextMonthPercent = fetchResults[0]?.value?.totalRenewalPercent
									? fetchResults[0].value.totalRenewalPercent.toString() + '%'
									: '??%';
							}
							if (fetchResults[1]) {
								this.mActiveUseSummary = fetchResults[1].value;
							}
							this.busy = false;
							resolve(undefined);
						});
					})
					.catch(e => {
						this.busy = false;
						reject(Api.asApiError(e));
					});
			});
			return promise;
		}
	};

	public loadActiveUseSummary = () => {
		if (!this.mActiveUseSummaryPromise) {
			this.mActiveUseSummaryPromise = new Promise<Api.IOperationResult<AdminModels.IActiveUseSummary>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IActiveUseSummary>) => {
						runInAction(() => {
							if (opResult.success) {
								this.mActiveUseSummary = opResult.value;
								this.mActiveUseSummaryPromise = null;
								resolve(opResult);
							} else {
								this.mActiveUseSummaryPromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IActiveUseSummary>(
						'admin/account/stats/summary',
						'POST',
						{},
						onFinish,
						onFinish
					);
				}
			);
		}
		return this.mActiveUseSummaryPromise;
	};

	public get currentMonthPercent() {
		return this.mCurrentMonthPercent;
	}

	public get nextMonthPercent() {
		return this.mNextMonthPercent;
	}

	public get activeUseSummary() {
		return this.mActiveUseSummary;
	}

	public get isLoadingActiveUseSummary() {
		return !!this.mActiveUseSummaryPromise;
	}

	public loadAccountsChanged = () => {
		if (!this.mAccountChangeSummaryPromise) {
			this.mAccountChangeSummaryPromise = new Promise<
				Api.IOperationResult<AdminModels.IActiveUseDailySummaryByAccount[]>
			>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<AdminModels.IActiveUseDailySummaryByAccount[]>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mAccountChangeSummary = opResult.value;
							this.mAccountChangeSummaryPromise = null;
							resolve(opResult);
						} else {
							this.mAccountChangeSummaryPromise = null;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<
					AdminModels.IActiveUseDailySummaryByAccount[]
				>('admin/account/activeUseStatusChanged', 'GET', null, onFinish, onFinish);
			});
		}
		return this.mAccountChangeSummaryPromise;
	};

	public get accountsChanged() {
		return this.mAccountChangeSummary;
	}

	public get isLoadingAccountsChanged() {
		return !!this.mAccountChangeSummaryPromise;
	}

	public loadRenewalByMonth = (filter: IAccountFilterCriteria) => {
		if (!this.mRenewalByMonthPromise) {
			this.mRenewalByMonthPromise = new Promise<Api.IOperationResult<AdminModels.IMonthlyRecurringRevenueSummary>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IMonthlyRecurringRevenueSummary>) => {
						runInAction(() => {
							if (opResult.success) {
								this.mRenewalByMonth = opResult.value;
								this.mRenewalByMonthPromise = null;
								resolve(opResult);
							} else {
								this.mRenewalByMonthPromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IMonthlyRecurringRevenueSummary>(
						`admin/account/renewalProbabilityByMonth`,
						'POST',
						filter,
						onFinish,
						onFinish
					);
				}
			);
		}
		return this.mRenewalByMonthPromise;
	};

	public get renewalByMonth() {
		return this.mRenewalByMonth;
	}

	public get isLoadingRenewalByMonth() {
		return !!this.mRenewalByMonthPromise;
	}

	private asPercentage(val: number) {
		return val >= 0 ? val.toLocaleString() + '%' : '0%';
	}

	public get validDailyActiveUse() {
		return this.asPercentage(this.mActiveUseSummary?.overall?.activeAccountsPercentage);
	}

	public get validGreenActiveUse() {
		return this.asPercentage(this.mActiveUseSummary?.overall?.greenAccountsPercentage);
	}

	public get validGrayActiveUse() {
		const overall = this.mActiveUseSummary?.overall;
		if (overall?.grayAccounts >= 0 && overall?.totalAccounts > 0) {
			return parseInt(((overall?.grayAccounts / overall?.totalAccounts) * 100).toLocaleString(), 10) + '%' || '??%';
		}
		return '??%';
	}

	public get validYellowActiveUse() {
		return this.asPercentage(this.mActiveUseSummary?.overall?.yellowAccountsPercentage);
	}

	public get validOrangeActiveUse() {
		return this.asPercentage(this.mActiveUseSummary?.overall?.orangeAccountsPercentage);
	}

	public get validNewRedActiveUse() {
		return this.asPercentage(this.mActiveUseSummary?.overall?.changedToRedAccountsPercentage);
	}

	public get validInsuranceActiveUse() {
		const insurance = this.mActiveUseSummary?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'insurance'
		);
		return this.asPercentage(insurance?.activeAccountsPercentage);
	}

	public get validRealEstateActiveUse() {
		const realEstate = this.mActiveUseSummary?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'real estate'
		);
		return this.asPercentage(realEstate?.activeAccountsPercentage);
	}

	public get validFinancialActiveUse() {
		const financial = this.mActiveUseSummary?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'financial'
		);
		return this.asPercentage(financial?.activeAccountsPercentage);
	}

	public get validMortgageActiveUse() {
		const mortgage = this.mActiveUseSummary?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'mortgage'
		);
		return this.asPercentage(mortgage?.activeAccountsPercentage);
	}

	public get validLegalActiveUse() {
		const legal = this.mActiveUseSummary?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'legal'
		);
		return this.asPercentage(legal?.activeAccountsPercentage);
	}

	/** Renewal data */
	public get validRenewalByMonth() {
		return this.asPercentage(this.renewalByMonth?.totalRenewalPercent);
	}

	public get validRenewalByMonthInsurance() {
		const insurance = this.mRenewalByMonth?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'insurance'
		);
		return this.asPercentage(insurance?.percentage);
	}

	public get validRenewalDollarsByMonthInsurance() {
		const insurance = this.renewalByMonth?.noSaleByIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'insurance'
		);
		return this.asPercentage(insurance?.amount);
	}

	public get validRenewalByMonthRealEstate() {
		const realEstate = this.mRenewalByMonth?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'real estate'
		);
		return this.asPercentage(realEstate?.percentage);
	}

	public get validRenewalByMonthFinancial() {
		const financial = this.mRenewalByMonth?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'financial'
		);
		return this.asPercentage(financial?.percentage);
	}

	public get validRenewalByMonthMortgage() {
		const mortgage = this.mRenewalByMonth?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'mortgage'
		);
		return this.asPercentage(mortgage?.percentage);
	}

	public get validRenewalByMonthLegal() {
		const legal = this.mRenewalByMonth?.byIndustry?.find(industry => industry.industry.toLocaleLowerCase() === 'legal');
		return this.asPercentage(legal?.percentage);
	}

	public get validRenewalByMonthNonProfit() {
		const nonProfit = this.mRenewalByMonth?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'non-profit'
		);
		return this.asPercentage(nonProfit?.percentage);
	}

	public get validRenewalByMonthAccounting() {
		const accounting = this.mRenewalByMonth?.byIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'accounting'
		);
		return this.asPercentage(accounting?.percentage);
	}

	/** No sale data */
	public get noSaleTotal() {
		return this.asDollars(this.renewalByMonth?.noSaleTotal);
	}

	public get noSaleInsurance() {
		const insurance = this.mRenewalByMonth?.noSaleByIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'insurance'
		);
		return this.asDollars(insurance?.amount);
	}

	public get noSaleRealEstate() {
		const realEstate = this.mRenewalByMonth?.noSaleByIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'real estate'
		);
		return this.asDollars(realEstate?.amount);
	}

	public get noSaleFinancial() {
		const financial = this.mRenewalByMonth?.noSaleByIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'financial'
		);
		return this.asDollars(financial?.amount);
	}

	public get noSaleMortgage() {
		const mortgage = this.mRenewalByMonth?.noSaleByIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'mortgage'
		);
		return this.asDollars(mortgage?.amount);
	}

	public get noSaleLegal() {
		const legal = this.mRenewalByMonth?.noSaleByIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'legal'
		);
		return this.asDollars(legal?.amount);
	}

	public get noSaleNonProfit() {
		const nonProfit = this.mRenewalByMonth?.noSaleByIndustry?.find(
			industry => industry.industry.toLocaleLowerCase() === 'non-profit'
		);
		return this.asDollars(nonProfit?.amount);
	}

	private asDollars(val: number) {
		return val > 0 ? '$' + val.toLocaleString() : '$0';
	}

	public get churn() {
		return this.asDollars(this.mRenewalByMonth?.projectedChurn);
	}

	public get totalMRR() {
		return this.asDollars(this.mRenewalByMonth?.totalMRR);
	}
}

export class TemplateCategoriesViewModel extends Api.ViewModel {
	public readonly industry: Api.Industry;
	public readonly campaignType: AdminModels.CampaignType;
	protected readonly mCategoriesApiPathComponent: string;
	private mCategories: Api.ObservableCollection<TemplateCategoryViewModel>;
	constructor(userSession: AdminUserSessionContext, industry: Api.Industry, campaignType: AdminModels.CampaignType) {
		super(userSession);

		makeObservable<TemplateCategoriesViewModel, 'mCategories'>(this, {
			mCategories: observable.ref,
			categories: computed,
			load: override,
			delete: action,
			create: action,
			update: action,
			reorderCategories: action,
		});

		this.industry = industry;
		this.campaignType = campaignType;
		this.mCategoriesApiPathComponent = 'categories';
		if (campaignType === AdminModels.CampaignType.Social) {
			this.mCategoriesApiPathComponent = 'socialCategories';
		}
		if (campaignType === AdminModels.CampaignType.Blog) {
			this.mCategoriesApiPathComponent = 'blogCategories';
		}
	}

	public get categories() {
		return this.mCategories;
	}

	public async load() {
		if (!this.isBusy) {
			this.busy = true;
			const opResult: Api.IOperationResult<Api.ITemplateCategory[]> =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITemplateCategory[]>(
					`admin/template/${encodeURIComponent(this.industry)}/${this.mCategoriesApiPathComponent}`,
					'GET'
				);

			this.busy = false;
			if (opResult.success) {
				this.mCategories = this.transformCategories(opResult.value);
				this.loaded = true;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	/**
	 * @NOTE This general call to delete category should hit both Email and Social
	 * @param categoryName {string} - the name of the category to delete
	 * @param campaignType {AdminModels.CampaignType} - the type of campaign
	 */
	public async delete(categoryName: string, campaignType: AdminModels.CampaignType) {
		if (!this.isBusy) {
			let categoriesType = '';
			switch (campaignType) {
				case AdminModels.CampaignType.Social:
				case AdminModels.CampaignType.Email:
					categoriesType = 'categories';
					break;
				case AdminModels.CampaignType.Blog:
					categoriesType = 'blogCategories';
					break;
				default:
					break;
			}
			this.busy = true;
			const opResult: Api.IOperationResult<Api.ITemplateCategory[]> =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITemplateCategory[]>(
					`admin/template/${encodeURIComponent(this.industry)}/${categoriesType}/${encodeURIComponent(categoryName)}`,
					'DELETE'
				);

			this.busy = false;
			if (opResult.success) {
				this.mCategories = new Api.ObservableCollection(this.mCategories.filter(x => x.name !== categoryName));
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	// This create call should hit both email and social
	public async create(name: string, type: Api.TemplateCategoryType = Api.TemplateCategoryType.Default) {
		if (!this.isBusy) {
			this.busy = true;
			const newCategory: Api.ITemplateCategory = {
				name,
				templates: [],
				total: 0,
				type,
			};

			const encodedCategoryName = encodeURIComponent(this.mCategoriesApiPathComponent);
			const opResult: Api.IOperationResult<Api.ITemplateCategory> =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITemplateCategory>(
					`admin/template/${encodeURIComponent(this.industry)}/${encodedCategoryName}`,
					'POST',
					newCategory
				);

			this.busy = false;
			if (opResult.success) {
				// splice into the second place in the list (after featured)
				this.mCategories.splice(
					1,
					0,
					new TemplateCategoryViewModel(this.mUserSession as AdminUserSessionContext, opResult.value, this.industry)
				);
				this.loaded = true;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async update(originalName: string, newName: string, type: Api.TemplateCategoryType) {
		if (!this.isBusy) {
			this.busy = true;
			const updatedCategory: Api.ITemplateCategory = {
				name: newName,
				templates: [],
				total: 0,
				type,
			};

			const opResult: Api.IOperationResult<Api.ITemplateCategory> =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITemplateCategory>(
					`admin/template/${encodeURIComponent(this.industry)}/${encodeURIComponent(this.mCategoriesApiPathComponent)}/${encodeURIComponent(originalName)}`,
					'PUT',
					updatedCategory
				);

			this.busy = false;
			if (opResult.success) {
				await this.load();
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	// Need a separate call for reordering categories for the social display
	public async reorderCategories(category: TemplateCategoryViewModel, toIndex: number) {
		if ((!category && !this.mCategories.has(category)) || this.isBusy) {
			return;
		}
		const fromIndex = this.mCategories.indexOf(category);
		if (fromIndex === toIndex) {
			return;
		}

		this.mCategories.splice(fromIndex, 1);
		this.mCategories.splice(toIndex, 0, category);
		const names = this.mCategories.map(x => x.name);
		const encodedCategoryName = encodeURIComponent(this.mCategoriesApiPathComponent);
		const opResult: Api.IOperationResult<Api.ITemplateCategory[]> =
			await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITemplateCategory[]>(
				`admin/template/${this.industry}/${encodedCategoryName}/order`,
				'PUT',
				names
			);

		if (opResult.success) {
			this.mCategories = this.transformCategories(opResult.value);
		} else {
			// undo
			this.mCategories.splice(toIndex, 1);
			this.mCategories.splice(fromIndex, 0, category);
		}
	}

	private transformCategories(
		categories: Api.ITemplateCategory[]
	): Api.ObservableCollection<TemplateCategoryViewModel> {
		return new Api.ObservableCollection(
			categories.map(x => new TemplateCategoryViewModel(this.userSession as AdminUserSessionContext, x, this.industry)),
			'name'
		);
	}
}

export class TemplateCategoryViewModel extends Api.ViewModel {
	private mTemplateCategory: Api.ITemplateCategory;
	private mTemplates: Api.ObservableCollection<TemplateWithStatusesViewModel>;
	private isExpandedUI: boolean;
	public readonly industry: Api.Industry;

	constructor(userSession: AdminUserSessionContext, category: Api.ITemplateCategory, industry: Api.Industry) {
		super(userSession);

		makeObservable<TemplateCategoryViewModel, 'mTemplateCategory' | 'mTemplates' | 'isExpandedUI'>(this, {
			mTemplateCategory: observable.ref,
			mTemplates: observable.ref,
			isExpandedUI: observable,
			setExpanded: action,
			numberOfUnpublishedTemplates: computed,
			isExpanded: computed,
			total: computed,
			setTotal: action,
			name: computed,
			type: computed,
			systemTemplates: computed,
			load: override,
			reorder: action,
			unfeature: action,
			isBusy: override,
			feature: action,
			deleteTemplate: action,
			addStatuses: action,
			removeStatuses: action,
			bulkArchive: action,
			bulkArchiveAll: action,
		});

		this.industry = industry;
		this.mTemplateCategory = category;
		this.mTemplates = null;
		this.isExpandedUI = false;
	}

	public setExpanded(expanded: boolean) {
		this.isExpandedUI = expanded;
	}

	public get numberOfUnpublishedTemplates() {
		return this.mTemplateCategory.templates.filter(t => t.status === Api.TemplateStatus.Draft)?.length;
	}

	public isUnpublishedTemplate(templateId: string) {
		if (this.mTemplates) {
			const sysTemplate = this.mTemplates.find(x => x.templateReference.template.id === templateId);
			return sysTemplate.templateReference.industries[this.industry] === Api.TemplateStatus.Draft;
		}
		return this.mTemplateCategory.templates.find(t => t.templateId === templateId)?.status === Api.TemplateStatus.Draft;
	}

	public get isExpanded() {
		return this.isExpandedUI;
	}

	public get total() {
		return this.isLoaded ? this.mTemplates?.length : this.mTemplateCategory.total;
	}

	public setTotal(count: number) {
		this.mTemplateCategory.total = count;
	}

	public get name() {
		return this.mTemplateCategory.name;
	}

	public get type() {
		return this.mTemplateCategory.type;
	}

	public get systemTemplates() {
		return this.mTemplates;
	}

	public async load(campaignType?: AdminModels.CampaignType) {
		const categoriesType = getCategoryPathByCampaignType(campaignType);

		if (!this.isBusy) {
			this.busy = true;
			try {
				const templates = await this.mUserSession.webServiceHelper.callAsync<AdminModels.ISystemEmailTemplate[]>(
					`admin/template/${encodeURIComponent(this.industry)}/${categoriesType}/${encodeURIComponent(
						this.name
					)}/templates`,
					'GET'
				);
				this.mTemplates = this.transformTemplates(templates);
			} finally {
				this.busy = false;
				this.loaded = true;
			}
		}
	}

	public async reorder(
		templates: TemplateWithStatusesViewModel[],
		toIndex: number,
		campaignType: AdminModels.CampaignType
	) {
		let templateType = '';
		switch (campaignType) {
			case AdminModels.CampaignType.Social:
				templateType = 'socialTemplates';
				break;
			case AdminModels.CampaignType.Blog:
				templateType = 'blogTemplates';
				break;
			case AdminModels.CampaignType.Email:
				templateType = 'templates';
				break;
			default:
				templateType = 'templates';
		}

		const templateToInsertBefore = this.mTemplates.getByIndex(toIndex);

		templates.forEach(template => {
			const fromIndex = this.mTemplates.indexOf(template);
			this.mTemplates.splice(fromIndex, 1);
		});

		// The previous toIndex was referencing prior to removing the above templates, find where it is again
		const newToIndex = this.mTemplates.indexOf(templateToInsertBefore);

		if (newToIndex === -1) {
			throw Api.asApiError({ systemMessage: 'Do not insert at a template being moved' });
			return;
		}

		this.mTemplates.splice(newToIndex, 0, ...templates);

		const ids = this.mTemplates.map(x => {
			return x.templateReference.template.id;
		});

		const encodedCategoryName = encodeURIComponent(this.mTemplateCategory.name);
		const opResult: Api.IOperationResultNoValue =
			await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IOperationResultNoValue>(
				`admin/template/${this.industry}/${encodedCategoryName}/${templateType}/order`,
				'PUT',
				ids
			);

		if (!opResult.success) {
			throw Api.asApiError({
				systemMessage: 'Unable to rearrange templates (make sure to refresh before trying again)',
			});
		}
	}

	public async unfeature(templateId: string, campaignType: AdminModels.CampaignType) {
		const categoriesType = getCategoryPathByCampaignType(campaignType);
		if (!this.isBusy) {
			const opResult =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
					`admin/template/industry/${categoriesType}/${encodeURIComponent(templateId)}/statuses`,
					'DELETE',
					[{ category: this.name, industry: this.industry }]
				);

			if (opResult.success) {
				this.mTemplateCategory.total -= 1;
				if (this.mTemplateCategory.total === 0) {
					this.isExpandedUI = false;
				}
				this.mTemplates = new Api.ObservableCollection(
					this.mTemplates.filter(x => x.templateReference.template.id !== templateId)
				);
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	public async feature(templateId: string, campaignType: AdminModels.CampaignType) {
		const categoriesType = getCategoryPathByCampaignType(campaignType);
		if (!this.isBusy) {
			const opResult =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
					`admin/template/industry/${categoriesType}/${encodeURIComponent(templateId)}/statuses`,
					'POST',
					[
						{
							category: 'Featured',
							industry: this.industry,
							status: 'Published',
						},
					]
				);

			if (opResult.success) {
				return opResult.value;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async deleteTemplate(templateId: string) {
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IOperationResultNoValue>(
				`admin/template/${encodeURIComponent(templateId)}`,
				'DELETE'
			);

			this.busy = false;
			if (opResult.success) {
				return opResult.value;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async addStatuses(
		templateId: string,
		statuses: Api.ITemplateStatus[],
		campaignType: AdminModels.CampaignType,
		isCombo: boolean
	) {
		const categoriesType = getCategoryPathByCampaignType(campaignType, isCombo);
		if (!this.isBusy) {
			this.busy = true;
			this.isExpandedUI = false;
			const opResult =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
					`admin/template/industry/${categoriesType}/${encodeURIComponent(templateId)}/statuses`,
					'POST',
					statuses
				);

			await this.load(campaignType);
			this.busy = false;
			this.setTotal(this.total + statuses.length);
			this.isExpandedUI = true;

			if (opResult.success) {
				return opResult.value;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async removeStatuses(
		templateId: string,
		statuses: Api.ITemplateStatus[],
		campaignType: AdminModels.CampaignType,
		isCombo: boolean
	) {
		const categoriesType = getCategoryPathByCampaignType(campaignType, isCombo);
		if (!this.isBusy) {
			this.busy = true;
			this.isExpandedUI = false;
			const opResult =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
					`admin/template/industry/${categoriesType}/${encodeURIComponent(templateId)}/statuses`,
					'DELETE',
					statuses
				);

			await this.load(campaignType);
			this.busy = false;
			this.setTotal(this.total - statuses.filter(x => x.industry === this.industry)?.length);

			if (opResult.success || opResult.systemMessage === 'Template not found in any category or industry') {
				return opResult.value;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async bulkArchive(templateIds: string[], industry: Api.Industry) {
		if (!this.isBusy) {
			this.busy = true;

			try {
				const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IOperationResultNoValue>(
					`admin/template/${industry}/archive/bulk`,
					'PUT',
					templateIds
				);

				this.busy = false;

				if (opResult.success) {
					return opResult;
				} else {
					throw Api.asApiError(opResult);
				}
			} catch (error) {
				this.busy = false;
				throw error;
			}
		}
	}

	public async bulkArchiveAll(templateIds: string[]) {
		if (!this.isBusy) {
			this.busy = true;

			try {
				const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IOperationResultNoValue>(
					`admin/template/archive/bulk`,
					'PUT',
					templateIds
				);

				this.busy = false;

				if (opResult.success) {
					return opResult;
				} else {
					throw Api.asApiError(opResult);
				}
			} catch (error) {
				this.busy = false;
				throw error;
			}
		}
	}

	private transformTemplates(templates: AdminModels.ISystemEmailTemplate[]) {
		return new Api.ObservableCollection(
			templates.map(
				x => new TemplateWithStatusesViewModel(this.userSession as AdminUserSessionContext, x, this.industry)
			)
		);
	}
}

export class TemplateWithStatusesViewModel extends Api.ViewModel {
	public readonly industry: string;
	private mTemplateReference: AdminModels.ISystemEmailTemplate<Api.ITemplate | Api.IBlogTemplate>;
	constructor(
		userSession: AdminUserSessionContext,
		template: AdminModels.ISystemEmailTemplate<Api.ITemplate | Api.IBlogTemplate>,
		industry: Api.Industry
	) {
		super(userSession);

		makeObservable<TemplateWithStatusesViewModel, 'mTemplateReference'>(this, {
			mTemplateReference: observable.ref,
			templateReference: computed,
			refName: computed,
			refIndustries: computed,
			refCreated: computed,
		});

		this.industry = industry;
		this.mTemplateReference = template;
	}

	public get templateReference() {
		return this.mTemplateReference;
	}

	public get refName() {
		return this.mTemplateReference?.template?.name ?? '';
	}

	public get refIndustries() {
		return Object.keys(this.templateReference.industries).join(', ').replace('_Others_', 'Others') ?? '';
	}

	public get refCreated() {
		return moment(this.mTemplateReference?.template?.creationDate).format('MM/DD/YYYY') ?? '';
	}
}

export class TemplateEditViewModel extends Api.ViewModel {
	protected mTemplate: Api.ITemplate;
	protected mStatuses: Api.ITemplateStatus[];
	protected mSocialMedia: Api.IEmbeddedSocialMediaTemplateContent;
	constructor(userSession: AdminUserSessionContext, template?: Api.ITemplate) {
		super(userSession);

		makeObservable<TemplateEditViewModel, 'mTemplate' | 'mStatuses' | 'mSocialMedia'>(this, {
			mTemplate: observable.ref,
			mStatuses: observable.ref,
			mSocialMedia: observable.ref,
			template: computed,
			id: computed,
			statuses: computed,
			socialMedia: computed,
			loadCategories: action,
			load: override,
			deleteAttachments: action,
			create: action,
			edit: action,
		});

		this.setTemplate = this.setTemplate.bind(this);
		this.setTemplate(template);
	}

	public get template() {
		return this.mTemplate;
	}

	public get id() {
		return this.mTemplate?.id;
	}

	public get statuses() {
		return this.mStatuses;
	}

	public get socialMedia() {
		return this.mSocialMedia;
	}
	private getCategoryNamesPathByCampaignType(campaignType: AdminModels.CampaignType, isCombo?: boolean) {
		let categoriesType = '';
		switch (campaignType) {
			case AdminModels.CampaignType.Social:
				categoriesType = 'socialCategoryNames';
				break;
			case AdminModels.CampaignType.Blog:
				categoriesType = 'blogCategoryNames';
				break;
			case AdminModels.CampaignType.Email:
				categoriesType = 'categoryNames';
				break;
			default:
				categoriesType = 'categoryNames';
				break;
		}
		if (campaignType === AdminModels.CampaignType.Social && isCombo) {
			categoriesType = 'socialCategoryNames';
		}
		return categoriesType;
	}

	public async loadCategories(campaignType: AdminModels.CampaignType) {
		const categoryNames = this.getCategoryNamesPathByCampaignType(campaignType);
		const opResult: Api.IOperationResult<AdminModels.ITemplatesIndustryWithCategories[]> =
			await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ITemplatesIndustryWithCategories[]>(
				`admin/template/${categoryNames}`,
				'GET'
			);

		if (opResult.success) {
			return opResult.value;
		} else {
			throw Api.asApiError(opResult);
		}
	}

	public async load(templateId: string = this.mTemplate.id, campaignType?: AdminModels.CampaignType) {
		const categoriesType = getCategoryPathByCampaignType(campaignType);
		if (!this.busy) {
			this.busy = true;
			const opResult =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
					`admin/template/industry/${categoriesType}/templates/${templateId}`,
					'GET'
				);

			this.busy = false;
			if (opResult.success) {
				this.setTemplate(opResult.value.template);
				this.mStatuses = opResult.value.statuses;
				this.mSocialMedia = opResult.value.socialMedia;
				this.loaded = true;
				return opResult.value;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async deleteAttachments(templateId: string = this.mTemplate.id, attachments: Api.IFileAttachment[]) {
		if (!this.busy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITemplate>(
				`admin/template/industry/categories/templates/${templateId}/attachments`,
				'DELETE',
				attachments?.map(x => x.id)
			);

			this.busy = false;
			if (opResult.success) {
				if (templateId === this.mTemplate.id) {
					this.setTemplate(opResult.value);
					this.mSocialMedia = opResult.value.socialMedia;
				} else if (templateId === this.mSocialMedia?.templateId) {
					this.mSocialMedia = {
						...this.mSocialMedia,
						attachments: opResult.value.attachments,
						content: opResult.value.content,
					};
				}
				return opResult.value;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async create(
		systemEmailTemplate: AdminModels.ISystemEmailTemplateWithStatuses = this.toJs(),
		campaignType?: AdminModels.CampaignType
	) {
		const categoriesType = getCategoryPathByCampaignType(campaignType);
		if (!this.busy) {
			this.busy = true;
			const opResult =
				await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
					`admin/template/industry/${categoriesType}/templates`,
					'POST',
					systemEmailTemplate
				);

			this.busy = false;
			if (opResult.success) {
				this.mStatuses = systemEmailTemplate.statuses; // not returned in response.
				this.setTemplate(opResult.value.template);
				return this.mTemplate;
			} else {
				throw Api.asApiError(opResult);
			}
		}
	}

	public async edit(
		template: Api.ITemplate,
		statusesToRemove: Api.ITemplateStatus[],
		statusesToAdd: Api.ITemplateStatus[],
		statusesToUpdate: Api.ITemplateStatus[],
		campaignType?: AdminModels.CampaignType,
		socialMedia?: Api.IEmbeddedSocialMediaTemplateContent
	) {
		if (!this.busy) {
			this.busy = true;
			let updatedTemplate: Api.ITemplate = null;
			let updatedStatuses: Api.ITemplateStatus[] = null;
			let updatedSocialMedia: Api.IEmbeddedSocialMediaTemplateContent = null;
			const categoriesType = getCategoryPathByCampaignType(campaignType);

			const finish = () => {
				this.busy = false;
				if (updatedTemplate) {
					this.setTemplate(updatedTemplate);
				}
				if (updatedStatuses) {
					this.mStatuses = updatedStatuses;
				}
				if (updatedSocialMedia) {
					this.mSocialMedia = updatedSocialMedia;
				}
			};
			const throwError = (operationResult: Api.IOperationResultNoValue) => {
				finish();
				throw Api.asApiError(operationResult);
			};

			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplate>(
				`admin/template/industry/${categoriesType}/templates/${template.id}`, // update this path per PR
				'PUT',
				{
					socialMedia,
					template,
				}
			);

			if (!opResult.success) {
				throwError(opResult);
			}

			updatedTemplate = opResult.value.template;
			updatedSocialMedia = opResult.value.socialMedia;

			if (statusesToRemove.length) {
				// remove statuses first
				const removeStatusResult =
					await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
						`admin/template/industry/${categoriesType}/${template.id}/statuses`,
						'DELETE',
						statusesToRemove
					);
				if (removeStatusResult.success) {
					updatedTemplate = removeStatusResult.value.template;
					updatedStatuses = removeStatusResult.value.statuses;
				} else {
					throwError(removeStatusResult);
				}
			}

			if (statusesToUpdate.length) {
				// update existing (publish)
				const updateStatusResult =
					await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
						`admin/template/industry/${categoriesType}/${template.id}/statuses`,
						'PUT',
						statusesToUpdate
					);
				if (updateStatusResult.success) {
					updatedTemplate = updateStatusResult.value.template;
					updatedSocialMedia = updateStatusResult.value.socialMedia;
					updatedStatuses = updateStatusResult.value.statuses;
				} else {
					throwError(updateStatusResult);
				}
			}

			if (statusesToAdd.length) {
				// then add desired statuses
				const statusResult =
					await this.mUserSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemEmailTemplateWithStatuses>(
						`admin/template/industry/${categoriesType}/${template.id}/statuses`,
						'POST',
						statusesToAdd
					);

				if (statusResult.success) {
					updatedStatuses = statusResult.value.statuses;
					updatedSocialMedia = statusResult.value.socialMedia;
					updatedTemplate = statusResult.value.template;
				} else {
					throwError(statusResult);
				}
			}

			finish();
		}
	}

	public toJs = (): AdminModels.ISystemEmailTemplateWithStatuses => {
		return {
			socialMedia: this.mSocialMedia,
			statuses: this.mStatuses,
			template: this.mTemplate,
		};
	};

	public getSystemTemplate = (): AdminModels.ISystemEmailTemplate => {
		return {
			industries: {},
			socialMedia: this.socialMedia,
			template: this.mTemplate,
		};
	};

	protected setTemplate(template?: Api.ITemplate) {
		this.mTemplate = template
			? this.mTemplate
				? {
						...(this.mTemplate || {}),
						...template, // template might not have content, but this.mTemplate might have this filled
					}
				: template
			: template;
	}
}

export class IndustryHtmlNewsletterViewModel extends TemplateEditViewModel implements IHtmlNewsletter {
	public static HtmlNewslettersCategoryName = 'HTML Newsletters';
	public readonly files: Api.AttachmentsViewModel;

	public static getDefaultTemplateStatuses() {
		return DefaultSupportedIndustries.map<Api.ITemplateStatus>(x => {
			return {
				category: IndustryHtmlNewsletterViewModel.HtmlNewslettersCategoryName,
				industry: x,
				status: Api.TemplateStatus.Draft,
			};
		});
	}

	public static async createWithDefaultBlankTemplate(
		userSession: AdminUserSessionContext,
		systemTemplate: AdminModels.ISystemEmailTemplateWithStatuses = {}
	) {
		const template: Api.ITemplate = {
			name: 'Untitled Newsletter',
			...(systemTemplate.template || {}),
			scope: Api.TemplateScope.Industry,
			templateType: Api.TemplateType.HtmlNewsletter,
		};
		const sysTemplate: AdminModels.ISystemEmailTemplateWithStatuses = {
			...systemTemplate,
			statuses: IndustryHtmlNewsletterViewModel.getDefaultTemplateStatuses(),
			template: {
				...HtmlNewsletterViewModel.createDefaultBlankTemplateModel(template),
				...template,
			},
		};
		const result = new IndustryHtmlNewsletterViewModel(userSession, sysTemplate);

		await result.create(sysTemplate, AdminModels.CampaignType.Email);
		return result;
	}

	constructor(userSession: AdminUserSessionContext, systemTemplate?: AdminModels.ISystemEmailTemplateWithStatuses) {
		super(userSession, systemTemplate?.template);

		makeObservable(this, {
			name: computed,
			industries: computed,
			design: computed,
			canEdit: computed,
		});

		this.files = new Api.AttachmentsViewModel(userSession);
	}

	public get name() {
		return this.mTemplate.name;
	}

	public get industries() {
		return this.statuses.map(x => x.industry);
	}

	public get design() {
		return this.template?.content ? JSON.parse(this.template.content.source) : null;
	}

	public get canEdit() {
		return !!(this.mUserSession as AdminUserSessionContext).permissions[AdminPermission.ManageTemplates];
	}

	public save = async (template: Api.ITemplate) => {
		await this.edit(
			{
				...(this.mTemplate || {}),
				...template,
			},
			[],
			[],
			[]
		);
		return this.mTemplate;
	};

	public saveAsNewTemplate = async (template: Api.ITemplate, statuses = this.mStatuses): Promise<Api.ITemplate> => {
		const draftStatuses = statuses
			? statuses.map(x => {
					const templateStatus = { ...x };
					templateStatus.status = Api.TemplateStatus.Draft;
					return templateStatus;
				})
			: IndustryHtmlNewsletterViewModel.getDefaultTemplateStatuses();
		await this.create({
			statuses: draftStatuses,
			template,
		});
		return this.mTemplate;
	};

	public sendTestEmail = () => {
		return HtmlNewsletterViewModel.prototype.sendTestEmail.call(this);
	};

	public setScope = async (): Promise<void> => {
		throw Api.asApiError('Not implemented');
	};

	public updateContent = async (design: Design, htmlStringValue: string): Promise<Api.ITemplate> => {
		const content: Api.IRawRichTextContentState = {
			document: htmlStringValue,
			documentVersion: 1,
			source: JSON.stringify(design),
			sourceFormat: Api.ContentSourceFormat.UnlayerHtmlNewsletter,
		};
		await this.edit(
			{
				...this.mTemplate,
				content,
			},
			[],
			[],
			[]
		);
		return this.mTemplate;
	};

	public updateName = async (name: string): Promise<Api.ITemplate> => {
		await this.edit(
			{
				...this.mTemplate,
				name: name.trim(),
			},
			[],
			[],
			[]
		);

		return this.mTemplate;
	};
}

export abstract class IndustryTemplateViewModel<
	TTemplateModel extends Api.IBaseResourceModel = Api.IBaseResourceModel,
	TSystemTemplate extends
		AdminModels.IBaseSystemTemplate<TTemplateModel> = AdminModels.IBaseSystemTemplate<TTemplateModel>,
> extends Api.ViewModel {
	protected mModel: TSystemTemplate;
	protected mIndustries: Api.Industry[];

	constructor(userSession: AdminUserSessionContext, template: TSystemTemplate, industries?: Api.Industry[]) {
		super(userSession);

		makeObservable<IndustryTemplateViewModel<TTemplateModel, TSystemTemplate>, 'mModel' | 'mIndustries'>(this, {
			mModel: observable.ref,
			mIndustries: observable.ref,
			isBusy: override,
			name: computed,
			id: computed,
			creationDate: computed,
			lastModifiedDate: computed,
			industries: computed,
			canDelete: computed,
			setSystemTemplate: action,
			update: action,
			delete: action,
			archive: action,
			publish: action,
			duplicate: action,
			load: override,
		});

		this.mIndustries = industries || (Object.keys(template?.industries || {}) as Api.Industry[]);
		this.mSetTemplate = this.mSetTemplate.bind(this);
		this.mSetTemplate(template);
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	// eslint-disable-next-line @typescript-eslint/class-literal-property-style
	public get name() {
		return '';
	}

	public get id() {
		return this.mModel?.template.id;
	}

	public get creationDate() {
		return this.mModel?.template.creationDate;
	}

	public get lastModifiedDate() {
		return this.mModel?.template.lastModifiedDate;
	}

	public get industries() {
		return this.mIndustries;
	}

	// eslint-disable-next-line @typescript-eslint/class-literal-property-style
	public get canDelete() {
		return true;
	}

	public setSystemTemplate = (template: TSystemTemplate) => {
		this.mSetTemplate(template);
	};

	public update = (template: TTemplateModel) => {
		return this.executeRequest(template, 'PUT');
	};

	public delete = () => {
		return this.executeRequest(null, 'DELETE');
	};

	public archive(industry: Api.Industry) {
		return this.executeRequest(this.mModel?.template, 'PUT', 'archive', {
			industry,
		});
	}

	public publish(industry: Api.Industry) {
		return this.executeRequest(this.mModel?.template, 'PUT', 'publish', {
			industry,
		});
	}

	public duplicate = (name?: string) => {
		return this.executeRequest(this.mModel?.template, 'POST', 'clone', name ? { name } : undefined, false);
	};

	public load() {
		return this.executeRequest(null, 'GET');
	}

	public getStatusForIndustry = (industry: Api.Industry) => {
		return this.mModel?.industries ? this.mModel.industries[industry] : null;
	};

	public toJs = () => {
		return this.mModel;
	};

	public toTemplate = () => {
		return this.mModel?.template;
	};

	protected abstract getBaseApiPath(method: Api.HTTPMethod): string;

	protected executeRequest = (
		template: TTemplateModel,
		method: Api.HTTPMethod,
		path?: string,
		params?: Api.IDictionary<string>,
		updateModelWithResult = true
	) => {
		if (!this.isBusy) {
			this.busy = true;
			const promise = new Promise<TSystemTemplate>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<TSystemTemplate>) => {
					this.busy = false;
					if (opResult.success) {
						if (method !== 'DELETE') {
							if (updateModelWithResult) {
								this.mSetTemplate(opResult.value);
								resolve(this.mModel);
								return;
							}
						}
						resolve(opResult.value);
					} else {
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TSystemTemplate>(
					`${this.getBaseApiPath(method)}/${this.id}${path ? `/${path}` : ''}${
						params ? `?${toQueryString(params)}` : ''
					}`,
					method,
					method !== 'PUT' ? null : template,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
	};

	protected mSetTemplate(templateModel: TSystemTemplate) {
		this.mModel = templateModel;
		this.mIndustries = templateModel?.industries
			? (Object.keys(templateModel?.industries || {}) as Api.Industry[])
			: [];
	}
}

export class IndustryAutomationTemplateViewModel extends IndustryTemplateViewModel<
	Api.IAutomationTemplate,
	AdminModels.ISystemAutomationTemplate
> {
	public static Create = async (
		userSession: AdminUserSessionContext,
		name?: string,
		trigger?: Api.IAutomationTrigger
	) => {
		const createOpResult = await userSession.webServiceHelper.callWebServiceAsync<Api.IAutomationTemplate>(
			`automationTemplate?name=${encodeURIComponent(
				name || Api.VmUtils.Automations.getDefaultNameForNewAutomationTemplate(trigger)
			)}`,
			'POST'
		);

		if (!createOpResult.success) {
			throw createOpResult;
		}

		let templateModel = createOpResult.value;

		// add the trigger
		if (trigger) {
			const template = new AdminAutomationTemplateViewModel(userSession, createOpResult.value);
			try {
				await template.addTrigger(trigger);
				templateModel = template.toJs();
			} catch (err) {
				try {
					await template.delete();
				} catch {
					// eat this
				}
				throw err;
			}
		}

		return IndustryAutomationTemplateViewModel.fromAutomationTemplate(userSession, templateModel);
	};

	public static fromAutomationTemplate = async (
		userSession: AdminUserSessionContext,
		template: Api.IAutomationTemplate
	) => {
		if (template) {
			const addToAllIndustriesOpResult =
				await userSession.webServiceHelper.callWebServiceAsync<AdminModels.ISystemAutomationTemplate>(
					`admin/AutomationTemplate/${template.id}/Industry`,
					'PUT',
					DefaultSupportedIndustries
				);
			if (!addToAllIndustriesOpResult.success) {
				throw addToAllIndustriesOpResult;
			}

			return new IndustryAutomationTemplateViewModel(userSession, addToAllIndustriesOpResult.value);
		}
	};

	protected declare mAutomationTemplate: AdminAutomationTemplateViewModel;

	constructor(
		userSession: AdminUserSessionContext,
		template: AdminModels.ISystemAutomationTemplate,
		industries?: Api.Industry[]
	) {
		super(userSession, template, industries);

		makeObservable<IndustryAutomationTemplateViewModel, 'mAutomationTemplate'>(this, {
			mAutomationTemplate: observable.ref,
			id: override,
			isLoaded: override,
			name: override,
			automationTemplate: computed,
			canDelete: override,
			publish: override,
			archive: override,
			setIndustries: action,
		});
	}

	public get id() {
		return this.mModel?.template?.id;
	}

	public get isLoaded() {
		return !!this.mModel?.template?.creationDate;
	}

	public get name() {
		return this.mAutomationTemplate?.name;
	}

	public get automationTemplate() {
		return this.mAutomationTemplate;
	}

	public get canDelete() {
		return !!this.industries?.every(x => this.mModel?.industries[x] === Api.TemplateStatus.Draft);
	}

	public async publish(industry: Api.Industry) {
		await this.mEnsurePublishedAutomationTemplate();
		return this.executeRequest(null, 'PUT', 'publish', { industry });
	}

	public async archive(industry: Api.Industry) {
		await this.mEnsurePublishedAutomationTemplate();
		return this.executeRequest(null, 'PUT', 'archive', { industry });
	}

	public setIndustries = async (industries: Api.Industry[], status?: Api.TemplateStatus) => {
		if (this.isBusy) {
			return null;
		}

		this.busy = true;
		if (status === Api.TemplateStatus.Published) {
			await this.mEnsurePublishedAutomationTemplate();
		}
		const promise = new Promise<Api.IOperationResult<AdminModels.ISystemAutomationTemplate>>((resolve, reject) => {
			const onFinish = action((opResult: Api.IOperationResult<AdminModels.ISystemAutomationTemplate>) => {
				this.busy = false;
				if (opResult.success) {
					this.mSetTemplate(opResult.value);
					resolve(opResult);
				} else {
					reject(opResult);
				}
			});
			this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.ISystemAutomationTemplate>(
				`${this.getBaseApiPath('PUT')}/${this.id}/Industry${status ? `?status=${encodeURIComponent(status)}` : ''}`,
				'PUT',
				industries,
				onFinish,
				onFinish
			);
		});
		return promise;
	};

	protected getBaseApiPath(method: Api.HTTPMethod) {
		if (method === 'DELETE') {
			return 'AutomationTemplate';
		}
		return 'admin/AutomationTemplate';
	}

	protected mSetTemplate(systemTemplate: AdminModels.ISystemAutomationTemplate) {
		super.mSetTemplate(systemTemplate);
		this.mAutomationTemplate = new AdminAutomationTemplateViewModel(
			this.userSession as AdminUserSessionContext,
			systemTemplate.template
		);
	}

	protected mEnsurePublishedAutomationTemplate = (): Promise<Api.IOperationResultNoValue> => {
		if (this.mAutomationTemplate?.status === Api.TemplateStatus.Draft || this.mAutomationTemplate?.hasDraftVersion) {
			return this.mAutomationTemplate.commitDraft();
		}

		return Promise.resolve({ success: true, systemCode: 200 });
	};
}

export class AdminAutomationTemplatesViewModel extends Api.ViewModel<AdminUserSessionContext> {
	protected mTemplates: Api.IAutomationTemplate[];

	constructor(userSession: AdminUserSessionContext) {
		super(userSession);
		makeObservable<AdminAutomationTemplatesViewModel, 'mTemplates'>(this, {
			mTemplates: observable.ref,
			canCreate: computed,
		});
		this.mTemplates = [];
	}

	public get canCreate() {
		return this.userSession.hasPermission(AdminPermission.CreateAutomationTemplateOnBehalf);
	}

	public load() {
		if (!this.isBusy) {
			this.loading = true;
			return new Promise<Api.IOperationResult<Api.IAutomationTemplate[]>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.IAutomationTemplate[]>) => {
					this.loading = false;
					if (opResult.success) {
						this.mTemplates = opResult.value;
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});
				this.userSession.webServiceHelper.callWebServiceWithOperationResults(
					'automationTemplate',
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}
	}
}

export class AdminAutomationTemplateViewModel extends Api.ViewModel<AdminUserSessionContext> {
	protected mModel: Api.IAutomationTemplate;

	constructor(
		userSession: AdminUserSessionContext,
		model: Api.IAutomationTemplate,
		_supportedStepModelTypes?: Api.AutomationStepModelType[]
	) {
		super(userSession);
		makeObservable<AdminAutomationTemplateViewModel, 'mModel'>(this, {
			mModel: observable.ref,
			canEdit: computed,
			name: computed,
			status: computed,
			hasDraftVersion: computed,
			id: computed,
		});
		this.mModel = model;
	}

	public get canEdit() {
		return (
			this.mModel?.status !== Api.TemplateStatus.Archived &&
			this.mUserSession.hasPermission(AdminPermission.ManageTemplates)
		);
	}

	public get name() {
		return this.mModel?.name;
	}

	public get status() {
		return this.mModel?.status;
	}

	public get hasDraftVersion() {
		return !!this.mModel?.draft;
	}

	public get id() {
		return this.mModel?.id;
	}

	public toJs() {
		return this.mModel;
	}

	public async addTrigger(trigger: Api.IAutomationTrigger) {
		const triggerPath = AdminAutomationTemplateViewModel.getTriggerPath(trigger);
		if (!triggerPath) {
			throw new Error(`Unknown trigger type: ${trigger?._type}`);
		}
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResult<Api.IAutomationTemplate>>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.IAutomationTemplate>) => {
					this.busy = false;
					if (opResult.success) {
						this.mModel = opResult.value;
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
					`AutomationTemplate/${encodeURIComponent(this.mModel.id)}/Trigger/${triggerPath}`,
					'PUT',
					trigger,
					onFinish,
					onFinish
				);
			});
		}
	}

	private static getTriggerPath(trigger: Api.IAutomationTrigger): string | null {
		switch (trigger?._type) {
			case Api.AutomationTriggerType.Tag:
				return 'Tag';
			case Api.AutomationTriggerType.NewLead:
				return 'NewLead';
			case Api.AutomationTriggerType.NewPolicyTrigger:
			case Api.AutomationTriggerType.ResourceSelector:
				return 'ResourceSelector';
			case Api.AutomationTriggerType.NewClient:
				return 'NewClient';
			case Api.AutomationTriggerType.NewDonor:
				return 'NewDonor';
			case Api.AutomationTriggerType.NewDonationTrigger:
				return 'Donation';
			case Api.AutomationTriggerType.Texting:
				return 'TextingCampaign';
			case Api.AutomationTriggerType.Meeting:
				return 'MeetingReminder';
			case Api.AutomationTriggerType.EventRegistration:
				return 'EventRegistration';
			default:
				return null;
		}
	}

	public async delete() {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResultNoValue) => {
					this.busy = false;
					if (opResult.success) {
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
					`automationTemplate/${this.mModel.id}`,
					'DELETE',
					null,
					onFinish,
					onFinish
				);
			});
		}
	}

	public async commitDraft() {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResultNoValue) => {
					this.busy = false;
					if (opResult.success) {
						resolve(opResult);
					} else {
						reject(opResult);
					}
				});
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
					`automationTemplate/${this.mModel.id}/commit`,
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}
	}
}

export abstract class IndustryTemplatesViewModel<
	TTemplate = any,
	TSystemTemplate extends AdminModels.IBaseSystemTemplate<TTemplate> = AdminModels.IBaseSystemTemplate<TTemplate>,
	TTemplateViewModel extends IndustryTemplateViewModel<TTemplate, TSystemTemplate> = IndustryTemplateViewModel<
		TTemplate,
		TSystemTemplate
	>,
> extends Api.ViewModel {
	protected mLoadingPromise: Promise<TSystemTemplate[]>;
	protected mArchived: Api.ObservableCollection<TTemplateViewModel>;
	protected mDrafts: Api.ObservableCollection<TTemplateViewModel>;
	protected mPublished: Api.ObservableCollection<TTemplateViewModel>;
	public readonly industry: Api.Industry;

	constructor(userSession: AdminUserSessionContext, industry: Api.Industry) {
		super(userSession);

		makeObservable<IndustryTemplatesViewModel<TTemplate, TSystemTemplate, TTemplateViewModel>, 'mLoadingPromise'>(
			this,
			{
				mLoadingPromise: observable.ref,
				drafts: computed,
				published: computed,
				archived: computed,
				isBusy: override,
				isLoading: override,
				load: override,
				editTemplate: action,
				deleteTemplate: action,
				archiveTemplate: action,
				publishTemplate: action,
				reorderPublishedTemplate: action,
				reset: action,
			}
		);

		this.industry = industry;
		this.mArchived = new Api.ObservableCollection(null, 'id');
		this.mDrafts = new Api.ObservableCollection(null, 'id');
		this.mPublished = new Api.ObservableCollection(null, 'id');
	}

	public get drafts() {
		return this.mDrafts;
	}

	public get published() {
		return this.mPublished;
	}

	public get archived() {
		return this.mArchived;
	}

	public get isBusy() {
		return this.busy || this.loading || !!this.mLoadingPromise;
	}

	public get isLoading() {
		return this.loading || !!this.mLoadingPromise;
	}

	public load() {
		if (!this.mLoadingPromise) {
			this.mLoadingPromise = new Promise<TSystemTemplate[]>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<TSystemTemplate[]>) => {
					this.mLoadingPromise = null;
					if (opResult.success) {
						this.mArchived.clear();
						this.mDrafts.clear();
						this.mPublished.clear();

						// sort
						opResult.value.forEach(x => {
							const status = x.industries[this.industry];
							const industriesList = Object.keys(x.industries) as Api.Industry[];
							if (status) {
								switch (status) {
									case Api.TemplateStatus.Draft: {
										this.mDrafts.add(this.createIndustryTemplate(x, industriesList) as any);
										break;
									}
									case Api.TemplateStatus.Archived: {
										this.mArchived.add(this.createIndustryTemplate(x, industriesList) as any);
										break;
									}
									case Api.TemplateStatus.Published: {
										this.mPublished.add(this.createIndustryTemplate(x, industriesList) as any);
										break;
									}
									default: {
										break;
									}
								}
							}
						});
						resolve(opResult.value);
					} else {
						reject(opResult);
					}
				});
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<TSystemTemplate[]>(
					`${this.getBaseApiPath('GET')}/industry/${this.industry}`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}
		return this.mLoadingPromise;
	}

	public editTemplate = (template: TTemplateViewModel, templateModel: TTemplate) => {
		if (!this.isBusy) {
			const promise = template.update(templateModel);
			if (promise) {
				this.busy = true;
				promise
					.then(() => {
						this.busy = false;
					})
					.catch(() => {
						this.busy = false;
					});
			}
			return promise;
		}
	};

	public deleteTemplate = (template: TTemplateViewModel) => {
		if (!this.isBusy) {
			const promise = template.delete();
			if (promise) {
				this.busy = true;
				promise
					.then(() => {
						runInAction(() => {
							this.busy = false;
							const itemsToRemove = [template];
							this.mDrafts.removeItems(itemsToRemove);
							this.mPublished.removeItems(itemsToRemove);
							this.mArchived.removeItems(itemsToRemove);
						});
					})
					.catch(() => {
						this.busy = false;
					});
			}
			return promise;
		}
	};

	public archiveTemplate = (template: TTemplateViewModel, industry: Api.Industry) => {
		if (!this.isBusy) {
			const promise = template.archive(industry);
			if (promise) {
				this.busy = true;
				promise
					.then(() => {
						runInAction(() => {
							this.busy = false;
							if (this.mDrafts.has(template) || this.mPublished.has(template)) {
								const itemsToRemove = [template];
								this.mDrafts.removeItems(itemsToRemove);
								this.mPublished.removeItems(itemsToRemove);
								this.mArchived.add(template);
							}
						});
					})
					.catch(() => {
						this.busy = false;
					});
			}
			return promise;
		}
	};

	public publishTemplate = (template: TTemplateViewModel, industry: Api.Industry) => {
		if (!this.isBusy) {
			const promise = template.publish(industry);
			if (promise) {
				this.busy = true;
				promise
					.then(() => {
						runInAction(() => {
							this.busy = false;
							if (this.mDrafts.has(template) || this.mArchived.has(template)) {
								const itemsToRemove = [template];
								this.mDrafts.removeItems(itemsToRemove);
								this.mArchived.removeItems(itemsToRemove);
								this.mPublished.add(template);
							}
						});
					})
					.catch(() => {
						this.busy = false;
					});
			}
			return promise;
		}
	};

	public reorderPublishedTemplate = (template: TTemplateViewModel, industry: Api.Industry, toIndex: number) => {
		if (!template && !this.mPublished.has(template)) {
			return null;
		}
		const fromIndex = this.mPublished.indexOf(template);
		if (fromIndex === toIndex) {
			return null;
		}
		if (!this.isBusy) {
			const promise = new Promise<Api.IOperationResultNoValue>((resolve, reject) => {
				const onFinish = action((opResult: Api.IOperationResult<Api.IOperationResultNoValue>) => {
					this.busy = false;
					if (opResult.success) {
						resolve(opResult);
					} else {
						// undo
						runInAction(() => {
							this.mPublished.splice(toIndex, 1);
							this.mPublished.splice(fromIndex, 0, template);
							reject(opResult);
						});
					}
				});

				runInAction(() => {
					this.mPublished.splice(fromIndex, 1);
					this.mPublished.splice(toIndex, 0, template);

					const ids = this.mPublished.map(x => x.id);
					this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IOperationResultNoValue>(
						`${this.getBaseApiPath('PUT')}/industry/${industry}`,
						'PUT',
						ids,
						onFinish,
						onFinish
					);
				});
			});
			return promise;
		}
	};

	public reset = () => {
		this.busy = false;
		this.mArchived.clear();
		this.mDrafts.clear();
		this.mPublished.clear();
		this.mLoadingPromise = null;
	};

	protected abstract getBaseApiPath(method: Api.HTTPMethod): string;
	protected abstract createIndustryTemplate(
		systemTemplate: TSystemTemplate,
		industries?: Api.Industry[]
	): IndustryTemplateViewModel;
}

export class IndustryAutomationTemplatesViewModel extends IndustryTemplatesViewModel<
	Api.IAutomationTemplate,
	AdminModels.ISystemAutomationTemplate,
	IndustryAutomationTemplateViewModel
> {
	public duplicateTemplate = (template: IndustryAutomationTemplateViewModel, name?: string) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<IndustryAutomationTemplateViewModel>((resolve, reject) => {
				template
					.duplicate(name)
					.then(model => {
						const clone = this.createIndustryTemplate(model);
						runInAction(() => {
							this.busy = false;
							this.mDrafts.add(clone);
							resolve(clone);
						});
					})
					.catch(error => {
						this.busy = false;
						reject(error);
					});
			});
		}
	};

	constructor(userSession: AdminUserSessionContext, industry: Api.Industry) {
		super(userSession, industry);

		makeObservable(this, {
			duplicateTemplate: action,
		});
	}

	protected getBaseApiPath() {
		return 'admin/AutomationTemplate';
	}

	// FOLLOWUP: Resolve

	// @ts-ignore
	protected createIndustryTemplate(systemTemplate: AdminModels.ISystemAutomationTemplate, industries?: Api.Industry[]) {
		return new IndustryAutomationTemplateViewModel(
			this.mUserSession as AdminUserSessionContext,
			systemTemplate,
			industries
		);
	}
}

export class AccountSnapshotsViewModel extends Api.ViewModel {
	protected mAccountSnapshotsSummaryByMonth: AdminModels.IMonthlyRecurringRevenueSummary;
	protected mAccountSnapshotsSummaryByMonthPromise: Promise<
		Api.IOperationResult<AdminModels.IMonthlyRecurringRevenueSummary>
	>;
	protected mFetchedAny: boolean;

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable<
			AccountSnapshotsViewModel,
			'mAccountSnapshotsSummaryByMonth' | 'mAccountSnapshotsSummaryByMonthPromise' | 'mFetchedAny'
		>(this, {
			mAccountSnapshotsSummaryByMonth: observable.ref,
			mAccountSnapshotsSummaryByMonthPromise: observable.ref,
			mFetchedAny: observable.ref,
			loadAccountSnapshotSummaryByMonth: action,
			isLoading: override,
			summary: computed,
		});

		this.mFetchedAny = false;
	}

	public loadAccountSnapshotSummaryByMonth = (filter: IAccountFilterCriteria) => {
		if (!this.mAccountSnapshotsSummaryByMonthPromise) {
			this.mAccountSnapshotsSummaryByMonthPromise = new Promise<
				Api.IOperationResult<AdminModels.IMonthlyRecurringRevenueSummary>
			>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<AdminModels.IMonthlyRecurringRevenueSummary>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mAccountSnapshotsSummaryByMonth = opResult.value;
							this.mAccountSnapshotsSummaryByMonthPromise = null;
							resolve(opResult);
						} else {
							this.mAccountSnapshotsSummaryByMonthPromise = null;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IMonthlyRecurringRevenueSummary>(
					`admin/AccountSnapshot/byMonth/renewalSummary`,
					'POST',
					filter,
					onFinish,
					onFinish
				);
			});
		}
		return this.mAccountSnapshotsSummaryByMonthPromise;
	};

	public get isLoading() {
		return !!this.mAccountSnapshotsSummaryByMonthPromise;
	}

	public get summary() {
		return this.mAccountSnapshotsSummaryByMonth;
	}
}

export class AccountSnapshotViewModel extends Api.ViewModel {
	protected mAccountSnapshot: AdminModels.IAccountSnapshot;
	protected mAccountSnapshotsRenewalPromise: Promise<Api.IOperationResult<AdminModels.IAccountSnapshot>>;
	protected mAccountSnapshotsRenewalDiscountPromise: Promise<Api.IOperationResult<AdminModels.IAccountSnapshot>>;

	constructor(userSession: Api.UserSessionContext, snapshot: AdminModels.IAccountSnapshot) {
		super(userSession);

		makeObservable<
			AccountSnapshotViewModel,
			'mAccountSnapshot' | 'mAccountSnapshotsRenewalPromise' | 'mAccountSnapshotsRenewalDiscountPromise'
		>(this, {
			mAccountSnapshot: observable.ref,
			mAccountSnapshotsRenewalPromise: observable.ref,
			mAccountSnapshotsRenewalDiscountPromise: observable.ref,
			updateRenewalProbability: action,
			updateRenewalDiscountPercentage: action,
			isLoading: override,
			canEdit: computed,
			accountModel: computed,
			id: computed,
			accountDeleted: computed,
			accountCancelled: computed,
			pendingCancellation: computed,
			updatedRenewalProbability: computed,
			updatedRenewalDiscountPercentage: computed,
			updatedWillRenewPrediction: computed,
			currentSummaryNoteId: computed,
			currentCsmNoteId: computed,
			renewalPdfSent: computed,
		});

		this.mAccountSnapshot = snapshot;
	}

	public updateRenewalProbability = (renewalProbability: number) => {
		if (!this.mAccountSnapshotsRenewalPromise) {
			this.mAccountSnapshotsRenewalPromise = new Promise<Api.IOperationResult<AdminModels.IAccountSnapshot>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IAccountSnapshot>) => {
						runInAction(() => {
							if (opResult.success) {
								this.mAccountSnapshot = opResult.value;
								this.mAccountSnapshotsRenewalPromise = null;
								resolve(opResult);
							} else {
								this.mAccountSnapshotsRenewalPromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IAccountSnapshot>(
						`admin/accountSnapshot/${this.mAccountSnapshot.id}/renewalProbability?renewalProbability=${renewalProbability}`,
						'PATCH',
						null,
						onFinish,
						onFinish
					);
				}
			);
		}
		return this.mAccountSnapshotsRenewalPromise;
	};

	public updateRenewalDiscountPercentage = (renewalDiscountPercentage: number) => {
		if (!this.mAccountSnapshotsRenewalDiscountPromise) {
			this.mAccountSnapshotsRenewalDiscountPromise = new Promise<Api.IOperationResult<AdminModels.IAccountSnapshot>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IAccountSnapshot>) => {
						runInAction(() => {
							if (opResult.success) {
								this.mAccountSnapshot = opResult.value;
								this.mAccountSnapshotsRenewalDiscountPromise = null;
								resolve(opResult);
							} else {
								this.mAccountSnapshotsRenewalDiscountPromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IAccountSnapshot>(
						`admin/accountSnapshot/${this.mAccountSnapshot.id}/renewalDiscountPercentage?value=${renewalDiscountPercentage}`,
						'PATCH',
						null,
						onFinish,
						onFinish
					);
				}
			);
		}
		return this.mAccountSnapshotsRenewalDiscountPromise;
	};

	public get isLoading() {
		return !!this.mAccountSnapshotsRenewalPromise || !!this.mAccountSnapshotsRenewalDiscountPromise;
	}

	public get canEdit() {
		const session = this.userSession as AdminUserSessionContext;
		if (session?.hasPermission(AdminPermission.OnlyForDevelopers)) {
			return true;
		}

		const renewal = moment(this.mAccountSnapshot?.account?.planDetails?.renewalDate);
		return renewal.isValid() ? moment().diff(renewal, 'days') <= 30 : false;
	}

	public toJs() {
		return this.mAccountSnapshot;
	}

	public get accountModel() {
		return this.mAccountSnapshot?.account;
	}

	public get id() {
		return this.mAccountSnapshot?.id;
	}

	public get accountDeleted() {
		return this.mAccountSnapshot?.accountDeleted;
	}

	public get accountCancelled() {
		return this.mAccountSnapshot?.accountCancelled;
	}

	public get pendingCancellation() {
		return this.mAccountSnapshot?.pendingCancellation;
	}

	public get updatedRenewalProbability() {
		return this.mAccountSnapshot?.updatedRenewalProbability;
	}

	public get updatedRenewalDiscountPercentage() {
		return this.mAccountSnapshot?.updatedRenewalDiscountPercentage;
	}

	public get updatedWillRenewPrediction() {
		return this.mAccountSnapshot.updatedWillRenewPrediction;
	}

	public get currentSummaryNoteId() {
		return this.mAccountSnapshot?.currentSummaryNoteId;
	}

	public get currentCsmNoteId() {
		return this.mAccountSnapshot?.currentCsmNoteId;
	}

	public get renewalPdfSent() {
		return this.mAccountSnapshot?.renewalPdfSent;
	}
}

export class BulkEmailHistoricalReportViewModel extends Api.ViewModel {
	private mCampaign: Api.ICampaign;
	private mReport: AdminModels.IBulkEmailHistoricalReport;
	protected mReportPromise: Promise<Api.IOperationResult<AdminModels.IBulkEmailHistoricalReport>>;

	constructor(userSession: UserSessionContext, campaign: Api.ICampaign = null) {
		super(userSession);

		makeObservable<BulkEmailHistoricalReportViewModel, 'mCampaign' | 'mReport' | 'mReportPromise'>(this, {
			mCampaign: observable.ref,
			mReport: observable.ref,
			mReportPromise: observable.ref,
			getBulkEmailReportForCampaign: action,
			getBulkEmailReport: action,
			report: computed,
			isLoading: override,
			isBusy: override,
			chartData: computed,
		});

		this.mCampaign = campaign;
	}

	public getBulkEmailReportForCampaign = () => {
		if (!this.mReportPromise && this.mCampaign) {
			this.mReportPromise = new Promise<Api.IOperationResult<AdminModels.IBulkEmailHistoricalReport>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IBulkEmailHistoricalReport>) => {
						runInAction(() => {
							if (opResult.success) {
								this.mReport = opResult.value;
								this.mReportPromise = null;
								resolve(opResult);
							} else {
								this.mReportPromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IBulkEmailHistoricalReport>(
						`admin/bulkEmailHistorical/stats/${AdminModels.AggregationIdType.Campaign}/${this.mCampaign.id}?userId=${this.mCampaign.creator?.id}`,
						'GET',
						null,
						onFinish,
						onFinish
					);
				}
			);
		}
		return this.mReportPromise;
	};

	public getBulkEmailReport = (type: AdminModels.AggregationIdType, id: string, start: Date, end: Date) => {
		if (!this.mReportPromise) {
			this.mReportPromise = new Promise<Api.IOperationResult<AdminModels.IBulkEmailHistoricalReport>>(
				(resolve, reject) => {
					const onFinish = (opResult: Api.IOperationResult<AdminModels.IBulkEmailHistoricalReport>) => {
						runInAction(() => {
							if (opResult.success) {
								this.mReport = opResult.value;
								this.mReportPromise = null;
								resolve(opResult);
							} else {
								this.mReportPromise = null;
								reject(opResult);
							}
						});
					};
					this.userSession.webServiceHelper.callWebServiceWithOperationResults<AdminModels.IBulkEmailHistoricalReport>(
						`admin/bulkEmailHistorical/stats/${type}/${id}?start=${encodeURIComponent(
							start.toISOString()
						)}&end=${encodeURIComponent(end.toISOString())}`,
						'GET',
						null,
						onFinish,
						onFinish
					);
				}
			);
		}
		return this.mReportPromise;
	};

	public toJs = () => {
		return this.mReport;
	};

	public get report() {
		return this.mReport;
	}

	public get isLoading() {
		return !!this.mReportPromise;
	}

	public get isBusy() {
		return !!this.mReportPromise;
	}

	public get chartData(): AdminModels.IBulkEmailHistoryDataPoint[] {
		return (this.mReport?.blocks || []).map<AdminModels.IBulkEmailHistoryDataPoint>(x => {
			return {
				ErrorMessages: x.errorMessages,
				Failures: x.failures,
				Hour: moment(x.block).local().format('M-D-YY @ hA'),
				Successes: x.successes,
			};
		});
	}
}

export class AccountDetailsNoteViewModel extends Api.NoteViewModel {
	protected mAccountOperations: AccountOperationsViewModel;
	protected mNoteType: AdminModels.InternalMeetingNoteType;

	constructor(
		accountOperations: AccountOperationsViewModel,
		noteType: AdminModels.InternalMeetingNoteType,
		note?: Api.INote
	) {
		super(accountOperations.userSession, note);
		makeObservable(this, {
			save: override,
		});
		this.mAccountOperations = accountOperations;
		this.mNoteType = noteType;
	}

	public save(note: Api.INote, newAttachments?: Api.AttachmentsToBeUploadedViewModel) {
		if (this.saving || this.savePromise) {
			return;
		}

		this.saving = true;
		const promise = new Promise<Api.INote>((resolve, reject) => {
			const updateExistingNote = !!this.note && !!this.note.id;
			const noteToSave = { ...note };
			if (updateExistingNote) {
				noteToSave.id = this.note.id;
			}

			const generateFormDataOrNull = (request: any) => {
				let formData: FormData = null;
				if (newAttachments && newAttachments.count > 0) {
					formData = new FormData();
					formData.append('value', JSON.stringify(request));
					newAttachments.attachments.forEach(x => {
						if (!(x instanceof File) && (x as File).name) {
							// is a Blob that has been given a name
							// need to send name as additional arg
							formData.append('files', x, (x as File).name);
						} else {
							formData.append('files', x);
						}
					});
				}
				return formData;
			};

			const meetingContext = note?.context as Api.IMeetingContext;
			const generateMeetingPromise = () => {
				// Transform "Ads Live" to "Live" for the API
				let apiMeetingType = meetingContext.meetingType;
				if (apiMeetingType === 'Ads Live') {
					apiMeetingType = 'Live' as Api.AdsMeetingTypes;
				}

				const meetingRequest = {
					durationInMinutes: meetingContext.durationInMinutes,
					meetingStatus: meetingContext.meetingStatus,
					meetingTags: meetingContext.meetingTags,
					meetingType: apiMeetingType,
					metWith: this.mNoteType,
					note,
				};

				return this.mAccountOperations.addAccountMeetingNote(
					this.mAccountOperations.account,
					meetingRequest,
					generateFormDataOrNull(meetingRequest)
				);
			};
			const savePromise =
				meetingContext?.source === Api.RichContentContextSource.Meeting
					? generateMeetingPromise()
					: this.mAccountOperations.updateAccountNote(
							this.mAccountOperations.account,
							this.mNoteType,
							note,
							generateFormDataOrNull(noteToSave)
						);
			savePromise
				?.then(value => {
					runInAction(() => {
						this.saving = false;
						this.savePromise = null;
						this.mSetRichContent(value);
						resolve(value);
					});
				})
				?.catch((error: Api.IOperationResultNoValue) => {
					runInAction(() => {
						this.saving = false;
						this.savePromise = null;
						reject(error);
					});
				});
		});

		this.savePromise = promise;
		return promise;
	}
}

export class AutoDiscoverEmailViewModel extends Api.ViewModel {
	public email: string;
	public errorMessage = '';
	private mEmailProviderConfiguration: Api.IEmailProviderConfiguration;

	constructor(userSession: UserSessionContext, initialDomain?: string) {
		super(userSession);

		makeObservable<AutoDiscoverEmailViewModel, 'mEmailProviderConfiguration'>(this, {
			email: observable,
			errorMessage: observable,
			mEmailProviderConfiguration: observable.ref,
			emailProviderConfiguration: computed,
			loadSuggestedEmailProvider: action,
		});

		this.email = initialDomain ? `test@${initialDomain}` : '';
	}

	public get emailProviderConfiguration() {
		return this.mEmailProviderConfiguration;
	}

	public loadSuggestedEmailProvider() {
		if (!this.busy) {
			this.busy = true;
			this.errorMessage = '';
			return new Promise<Api.IOperationResult<Api.IEmailProviderConfiguration>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IEmailProviderConfiguration>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mEmailProviderConfiguration = opResult.value;
							this.busy = false;
							resolve(opResult);
						} else {
							this.busy = false;
							this.errorMessage = opResult.systemMessage;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IEmailProviderConfiguration>(
					`admin/account/suggestedEmailProvider?email=${this.email}`,
					'POST',
					{},
					onFinish,
					onFinish
				);
			});
		}
		return null;
	}
}

export class PhoneNumberInfoViewModel extends Api.ViewModel {
	public phoneNumber: string;
	public errorMessage = '';
	private mPhoneNumberInfo: Api.IPhoneNumberInfo;

	constructor(userSession: UserSessionContext, initialPhoneNumber?: string) {
		super(userSession);

		makeObservable<PhoneNumberInfoViewModel, 'mPhoneNumberInfo'>(this, {
			phoneNumber: observable,
			errorMessage: observable,
			mPhoneNumberInfo: observable.ref,
			phoneNumberInfo: computed,
			loadPhoneNumberInfo: action,
		});

		this.phoneNumber = initialPhoneNumber ? initialPhoneNumber : '';
	}

	public get phoneNumberInfo() {
		return this.mPhoneNumberInfo;
	}

	public loadPhoneNumberInfo() {
		if (!this.busy) {
			this.busy = true;
			this.errorMessage = '';
			return new Promise<Api.IOperationResult<Api.IPhoneNumberInfo>>((resolve, reject) => {
				const onFinish = (opResult: Api.IOperationResult<Api.IPhoneNumberInfo>) => {
					runInAction(() => {
						if (opResult.success) {
							this.mPhoneNumberInfo = opResult.value;
							this.busy = false;
							resolve(opResult);
						} else {
							this.busy = false;
							this.errorMessage = opResult.systemMessage;
							reject(opResult);
						}
					});
				};
				this.userSession.webServiceHelper.callWebServiceWithOperationResults<Api.IPhoneNumberInfo>(
					`admin/phoneNumber/portability?phoneNumber=${this.phoneNumber}`,
					'POST',
					{},
					onFinish,
					onFinish
				);
			});
		}
		return null;
	}
}

export class ImpersonationContextViewModel implements Api.IImpersonationContext {
	public account: Api.IAccount;
	public user: Api.IUser;

	constructor(impersonationContext?: Api.IImpersonationContext) {
		makeObservable(this, {
			account: observable.ref,
			user: observable.ref,
			isValid: computed,
			reset: action,
		});

		this.update(impersonationContext);
	}

	public get isValid() {
		return !!this.account?.id;
	}

	public update = (impersonationContext?: Api.IImpersonationContext) => {
		this.account = impersonationContext?.account || null;
		this.user = impersonationContext?.user || null;
		return this;
	};

	public updateUser = (user?: Api.IUser) => {
		return this.update({
			user,
			account: this.account,
		});
	};

	public applyImpersonation = (...brokers: Api.ImpersonationBroker[]) => {
		if (this.isValid && brokers) {
			brokers?.forEach(x => x && x.impersonate(this));
		}
	};

	public reset = () => {
		this.account = null;
		this.user = null;
		return this;
	};

	public toJs = (): Api.IImpersonationContext => {
		return {
			account: this.account instanceof Api.AccountViewModel ? this.account.toJs() : this.account,
			user: this.user instanceof Api.UserViewModel ? this.user.toJs() : this.user,
		};
	};
}

export class AdminEmailWorkloadViewModel extends EmailWorkloadViewModel {
	public getAccountWorkloadForDateRange = (startDate: Date, endDate: Date, timeZone?: string) => {
		return new Promise<Api.IOperationResult<Api.IDayLoad[]>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<Api.IDayLoad[]>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IDayLoad[]>(
				this.composeApiUrl({
					queryParams: {
						endDate: endDate.toISOString(),
						startDate: startDate.toISOString(),
						timeZone,
					},
					urlPath: 'email/account/forecast',
				}),
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};
}

export class QuickSightDashboardViewModel extends Api.ViewModel {
	private dashboardId: string;
	private mAccountId: string;
	private mQueryParams: Record<string, string> = {};
	private embedUrl: string;

	constructor(
		userSession: UserSessionContext,
		dashboardId: string,
		accountId?: string,
		params?: Record<string, string>
	) {
		super(userSession);

		makeObservable<QuickSightDashboardViewModel, 'dashboardId' | 'mAccountId' | 'mQueryParams' | 'embedUrl'>(this, {
			dashboardId: observable,
			mAccountId: observable,
			mQueryParams: observable,
			embedUrl: observable,
			quickSightUrl: computed,
			accountId: computed,
			queryParams: computed,
			load: override,
			loadAida: action,
			getExternalEmbedUrl: action,
		});

		this.dashboardId = dashboardId;
		this.mAccountId = accountId;
		this.mQueryParams = params || {};
	}

	public static SalesKPIsId = '522356de-2eaf-4528-bb4b-3d202a8e0368';
	public static SuccessKPIsId = '51e5d947-d3ea-4b67-8b5d-458e8eab927d';
	public static CustomerMapId = 'b9df82ab-92de-420a-b533-cc1e35d08d57';
	public static ActiveUseId = 'f6bce626-ec26-44ff-a819-b8f7df53ef58';
	public static AIDARealMagicBookedByOverviewId = '07171337-0fd3-4239-8053-059e3d6ae303';
	public static AIDARealMagicDialsId = 'b008789e-9bd9-4965-ac52-f5075eb1b160?#';
	public static AIDARealMagicProspectingKPIsId = '52d89621-092a-4b2a-8300-063a0dba8c3f';
	public static AIDARealMagicCloserKPIsId = '23224f29-42ed-45cb-8859-64c051bc8235';
	public static AIDARealMagicCircleOfMagicId = 'e3684afc-3fc0-4d8a-9d77-4d68d35ecbdb';
	public static AIDACustomersDialsId = 'aceb1a60-6517-4394-b2df-581eba076bbe';
	public static AIDARealMagicDemoQualityId = '4e3fac9c-dfee-4328-b469-921675d4e483';
	public static AIDARealMagicAugustEOMId = '0fbd3c75-98bc-4fa5-a769-5e75d4cede19';
	public static CSMAccountDetailsId = '495a82d7-a917-477b-a97e-a061a4c28fcb';
	public static BookOfBusinessHealthId = '3b2216fe-565e-4743-bd11-3596d2f8aa9b';
	public static MonthlyRenewalId = '02e771a5-2d62-48b8-b81e-6f34bd58c2a1';
	public static DonationsId = '75ea1064-b186-4444-935c-5b9d70d60d82'; // Unreferenced on FE

	public get quickSightUrl() {
		return this.embedUrl;
	}

	public get accountId() {
		return this.mAccountId;
	}

	public get queryParams() {
		return this.mQueryParams || {};
	}

	public load = async () => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<string>(
			this.composeApiUrl({ urlPath: `admin/dashboard/quicksight/${this.dashboardId}` }),
			'GET'
		);

		if (opResult.success) {
			this.embedUrl = opResult.value;
		} else {
			throw Api.asApiError(opResult);
		}
	};

	public loadAida = async () => {
		this.embedUrl = await this.getExternalEmbedUrl();
	};

	public getExternalEmbedUrl = async () => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<string>(
			this.composeApiUrl({ urlPath: `aida/dashboard/quicksight/${this.dashboardId}` }),
			'GET'
		);

		if (opResult.success) {
			return opResult.value;
		} else {
			throw Api.asApiError(opResult);
		}
	};
}

export class GhostCalendarViewModel extends Api.ViewModel {
	private mSuggestions: IContentCalendarSuggestion[];
	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable<GhostCalendarViewModel, 'mSuggestions' | 'getByMonth'>(this, {
			mSuggestions: observable,
			suggestions: computed,
			templateIsSuggestion: action,
			loadAllSuggestions: action,
			getByMonth: action,
		});
	}

	public get suggestions() {
		return this.mSuggestions;
	}

	public templateIsSuggestion = (templateId: string) => {
		const isSuggestion = this.mSuggestions.find(x => x.templateReference.templateId === templateId);
		return isSuggestion;
	};

	public loadAllSuggestions = async (
		startDate: Date,
		endDate: Date,
		request: Api.IContentCalendarSuggestionRequest,
		campaignType: AdminModels.CampaignType
	) => {
		let suggestions: Api.IContentCalendarSuggestion[] = [];
		if (request.verticals?.length > 0 || (request.industries?.length > 0 && request.subverticals?.length > 0)) {
			const monthKeys = Array.from(Array(moment(endDate).diff(startDate, 'months')).keys());
			const startMoment = moment(startDate).startOf('month');
			for (let i = 0; i < monthKeys.length; i++) {
				const nextMoment = i > 0 ? moment(startMoment).add(i, 'month').startOf('month') : startMoment;
				const results = await this.getByMonth(nextMoment.toDate(), request);
				if (results?.length) {
					suggestions = [...suggestions, ...results];
				}
			}
		}
		if (campaignType === AdminModels.CampaignType.Social) {
			suggestions = suggestions.filter(x => x.templateReference.templateType === Api.TemplateType.SocialMediaPost);
		} else if (campaignType === AdminModels.CampaignType.Blog) {
			suggestions = suggestions.filter(x => x.templateReference.templateType === Api.TemplateType.Blog);
		} else if (campaignType === AdminModels.CampaignType.Email) {
			suggestions = suggestions.filter(x => x.templateReference.templateType === Api.TemplateType.Email);
		}
		this.mSuggestions = suggestions;
	};

	private getByMonth = async (month: Date, request: Api.IContentCalendarSuggestionRequest) => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IContentCalendarSuggestion[]>(
			this.composeApiUrl({
				queryParams: {
					month: month.toISOString(),
				},
				urlPath: 'contentCalendarSuggestion/filter/admin',
			}),
			'POST',
			request
		);

		if (opResult.success) {
			return opResult.value;
		} else {
			throw Api.asApiError(opResult);
		}
	};
}

export class CreateGhostCalendarSuggestionViewModel extends Api.ViewModel {
	public accountAges: Api.ContentCalendarSuggestionAccountAge[] = [];
	public completeByDays = 30;
	public expirationDate?: string;
	public industries: string[] = [];
	public sendAsap = true;
	public startDate: moment.Moment;
	// @observable public expirationDate?: moment.Moment;
	public subverticals: string[] = [];
	public tags: string[] = [];
	public targets: Api.IPostTarget[] = [];
	public template: Api.ITemplate;
	public templateReference: Api.ITemplateReference;
	public suggestion: Api.IContentCalendarSuggestion;

	// Create new suggestion
	public static instanceForCreate = (
		userSession: UserSessionContext,
		template: Api.ITemplate,
		industries?: Api.Industry[],
		subverticals?: string[],
		accountAges?: Api.ContentCalendarSuggestionAccountAge[],
		date?: moment.Moment,
		targets?: Api.IPostTarget[]
	) => {
		const vm = new CreateGhostCalendarSuggestionViewModel(userSession);
		vm.template = template;
		vm.industries = industries || [];
		vm.subverticals = subverticals || [];
		vm.accountAges = accountAges || [];
		vm.targets = targets || [];
		vm.expirationDate = template?.schedule?.expirationDate ? template.schedule.expirationDate : null;
		vm.startDate = moment(date).set('hours', 9);
		return vm;
	};
	// Edit existing suggestion
	public static instanceForEdit = (userSession: UserSessionContext, suggestion: Api.IContentCalendarSuggestion) => {
		const vm = new CreateGhostCalendarSuggestionViewModel(userSession);
		vm.accountAges = suggestion.accountAges;
		vm.completeByDays = suggestion.minimumDurationInDays || 30;
		vm.expirationDate = suggestion.schedule.expirationDate;
		vm.industries = suggestion.industries.includes(ContentCalendarSuggestionAll)
			? DefaultSupportedIndustries
			: suggestion.industries;
		vm.startDate = moment(suggestion.schedule?.startDate);
		vm.subverticals = suggestion.subverticals.includes(ContentCalendarSuggestionAll) ? [] : suggestion.subverticals;
		vm.tags = suggestion.filter?.criteria?.length ? suggestion.filter.criteria.map(x => x.value) : [];
		vm.targets = mapSocialMediaTypesToPostTargets(suggestion.targets);
		vm.templateReference = suggestion.templateReference;
		vm.suggestion = suggestion;
		vm.sendAsap = !suggestion.minimumDurationInDays;
		return vm;
	};

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			accountAges: observable,
			completeByDays: observable,
			expirationDate: observable,
			industries: observable,
			sendAsap: observable,
			startDate: observable,
			subverticals: observable,
			tags: observable,
			targets: observable,
			template: observable,
			templateReference: observable,
			suggestion: observable,
			isEditing: computed,
			canEdit: computed,
			isAllIndustriesSelected: computed,
			subverticalOptions: computed,
			templateContent: computed,
			create: action,
			update: action,
			delete: action,
		});
	}

	public get isEditing() {
		return !!this.suggestion;
	}

	public get canEdit() {
		return (
			!this.impersonationContext?.account ||
			(this.impersonationContext?.account &&
				(this.userSession?.user?.role === 'admin' || this.userSession?.user?.role === 'superAdmin'))
		);
	}

	public get isAllIndustriesSelected() {
		return this.industries.length === GhostCalendarIndustryOptions.length;
	}

	public get subverticalOptions() {
		if (this.industries?.length === 1) {
			return getSubverticalOptions(this.industries[0] as Api.Industry);
		}
		return [];
	}

	public get templateContent() {
		if (this.template?.content) {
			return convertRawRichTextContentStateToRichContentEditorState(this.template?.content);
		}
		return null;
	}

	public toJs = () => {
		const industries =
			this.industries.length === 0 || this.industries.length === DefaultSupportedIndustries.length
				? [ContentCalendarSuggestionAll]
				: this.industries;
		const subverticals =
			this.subverticals.length === 0 || this.subverticals.length === this.subverticalOptions.length
				? [ContentCalendarSuggestionAll]
				: this.subverticals;
		let filter: Api.IContactFilterCriteria = {};
		if (this.tags?.length) {
			filter = {
				criteria: [
					{
						criteria: [
							...this.tags.map(value => {
								return {
									property: Api.ContactFilterCriteriaProperty.Tag,
									value,
								};
							}),
						],
						op: Api.FilterOperator.Or,
					},
					...DefaultBulkSendExcludedFilterCriteria,
				],
			};
		}
		const suggestion: Api.IContentCalendarSuggestion = {
			accountAges: this.accountAges,
			archived: false,
			filter,
			industries,
			minimumDurationInDays: this.sendAsap ? null : this.completeByDays,
			schedule: {
				expirationDate: this?.expirationDate?.toString(),
				startDate: this.startDate.toISOString(),
			},
			subverticals,
			targets: getProviderTypesFromPostTargest(this.targets),
			templateReference: {
				isCustomized: false,
				isSystemTemplate: true,
				name: this.template?.name,
				templateId: this.template?.id,
			},
		};
		return suggestion;
	};

	public create = () => this.crudRequest('POST', this.toJs());

	public update = () => this.crudRequest('PUT', { ...this.suggestion, ...this.toJs() });

	public delete = () => this.crudRequest('DELETE', this.suggestion);

	private crudRequest = (method: 'POST' | 'PUT' | 'DELETE', suggestion: Api.IContentCalendarSuggestion) => {
		this.busy = true;
		return new Promise<Api.IContentCalendarSuggestion>((resolve, reject) => {
			const onFinish = (result: Api.IOperationResult<Api.IContentCalendarSuggestion>) => {
				this.busy = false;
				if (result.success) {
					resolve(result.value);
				} else {
					reject(result);
				}
			};

			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IContentCalendarSuggestion>(
				this.composeApiUrl({ urlPath: `contentCalendarSuggestion${method === 'POST' ? '' : `/${suggestion.id}`}` }),
				method,
				suggestion,
				onFinish,
				onFinish
			);
		});
	};
}

export class AdminCoffeeSlotMachineViewModel extends Api.ViewModel {
	protected mAchievments: SlotMachineAchievementsViewModel;
	constructor(userSession: AdminUserSessionContext) {
		super(userSession);

		makeObservable<AdminCoffeeSlotMachineViewModel, 'mAchievments'>(this, {
			mAchievments: observable.ref,
			achievments: computed,
			load: override,
			grantSlotMachineSpin: action,
		});

		this.impersonate = this.impersonate.bind(this);
		this.mAchievments = new SlotMachineAchievementsViewModel(userSession);
	}

	public get achievments() {
		return this.mAchievments?.fetchResults;
	}

	public load = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		await this.mAchievments.load();
		this.loading = false;
	};

	public grantSlotMachineSpin = async (
		user: AccountUserViewModel,
		achievement: SlotMachineAchievementViewModel,
		count = 1
	) => {
		const impContextBefore = this.mImpersonationContext;
		this.impersonate({ account: { id: user.accountId }, user });

		const opResults = await Promise.all(
			Array.from({ length: count }).map(() =>
				this.mUserSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({ urlPath: `achievement/user-achievement` }),
					'POST',
					achievement.configuration
				)
			)
		);

		this.impersonate(impContextBefore);

		const badResult = opResults.find(x => !x.success);
		if (badResult) {
			throw badResult;
		}
	};

	public impersonate(impersonationContext?: Api.IImpersonationContext) {
		super.impersonate(impersonationContext);
		this.mAchievments.impersonate(impersonationContext);
		return this;
	}
}
